// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — PA Grants Project Workspace
// Full project detail view with 8 tabs (Screens 4-8)
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef, Fragment } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Paper, Text, Group, Stack, Badge, Button, SimpleGrid, Table, Tabs,
  Loader, Center, Title, Progress, ActionIcon, Textarea, Modal, Checkbox,
  Divider, ThemeIcon, TextInput, Select, SegmentedControl, Popover, Tooltip, Alert,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconEdit, IconPlus, IconUpload, IconTrash,
  IconArrowLeft, IconCheck, IconClock, IconFile,
  IconFileTypePdf, IconFileSpreadsheet, IconRobot, IconFileDescription,
  IconCircleCheck, IconCircle, IconListCheck, IconCheckbox, IconScan,
  IconChevronDown, IconChevronRight, IconShieldCheck, IconDownload, IconPencil,
  IconCurrencyDollar, IconPhoto, IconLeaf, IconNotes, IconCalendarTime, IconReceipt, IconHistory, IconAlertTriangle, IconMessageQuestion, IconSend,
} from '@tabler/icons-react';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useImpactStore } from '../../store/impactStore';
import { exportToCSV } from '../../lib/exportUtils';
import { TABLES, FIELDS, createRecord, updateRecord, deleteRecord, callClaude, fetchTable } from '../../lib/api-switch';
import { useAuthStore } from '../../store/authStore';
import { useMyPAOrgIds } from '../../hooks/useMyPAOrgData';
import { useRoleStore } from '../../store/roleStore';
import {
  DAMAGE_CATEGORY_COLORS,
  DAMAGE_CATEGORY_LABELS,
  PROJECT_STATUS_LABELS,
} from '../../types/pagrants';
import type { DamageCategory, ProjectStatus, TimelineEventType, PAProjectCost, PAProjectRfi, PAProjectRfiResponse, RfiStatus, RfiCategory, RfiPriority, DobSource, DobCertification } from '../../types/pagrants';
import MitigationTab from './MitigationTab';
import ProjectPhotos from './ProjectPhotos';
import Contracts from './Contracts';
import ProjectVersions from './components/ProjectVersions';
import { loadTemplates } from './MilestoneTemplates';
import RichTextEditor from '../../components/ui/RichTextEditor';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { fmtDateTime } from '../../lib/date-format';
import { FEMA_EQUIP_RATES, FEMA_CATEGORIES } from '../../data/femaEquipmentRates';
import type { FemaEquipRate } from '../../data/femaEquipmentRates';

// ── Formatters & color maps ──────────────────────────────────

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const COST_TYPE_COLORS: Record<string, string> = {
  labor: 'var(--data-viz-650)', equipment: '#F86A0B', materials: '#03AC9E', contracts: 'var(--color-primary-700)',
  rented_equip: '#03AC9E', donated: 'pink', other: '#939598',
};

const COST_TYPE_LABELS: Record<string, string> = {
  labor: 'Labor', equipment: 'Equipment', materials: 'Materials',
  contracts: 'Contracts', rented_equip: 'Rented Equip', donated: 'Donated', other: 'Other',
};

const TIMELINE_EVENT_COLORS: Record<string, string> = {
  milestone: 'var(--color-primary-700)', deadline: 'var(--color-error)', submission: '#03AC9E', approval: 'var(--color-success)',
  inspection: '#F86A0B', meeting: 'var(--data-viz-650)', correspondence: '#939598',
};

const num = (v: string | undefined) => parseFloat(v || '0') || 0;

/** Format ISO timestamps or date strings to "MMM D, YYYY" */
function fmtDate(v: string | undefined | null): string {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Lightweight markdown-to-HTML for DDD/SOW display (no dependencies) */
function renderSimpleMarkdown(text: string): string {
  // Escape HTML entities
  let html = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // Restore newlines: markdown headings, ---, and | as section breaks
  html = html.replace(/\s*(#{1,3})\s+/g, '\n$1 ');
  html = html.replace(/\s*---\s*/g, '\n---\n');
  html = html.replace(/\s*\|\s*/g, ' | ');
  // Add breaks before common section patterns
  html = html.replace(/\s*(\*\*\d+\.\d+)/g, '\n\n$1');
  html = html.replace(/\s*(\*\*Division|\*\*Note:|\*\*Total|\*\*Subtotal)/g, '\n\n$1');
  // Add break before bold labels that start sections (e.g., **Gymnasium**, **HVAC**)
  html = html.replace(/\s*(\*\*[A-Z][^*]{2,40}\*\*:?)\s/g, '\n$1 ');
  // Add break before list-like items (- item)
  html = html.replace(/\s+-\s+/g, '\n- ');
  // Convert markdown formatting
  html = html.replace(/^### (.+)$/gm, '<h4 style="margin:16px 0 4px;font-size:14px">$1</h4>');
  html = html.replace(/^## (.+)$/gm, '<h3 style="margin:16px 0 8px;font-size:16px">$1</h3>');
  html = html.replace(/^# (.+)$/gm, '<h2 style="margin:16px 0 8px;font-size:20px">$1</h2>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/^---$/gm, '<hr style="margin:8px 0;border:none;border-top:1px solid #ddd"/>');
  html = html.replace(/^- (.+)$/gm, '<li style="margin-left:16px">$1</li>');
  // Newlines to <br> / paragraphs
  html = html.replace(/\n{2,}/g, '</p><p style="margin:8px 0">');
  html = html.replace(/\n/g, '<br/>');
  return '<p style="margin:0">' + html + '</p>';
}

function formatCurrency(raw: string | number | null | undefined): string {
  const cleaned = String(raw ?? '').replace(/[^0-9.]/g, '');
  if (!cleaned) return '';
  const parts = cleaned.split('.');
  const whole = parts[0].replace(/^0+(?=\d)/, '');
  const formatted = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (parts.length > 1) return `$${formatted}.${parts[1].slice(0, 2)}`;
  return formatted ? `$${formatted}` : '';
}
function stripCurrency(formatted: string | null | undefined): string {
  return String(formatted ?? '').replace(/[$,]/g, '');
}

// ── FEMA Rate Picker (equipment cost lookup) ─────────────────

const FEMA_RATE_CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  ...FEMA_CATEGORIES.map(c => ({ value: c, label: c })),
];

function FemaRatePickerPA({ onSelect, onClose }: { onSelect: (r: FemaEquipRate) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');

  const results = useMemo(() => {
    const lq = q.toLowerCase();
    return FEMA_EQUIP_RATES.filter(r => {
      const matchCat = !cat || r.equipment.toLowerCase().includes(cat.toLowerCase());
      const matchQ = !lq || r.costCode.includes(lq) || r.equipment.toLowerCase().includes(lq) ||
        r.manufacturer.toLowerCase().includes(lq) || r.specification.toLowerCase().includes(lq) ||
        r.capacityOrSize.toLowerCase().includes(lq);
      return matchCat && matchQ;
    }).slice(0, 100);
  }, [q, cat]);

  return (
    <Stack gap="sm">
      <Group gap="sm">
        <TextInput placeholder="Search equipment, code, manufacturer…" value={q}
          onChange={e => setQ(e.target.value)} style={{ flex: 1 }} autoFocus />
        <Select data={FEMA_RATE_CATEGORY_OPTIONS} value={cat} onChange={v => setCat(v || '')}
          placeholder="Category" style={{ width: 180 }} />
      </Group>
      <Text size="xs" c="dimmed">{results.length} result{results.length !== 1 ? 's' : ''} — click to select</Text>
      <div style={{ overflowY: 'auto', maxHeight: 360, border: '1px solid #e2e8f0', borderRadius: 6 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
            <tr>
              <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>Code</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>Equipment</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>Spec / Size</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>HP</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>Rate</th>
            </tr>
          </thead>
          <tbody>
            {results.length === 0 && (
              <tr><td colSpan={5} style={{ padding: '20px 8px', textAlign: 'center', color: '#94a3b8' }}>No matches</td></tr>
            )}
            {results.map((r, i) => (
              <tr key={r.costCode + i}
                style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                onClick={() => onSelect(r)}
                onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f8fafc')}>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontWeight: 600, color: 'var(--color-primary-700)', whiteSpace: 'nowrap' }}>{r.costCode}</td>
                <td style={{ padding: '6px 8px', fontWeight: 500 }}>{r.equipment}</td>
                <td style={{ padding: '6px 8px', color: '#475569' }}>{[r.specification, r.capacityOrSize].filter(Boolean).join(' / ') || '—'}</td>
                <td style={{ padding: '6px 8px', color: '#475569' }}>{r.hp || '—'}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                  ${r.rate.toFixed(2)}<span style={{ color: '#94a3b8', fontWeight: 400 }}>/{r.unit}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Group justify="flex-end">
        <Button variant="default" size="sm" onClick={onClose}>Cancel</Button>
      </Group>
    </Stack>
  );
}

// ── Cost form type ───────────────────────────────────────────

interface CostFormState {
  costType: string;
  description: string;
  quantity: string;
  unit: string;
  unitCost: string;
  totalCost: string;
  cefPart: string;
  vendorEmployee: string;
  sourceReference: string;
  estimatedOrActual: string;
  eligible: string;
  notes: string;
}

const emptyCostForm = (): CostFormState => ({
  costType: '', description: '', quantity: '', unit: '',
  unitCost: '', totalCost: '', cefPart: '', vendorEmployee: '', sourceReference: '',
  estimatedOrActual: 'estimated', eligible: 'true', notes: '',
});

// ── Insurance form type ──────────────────────────────────────

interface InsFormState {
  policyNumber: string; carrier: string; policyType: string;
  coverageAmount: string; deductible: string;
  policyEffectiveDate: string; policyExpirationDate: string;
  claimFiled: string; claimNumber: string; claimDate: string;
  settlementAmount: string; settlementDate: string;
  denialReceived: string; denialReason: string;
  obtainMaintainRequired: string; obtainMaintainCommitted: string;
  femaDeductionAmount: string;
}

const emptyInsForm = (): InsFormState => ({
  policyNumber: '', carrier: '', policyType: '',
  coverageAmount: '', deductible: '',
  policyEffectiveDate: '', policyExpirationDate: '',
  claimFiled: 'no', claimNumber: '', claimDate: '',
  settlementAmount: '', settlementDate: '',
  denialReceived: 'no', denialReason: '',
  obtainMaintainRequired: 'no', obtainMaintainCommitted: 'no',
  femaDeductionAmount: '',
});

// ── Compliance form type ─────────────────────────────────────

interface CompFormState {
  nepaRequired: string; nepaLevel: string; nepaStatus: string; nepaCompletionDate: string;
  section106Required: string; section106Status: string;
  historicProperty: string; shpoConsultation: string; tribalConsultation: string;
  inFloodplain: string; floodZone: string;
  floodInsuranceRequired: string; eo11988Compliance: string;
  coastalBarrierArea: string; coastalZone: string;
  esaConsultationRequired: string; hazmatPresent: string;
}

const emptyCompForm = (): CompFormState => ({
  nepaRequired: '', nepaLevel: '', nepaStatus: '', nepaCompletionDate: '',
  section106Required: '', section106Status: '',
  historicProperty: '', shpoConsultation: '', tribalConsultation: '',
  inFloodplain: '', floodZone: '',
  floodInsuranceRequired: '', eo11988Compliance: '',
  coastalBarrierArea: '', coastalZone: '',
  esaConsultationRequired: '', hazmatPresent: '',
});

// ── Timeline event form type ─────────────────────────────────

interface EventFormState {
  eventType: string; eventDate: string; dueDate: string;
  description: string; status: string;
}

const emptyEventForm = (): EventFormState => ({
  eventType: 'milestone', eventDate: '', dueDate: '',
  description: '', status: 'pending',
});

// ── Edit Project form type ───────────────────────────────────

interface EditProjectFormState {
  projectNumber: string; projectTitle: string;
  category: string; projectSize: string; workType: string;
  projectAddress: string;
  damageDescription: string; scopeOfWork: string; costNarrative: string;
  estimatedCost: string; actualCost: string; federalSharePct: string;
  obligatedAmount: string; drawdownAmount: string;
  workStartDate: string; deadline: string; completionPctEstimate: string;
  assignedSpecialist: string; status: string; phase: string; notes: string;
}

const emptyEditProjectForm = (): EditProjectFormState => ({
  projectNumber: '', projectTitle: '',
  category: '', projectSize: '', workType: '',
  projectAddress: '',
  damageDescription: '', scopeOfWork: '', costNarrative: '',
  estimatedCost: '', actualCost: '', federalSharePct: '75',
  obligatedAmount: '', drawdownAmount: '',
  workStartDate: '', deadline: '', completionPctEstimate: '',
  assignedSpecialist: '', status: 'draft', phase: 'formulation', notes: '',
});

// ── Select option arrays ─────────────────────────────────────

const COST_TYPE_OPTIONS = [
  { value: 'labor', label: 'Labor' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'materials', label: 'Materials' },
  { value: 'contracts', label: 'Contracts' },
  { value: 'rented_equip', label: 'Rented Equipment' },
  { value: 'donated', label: 'Donated' },
  { value: 'other', label: 'Other' },
];

const EST_ACTUAL_OPTIONS = [
  { value: 'estimated', label: 'Estimated' },
  { value: 'actual', label: 'Actual' },
];

const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const ELIGIBLE_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const NEPA_LEVEL_OPTIONS = [
  { value: 'catex', label: 'CatEx' },
  { value: 'ea', label: 'EA' },
  { value: 'eis', label: 'EIS' },
];

const COMPLIANCE_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'not_required', label: 'Not Required' },
];

const EVENT_TYPE_OPTIONS = [
  { value: 'milestone', label: 'Milestone' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'submission', label: 'Submission' },
  { value: 'approval', label: 'Approval' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'correspondence', label: 'Correspondence' },
];

const EVENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'overdue', label: 'Overdue' },
];

const CATEGORY_OPTIONS = [
  { value: 'A', label: 'A - Debris Removal' },
  { value: 'B', label: 'B - Emergency Protective Measures' },
  { value: 'C', label: 'C - Roads & Bridges' },
  { value: 'D', label: 'D - Water Control Facilities' },
  { value: 'E', label: 'E - Buildings & Equipment' },
  { value: 'F', label: 'F - Utilities' },
  { value: 'G', label: 'G - Parks & Recreation' },
];

const SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'large', label: 'Large' },
];

const PROJECT_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_review', label: 'In Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'obligated', label: 'Obligated' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed' },
  { value: 'appealed', label: 'Appealed' },
  { value: 'denied', label: 'Denied' },
];

const PHASE_OPTIONS = [
  { value: 'formulation', label: 'Formulation' },
  { value: 'review', label: 'Review' },
  { value: 'obligation', label: 'Obligation' },
  { value: 'work_in_progress', label: 'Work in Progress' },
  { value: 'closeout', label: 'Closeout' },
  { value: 'appeal', label: 'Appeal' },
];

const WORK_TYPE_OPTIONS = [
  { value: 'emergency_work', label: 'Emergency Work (Cat A/B)' },
  { value: 'permanent_repair', label: 'Permanent Repair' },
  { value: 'permanent_replace', label: 'Permanent Replacement' },
  { value: 'improved_project', label: 'Improved Project (406)' },
  { value: 'alternate_project', label: 'Alternate Project (428)' },
  { value: 'debris_removal', label: 'Debris Removal' },
  { value: 'emergency_protective', label: 'Emergency Protective Measures' },
];

const CEF_PART_OPTIONS = [
  { value: 'A', label: 'Part A \u2013 Force Account Labor' },
  { value: 'B', label: 'Part B \u2013 Force Account Equipment' },
  { value: 'C', label: 'Part C \u2013 Materials' },
  { value: 'D', label: 'Part D \u2013 Rented Equipment' },
  { value: 'E', label: 'Part E \u2013 Contract Work' },
  { value: 'F', label: 'Part F \u2013 Direct Administrative Costs' },
];

const POLICY_TYPE_OPTIONS = [
  { value: 'property', label: 'Property' },
  { value: 'flood', label: 'Flood (NFIP)' },
  { value: 'wind', label: 'Wind/Storm' },
  { value: 'liability', label: 'General Liability' },
  { value: 'vehicle', label: 'Vehicle/Equipment' },
  { value: 'other', label: 'Other' },
];

// ── PA Project Checklist Items ────────────────────────────────
// Standard checklist of forms, documents, reviews, and milestones
// that every PA project needs to complete.

interface ChecklistItem {
  key: string;
  label: string;
  category: 'document' | 'form' | 'review' | 'milestone';
  description: string;
  required: boolean;
}

const PA_PROJECT_CHECKLIST_FALLBACK: ChecklistItem[] = [
  // Documents
  { key: 'damage_photos', label: 'Damage Photos (Pre/Post)', category: 'document', description: 'Photographs documenting damage before and after disaster', required: true },
  { key: 'ddd', label: 'Damage Description & Dimensions (DDD)', category: 'document', description: 'Detailed damage description including facility name, pre-disaster condition, and measurements', required: true },
  { key: 'sow', label: 'Scope of Work (SOW)', category: 'document', description: 'Detailed scope including materials, methods, labor, equipment, and quantities', required: true },
  { key: 'cost_estimate', label: 'Cost Estimate / CEF', category: 'document', description: 'FEMA Cost Estimating Format breakdown with supporting documentation', required: true },
  { key: 'insurance_docs', label: 'Insurance Documentation', category: 'document', description: 'Insurance policies, claim filings, settlement letters, or denial documentation', required: true },
  { key: 'procurement_docs', label: 'Procurement Documentation', category: 'document', description: 'Bid solicitations, proposals, contracts, and 2 CFR 200 compliance records', required: false },
  { key: 'invoices', label: 'Invoices & Receipts', category: 'document', description: 'Vendor invoices, material receipts, rental agreements, and proof of payment', required: false },
  { key: 'force_account_labor', label: 'Force Account Labor Records', category: 'document', description: 'Timesheets, payroll records, and fringe benefit documentation', required: false },
  { key: 'force_account_equip', label: 'Force Account Equipment Records', category: 'document', description: 'Equipment logs, FEMA rate schedule justification, operator records', required: false },
  { key: 'ownership_docs', label: 'Legal Responsibility / Ownership', category: 'document', description: 'Proof of legal responsibility for the damaged facility (deeds, leases, maintenance agreements)', required: true },
  // Forms
  { key: 'applicant_certification', label: 'Applicant Certification', category: 'form', description: 'Signed certification of legal responsibility, insurance, procurement compliance, and no duplication', required: true },
  { key: 'sf424', label: 'SF-424 Application for Federal Assistance', category: 'form', description: 'Standard federal grant application form', required: false },
  // Reviews
  { key: 'ehp_review', label: 'EHP / NEPA Review', category: 'review', description: 'Environmental and Historic Preservation review (NEPA, Section 106, floodplain, ESA)', required: true },
  { key: 'hazard_mitigation_review', label: 'Hazard Mitigation Review', category: 'review', description: 'Review of 406 hazard mitigation proposal and benefit-cost analysis', required: false },
  { key: 'insurance_review', label: 'Insurance Review / O&M', category: 'review', description: 'FEMA insurance specialist review of obtain-and-maintain requirements', required: true },
  { key: 'cost_review', label: 'Cost Reasonableness Review', category: 'review', description: 'Review that project costs are reasonable, necessary, and directly tied to the disaster', required: true },
  // Milestones
  { key: 'site_inspection', label: 'Site Inspection Complete', category: 'milestone', description: 'FEMA/state site inspection of damage has been completed', required: true },
  { key: 'project_formulated', label: 'Project Worksheet Formulated', category: 'milestone', description: 'PW has been drafted and validated with all required documentation', required: true },
  { key: 'project_obligated', label: 'Project Obligated', category: 'milestone', description: 'Federal funds have been obligated for this project', required: true },
  { key: 'work_completed', label: 'Work Completed', category: 'milestone', description: 'All repair/replacement work has been physically completed', required: false },
  { key: 'closeout_submitted', label: 'Closeout Package Submitted', category: 'milestone', description: 'Final documentation, certifications, and reconciliation submitted for closeout', required: false },
];

const CHECKLIST_CATEGORY_COLORS: Record<string, string> = {
  document: 'var(--color-primary-700)', form: 'var(--data-viz-650)', review: '#F86A0B', milestone: 'var(--color-success)',
};
const CHECKLIST_CATEGORY_LABELS: Record<string, string> = {
  document: 'Document', form: 'Form', review: 'Review', milestone: 'Milestone',
};

// ── FEMA Special Consideration Questions (009-0-120) ─────────
const SCQ_QUESTIONS = [
  { key: 'scq1', text: '1. Does the damaged facility or item of work have insurance and/or is it an insurable risk? (e.g., buildings, equipment, vehicles)', compField: 'floodInsuranceRequired' as const },
  { key: 'scq2', text: '2. Is the damaged facility located within a floodplain or coastal high hazard area, or does it have an impact on a floodplain or wetland?', compField: 'inFloodplain' as const },
  { key: 'scq3', text: '3. Is the damaged facility or item of work located within or adjacent to a Coastal Barrier Resource System Unit or an Otherwise Protected Area?', compField: 'coastalBarrierArea' as const },
  { key: 'scq4', text: '4. Will the proposed facility repairs/reconstruction change the pre-disaster condition? (e.g., footprint, material, location, capacity, use or function)', compField: 'eo11988Compliance' as const },
  { key: 'scq5', text: '5. Does the applicant have a hazard mitigation proposal or would the applicant like technical assistance for a hazard mitigation proposal?', compField: null },
  { key: 'scq6', text: '6. Is the damaged facility on the National Register of Historic Places or the state historic listing? Is it older than 50 years? Are there other, similar buildings near the site?', compField: 'historicProperty' as const },
  { key: 'scq7', text: '7. Is there any contamination or hazardous material at or near the site? (e.g., underground storage tanks, asbestos, lead paint)', compField: 'hazmatPresent' as const },
  { key: 'scq8', text: '8. Are there any environmental or ecological concerns? (e.g., endangered species, critical habitat, wetlands, wild and scenic rivers)', compField: 'esaConsultationRequired' as const },
];

interface ScqAnswers {
  [key: string]: { answer: string; comment: string };
}

// Document type options built dynamically from checklist items (moved into component as useMemo)

function deriveDocType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const map: Record<string, string> = {
    pdf: 'PDF', doc: 'Document', docx: 'Document', xlsx: 'Spreadsheet',
    xls: 'Spreadsheet', csv: 'Spreadsheet', pptx: 'Presentation',
    ppt: 'Presentation', png: 'Image', jpg: 'Image', jpeg: 'Image',
    gif: 'Image', zip: 'Archive', rar: 'Archive',
  };
  return map[ext] || 'Other';
}

// ── PA Task Templates ────────────────────────────────────────

const PA_TASK_TEMPLATES: Array<{ name: string; tasks: Array<{ description: string; daysFromStart: number }> }> = [
  {
    name: 'Standard PA Project Setup',
    tasks: [
      { description: 'Complete Preliminary Damage Assessment (PDA)', daysFromStart: 0 },
      { description: 'Submit Request for Public Assistance (RPA)', daysFromStart: 3 },
      { description: 'Schedule and complete FEMA site inspection', daysFromStart: 7 },
      { description: 'Prepare Project Worksheet with cost estimate', daysFromStart: 14 },
      { description: 'Submit Project Worksheet for FEMA review', daysFromStart: 21 },
      { description: 'Complete environmental/historic (EHP) review', daysFromStart: 30 },
      { description: 'Obtain insurance documentation and file claims', daysFromStart: 14 },
      { description: 'Complete procurement for contracted work', daysFromStart: 45 },
      { description: 'Begin permanent repair work', daysFromStart: 60 },
      { description: 'Submit quarterly progress reports', daysFromStart: 90 },
    ],
  },
  {
    name: 'Emergency Work (Cat A/B)',
    tasks: [
      { description: 'Document pre-disaster conditions', daysFromStart: 0 },
      { description: 'Begin emergency protective measures', daysFromStart: 0 },
      { description: 'Photograph all damage before cleanup', daysFromStart: 1 },
      { description: 'Track force account labor hours daily', daysFromStart: 1 },
      { description: 'Log equipment usage with FEMA rates', daysFromStart: 1 },
      { description: 'Collect material receipts and invoices', daysFromStart: 3 },
      { description: 'Submit debris monitoring reports', daysFromStart: 7 },
      { description: 'Complete emergency work summary', daysFromStart: 14 },
    ],
  },
  {
    name: 'Large Project Closeout',
    tasks: [
      { description: 'Compile all invoices and payment documentation', daysFromStart: 0 },
      { description: 'Complete final inspection with FEMA specialist', daysFromStart: 7 },
      { description: 'Reconcile actual costs vs. obligated amount', daysFromStart: 14 },
      { description: 'Submit final claim with supporting documentation', daysFromStart: 21 },
      { description: 'Resolve any audit findings or questioned costs', daysFromStart: 30 },
      { description: 'Obtain final insurance settlement documentation', daysFromStart: 30 },
      { description: 'Submit project completion certification', daysFromStart: 45 },
    ],
  },
];

// ── RFI Tab Component ─────────────────────────────────────────

const RFI_STATUS_COLORS: Record<RfiStatus, string> = {
  open: '#D97706', responded: '#2563EB', in_review: '#7C3AED', resolved: '#059669', closed: '#6B7280',
};
const RFI_STATUS_LABELS: Record<RfiStatus, string> = {
  open: 'Open', responded: 'Responded', in_review: 'In Review', resolved: 'Resolved', closed: 'Closed',
};
const RFI_PRIORITY_COLORS: Record<RfiPriority, string> = {
  low: '#6B7280', normal: '#2563EB', high: '#D97706', urgent: '#DC2626',
};
const RFI_CATEGORY_LABELS: Record<RfiCategory, string> = {
  documentation: 'Documentation', clarification: 'Clarification', eligibility: 'Eligibility',
  site_access: 'Site Access', cost_justification: 'Cost Justification', environmental: 'Environmental',
  procurement: 'Procurement', other: 'Other',
};

function RfiTab({
  projectRid, project, rfis, rfiResponses, rfisLoading,
  fetchRfiResponses, createRfi, updateRfi, deleteRfi, createRfiResponse, deleteRfiResponse,
  canEdit, isApplicant,
}: {
  projectRid: string;
  project: any;
  rfis: PAProjectRfi[];
  rfiResponses: PAProjectRfiResponse[];
  rfisLoading: boolean;
  fetchRfiResponses: (id: number) => Promise<void>;
  createRfi: (data: Partial<PAProjectRfi>) => Promise<number>;
  updateRfi: (id: number, data: Partial<PAProjectRfi>) => Promise<void>;
  deleteRfi: (id: number) => Promise<void>;
  createRfiResponse: (data: Partial<PAProjectRfiResponse>) => Promise<number>;
  deleteRfiResponse: (id: number, rfiId: number) => Promise<void>;
  canEdit: boolean;
  isApplicant: boolean;
}) {
  const { user } = useAuthStore();

  const [selected, setSelected] = useState<PAProjectRfi | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingRfi, setEditingRfi] = useState<PAProjectRfi | null>(null);

  // Create form
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newCategory, setNewCategory] = useState<string>('documentation');
  const [newPriority, setNewPriority] = useState<string>('normal');
  const [newDueDate, setNewDueDate] = useState('');
  const [saving, setSaving] = useState(false);

  // Response form
  const [replyBody, setReplyBody] = useState('');
  const [replyInternal, setReplyInternal] = useState(false);
  const [replySaving, setReplySaving] = useState(false);

  // Edit RFI
  const [editStatus, setEditStatus] = useState('');
  const [editInternalNotes, setEditInternalNotes] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const selectedResponses = useMemo(
    () => rfiResponses.filter(r => String(r.rfiId) === String(selected?.id)),
    [rfiResponses, selected?.id],
  );

  function openRfi(rfi: PAProjectRfi) {
    setSelected(rfi);
    fetchRfiResponses(rfi.id);
  }

  async function handleCreate() {
    if (!newSubject.trim()) return;
    setSaving(true);
    await createRfi({
      projectId: projectRid,
      organizationId: project?.organizationId || '',
      disasterId: project?.disasterId || '',
      subject: newSubject.trim(),
      body: newBody.trim(),
      category: newCategory as RfiCategory,
      priority: newPriority as RfiPriority,
      dueDate: newDueDate || undefined,
      createdBy: user?.email || '',
    });
    setSaving(false);
    setShowCreate(false);
    setNewSubject(''); setNewBody(''); setNewCategory('documentation');
    setNewPriority('normal'); setNewDueDate('');
  }

  async function handleReply() {
    if (!selected || !replyBody.trim()) return;
    setReplySaving(true);
    await createRfiResponse({
      rfiId: String(selected.id),
      body: replyBody.trim(),
      authorName: user?.name || user?.email || '',
      authorRole: isApplicant ? 'applicant' : 'grantee',
      isInternal: replyInternal && !isApplicant,
    });
    setReplyBody(''); setReplyInternal(false);
    setReplySaving(false);
    // Refresh selected
    const updated = rfis.find(r => r.id === selected.id);
    if (updated) setSelected(updated);
  }

  async function handleUpdateRfi() {
    if (!editingRfi) return;
    setEditSaving(true);
    await updateRfi(editingRfi.id, {
      status: editStatus as RfiStatus,
      priority: editPriority as RfiPriority,
      internalNotes: editInternalNotes,
      ...(editStatus === 'resolved' ? { resolvedBy: user?.email || '', resolvedAt: new Date().toISOString() } : {}),
    });
    setEditSaving(false);
    setEditingRfi(null);
    // Refresh selected if it was this RFI
    if (selected?.id === editingRfi.id) {
      const updated = rfis.find(r => r.id === editingRfi.id);
      if (updated) setSelected({ ...updated, status: editStatus as RfiStatus, priority: editPriority as RfiPriority, internalNotes: editInternalNotes });
    }
  }

  const openCnt = rfis.filter(r => r.status === 'open').length;
  const respondedCnt = rfis.filter(r => r.status === 'responded').length;

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* ── RFI List ── */}
      <div style={{ flex: '0 0 420px', minWidth: 0 }}>
        <Paper withBorder style={{ borderColor: '#e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Group gap="sm">
              <IconMessageQuestion size={18} color="#1E40AF" />
              <Text fw={600} size="sm">RFIs ({rfis.length})</Text>
              {openCnt > 0 && <Badge size="sm" style={{ background: '#FEF3C7', color: '#D97706', border: '1px solid #FDE68A' }}>{openCnt} open</Badge>}
              {respondedCnt > 0 && <Badge size="sm" style={{ background: '#DBEAFE', color: '#2563EB', border: '1px solid #BFDBFE' }}>{respondedCnt} responded</Badge>}
            </Group>
            {canEdit && (
              <Button size="xs" leftSection={<IconPlus size={13} />} style={{ background: '#1E40AF', color: '#fff', border: 'none' }} onClick={() => setShowCreate(true)}>
                New RFI
              </Button>
            )}
          </div>

          {rfisLoading ? (
            <div style={{ padding: 32, textAlign: 'center' }}><Text size="sm" c="dimmed">Loading…</Text></div>
          ) : rfis.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <IconMessageQuestion size={32} color="#cbd5e1" />
              <Text size="sm" c="dimmed" mt="xs">No RFIs for this project</Text>
              {canEdit && <Text size="xs" c="dimmed">Click "New RFI" to issue a request</Text>}
            </div>
          ) : (
            <div style={{ maxHeight: 'calc(100vh - 420px)', overflowY: 'auto' }}>
              {rfis.map(rfi => {
                const isDue = rfi.dueDate && new Date(rfi.dueDate) < new Date() && !['resolved','closed'].includes(rfi.status);
                const isActive = selected?.id === rfi.id;
                return (
                  <div
                    key={rfi.id}
                    onClick={() => openRfi(rfi)}
                    style={{
                      padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                      background: isActive ? '#EFF6FF' : undefined,
                      borderLeft: isActive ? '3px solid #1E40AF' : '3px solid transparent',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = ''; }}
                  >
                    <Group justify="space-between" gap="xs" mb={4}>
                      <Text size="xs" fw={700} style={{ color: '#1E40AF' }}>{rfi.rfiNumber}</Text>
                      <Badge
                        size="xs"
                        style={{
                          background: RFI_STATUS_COLORS[rfi.status as RfiStatus] + '22',
                          color: RFI_STATUS_COLORS[rfi.status as RfiStatus],
                          border: `1px solid ${RFI_STATUS_COLORS[rfi.status as RfiStatus]}44`,
                          flexShrink: 0,
                        }}
                      >
                        {RFI_STATUS_LABELS[rfi.status as RfiStatus]}
                      </Badge>
                    </Group>
                    <Text size="sm" fw={500} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rfi.subject}</Text>
                    <Group gap="xs" mt={4}>
                      <Badge size="xs" variant="light" style={{ background: '#f1f5f9', color: '#64748b' }}>
                        {RFI_CATEGORY_LABELS[rfi.category as RfiCategory] || rfi.category}
                      </Badge>
                      <Badge size="xs" variant="dot" style={{ color: RFI_PRIORITY_COLORS[rfi.priority as RfiPriority] }}>
                        {rfi.priority}
                      </Badge>
                      {rfi.dueDate && (
                        <Text size="xs" style={{ color: isDue ? '#DC2626' : '#94a3b8' }}>
                          {isDue && '⚠ '}Due {fmtDate(rfi.dueDate)}
                        </Text>
                      )}
                    </Group>
                  </div>
                );
              })}
            </div>
          )}
        </Paper>
      </div>

      {/* ── RFI Detail / Thread ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!selected ? (
          <Paper withBorder p="xl" style={{ borderColor: '#e2e8f0', textAlign: 'center', color: '#94a3b8' }}>
            <IconMessageQuestion size={40} color="#cbd5e1" />
            <Text size="sm" c="dimmed" mt="xs">Select an RFI to view the thread</Text>
          </Paper>
        ) : (
          <Stack gap="md">
            {/* Header */}
            <Paper withBorder p="md" style={{ borderColor: '#e2e8f0' }}>
              <Group justify="space-between" mb="xs">
                <Group gap="sm">
                  <Text fw={700} size="lg">{selected.rfiNumber}</Text>
                  <Badge
                    size="sm"
                    style={{
                      background: RFI_STATUS_COLORS[selected.status as RfiStatus] + '22',
                      color: RFI_STATUS_COLORS[selected.status as RfiStatus],
                      border: `1px solid ${RFI_STATUS_COLORS[selected.status as RfiStatus]}44`,
                    }}
                  >
                    {RFI_STATUS_LABELS[selected.status as RfiStatus]}
                  </Badge>
                  <Badge size="sm" variant="dot" style={{ color: RFI_PRIORITY_COLORS[selected.priority as RfiPriority] }}>
                    {selected.priority} priority
                  </Badge>
                </Group>
                {canEdit && (
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="outline"
                      leftSection={<IconEdit size={13} />}
                      onClick={() => {
                        setEditingRfi(selected);
                        setEditStatus(selected.status);
                        setEditPriority(selected.priority);
                        setEditInternalNotes(selected.internalNotes || '');
                      }}
                    >
                      Update
                    </Button>
                    {!isApplicant && (
                      <Button
                        size="xs"
                        variant="outline"
                        color="red"
                        leftSection={<IconTrash size={13} />}
                        onClick={async () => {
                          if (!confirm('Delete this RFI and all responses?')) return;
                          await deleteRfi(selected.id);
                          setSelected(null);
                        }}
                      >
                        Delete
                      </Button>
                    )}
                  </Group>
                )}
              </Group>

              <Text fw={600} size="md" mb={4}>{selected.subject}</Text>
              {selected.dueDate && (
                <Text size="xs" c="dimmed" mb={4}>
                  Due: {fmtDate(selected.dueDate)}
                  {(() => { const d = new Date(selected.dueDate); return !isNaN(d.getTime()) && d < new Date() && !['resolved','closed'].includes(selected.status) ? <span style={{ color: '#DC2626', marginLeft: 6 }}>⚠ Overdue</span> : null; })()}
                </Text>
              )}
              <Group gap="xs">
                <Badge size="xs" variant="light" style={{ background: '#f1f5f9', color: '#64748b' }}>
                  {RFI_CATEGORY_LABELS[selected.category as RfiCategory] || selected.category}
                </Badge>
                {selected.createdBy && <Text size="xs" c="dimmed">Issued by {selected.createdBy}</Text>}
              </Group>
            </Paper>

            {/* RFI body */}
            {selected.body && (
              <Paper withBorder p="md" style={{ borderColor: '#e2e8f0', background: '#f8fafc' }}>
                <Text size="xs" fw={600} c="dimmed" mb="xs" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Details</Text>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{selected.body}</Text>
              </Paper>
            )}

            {/* Thread */}
            {selectedResponses.length > 0 && (
              <Stack gap="xs">
                <Text size="xs" fw={600} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Responses ({selectedResponses.length})
                </Text>
                {selectedResponses.map(resp => {
                  const isGrantee = resp.authorRole === 'grantee';
                  return (
                    <Paper
                      key={resp.id}
                      withBorder
                      p="sm"
                      style={{
                        borderColor: resp.isInternal ? '#FDE68A' : '#e2e8f0',
                        background: resp.isInternal ? '#FFFBEB' : (isGrantee ? '#EFF6FF' : '#F0FDF4'),
                        marginLeft: isGrantee ? 0 : 24,
                      }}
                    >
                      <Group justify="space-between" mb={4}>
                        <Group gap="xs">
                          <Text size="xs" fw={700} style={{ color: isGrantee ? '#1E40AF' : '#059669' }}>
                            {resp.authorName || (isGrantee ? 'Grantee' : 'Applicant')}
                          </Text>
                          <Badge size="xs" variant="light" style={{ background: isGrantee ? '#DBEAFE' : '#DCFCE7', color: isGrantee ? '#2563EB' : '#059669' }}>
                            {isGrantee ? 'Grantee' : 'Applicant'}
                          </Badge>
                          {resp.isInternal && <Badge size="xs" style={{ background: '#FEF3C7', color: '#D97706' }}>Internal</Badge>}
                        </Group>
                        <Group gap="xs">
                          <Text size="xs" c="dimmed">{fmtDate(resp.created_at)}</Text>
                          {canEdit && !isApplicant && (
                            <ActionIcon
                              size="xs"
                              variant="subtle"
                              color="red"
                              onClick={() => deleteRfiResponse(resp.id, Number(resp.rfiId))}
                            >
                              <IconTrash size={12} />
                            </ActionIcon>
                          )}
                        </Group>
                      </Group>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{resp.body}</Text>
                    </Paper>
                  );
                })}
              </Stack>
            )}

            {/* Reply box */}
            {!['resolved','closed'].includes(selected.status) && (
              <Paper withBorder p="md" style={{ borderColor: '#e2e8f0' }}>
                <Text size="xs" fw={600} c="dimmed" mb="xs" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Add Response</Text>
                <Textarea
                  placeholder="Type your response…"
                  value={replyBody}
                  onChange={e => setReplyBody(e.currentTarget.value)}
                  minRows={3}
                  mb="xs"
                />
                <Group justify="space-between">
                  {!isApplicant && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={replyInternal}
                        onChange={e => setReplyInternal(e.currentTarget.checked)}
                        style={{ width: 14, height: 14 }}
                      />
                      Internal note (not visible to applicant)
                    </label>
                  )}
                  {isApplicant && <div />}
                  <Button
                    size="sm"
                    leftSection={<IconSend size={14} />}
                    style={{ background: '#1E40AF', color: '#fff', border: 'none' }}
                    loading={replySaving}
                    disabled={!replyBody.trim()}
                    onClick={handleReply}
                  >
                    Send
                  </Button>
                </Group>
              </Paper>
            )}

            {/* Internal notes (grantee only) */}
            {!isApplicant && selected.internalNotes && (
              <Paper withBorder p="sm" style={{ borderColor: '#FDE68A', background: '#FFFBEB' }}>
                <Text size="xs" fw={700} c="dimmed" mb={4}>Internal Notes</Text>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{selected.internalNotes}</Text>
              </Paper>
            )}
          </Stack>
        )}
      </div>

      {/* ── Create RFI Modal ── */}
      <Modal opened={showCreate} onClose={() => setShowCreate(false)} title="New Request for Information" size="lg">
        <Stack gap="sm">
          <TextInput
            label="Subject"
            placeholder="Brief description of the information needed"
            value={newSubject}
            onChange={e => setNewSubject(e.currentTarget.value)}
            required
          />
          <Textarea
            label="Body"
            placeholder="Detailed description, context, specific documents or data requested…"
            value={newBody}
            onChange={e => setNewBody(e.currentTarget.value)}
            minRows={4}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select
              label="Category"
              data={Object.entries(RFI_CATEGORY_LABELS).map(([v, l]) => ({ value: v, label: l }))}
              value={newCategory}
              onChange={v => setNewCategory(v || 'other')}
            />
            <Select
              label="Priority"
              data={[
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
                { value: 'high', label: 'High' },
                { value: 'urgent', label: 'Urgent' },
              ]}
              value={newPriority}
              onChange={v => setNewPriority(v || 'normal')}
            />
          </div>
          <TextInput label="Due Date" type="date" value={newDueDate} onChange={e => setNewDueDate(e.currentTarget.value)} />
          <Group justify="flex-end" mt="sm">
            <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button size="sm" style={{ background: '#1E40AF', color: '#fff', border: 'none' }} loading={saving} disabled={!newSubject.trim()} onClick={handleCreate}>
              Create RFI
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Edit RFI Modal ── */}
      <Modal opened={!!editingRfi} onClose={() => setEditingRfi(null)} title="Update RFI" size="md">
        <Stack gap="sm">
          <Select
            label="Status"
            data={[
              { value: 'open', label: 'Open' },
              { value: 'responded', label: 'Responded' },
              { value: 'in_review', label: 'In Review' },
              { value: 'resolved', label: 'Resolved' },
              { value: 'closed', label: 'Closed' },
            ]}
            value={editStatus}
            onChange={v => setEditStatus(v || 'open')}
          />
          <Select
            label="Priority"
            data={[
              { value: 'low', label: 'Low' },
              { value: 'normal', label: 'Normal' },
              { value: 'high', label: 'High' },
              { value: 'urgent', label: 'Urgent' },
            ]}
            value={editPriority}
            onChange={v => setEditPriority(v || 'normal')}
          />
          <Textarea
            label="Internal Notes"
            placeholder="Grantee-only notes (not visible to applicant)"
            value={editInternalNotes}
            onChange={e => setEditInternalNotes(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="outline" size="sm" onClick={() => setEditingRfi(null)}>Cancel</Button>
            <Button size="sm" style={{ background: '#1E40AF', color: '#fff', border: 'none' }} loading={editSaving} onClick={handleUpdateRfi}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}

// ── Project Tab Strip ─────────────────────────────────────────
// Replaces the native Tabs.List with a compact grouped icon strip
// so all 15 sections fit in 1140px without horizontal scrolling.

const ACENT = '#1E40AF';

interface StripChip {
  value: string;
  label: string;
  Icon: (props: { size?: number }) => JSX.Element | null;
  badge?: string | number;
}

interface DropGroup {
  label: string;
  items: StripChip[];
}

function ProjectTabStrip({ activeTab, onSelect, checklistDone, checklistTotal, docCount, taskCount, evidenceCount, rfiCount, isCloseout }: {
  activeTab: string;
  onSelect: (v: string) => void;
  checklistDone: number;
  checklistTotal: number;
  docCount: number;
  taskCount: number;
  evidenceCount: number;
  rfiCount: number;
  isCloseout?: boolean;
}) {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function openDrop(label: string) {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpenGroup(label);
  }
  function scheduleDrop() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenGroup(null), 140);
  }

  const inline: StripChip[] = [
    { value: 'checklist', label: `Checklist`, Icon: IconListCheck, badge: `${checklistDone}/${checklistTotal}` },
    { value: 'scope',     label: 'Scope & Damage', Icon: IconFileDescription },
    { value: 'costs',     label: 'Costs',          Icon: IconCurrencyDollar },
    { value: 'documents', label: 'Documents',       Icon: IconFile, badge: docCount || undefined },
    { value: 'tasks',     label: 'Tasks',           Icon: IconCheckbox, badge: taskCount || undefined },
    ...(isCloseout ? [{ value: 'closeout', label: 'Closeout', Icon: IconCircleCheck } as StripChip] : []),
  ];

  const groups: DropGroup[] = [
    {
      label: 'Records',
      items: [
        { value: 'photos',    label: 'Photos',       Icon: IconPhoto },
        { value: 'contracts', label: 'Contracts',    Icon: IconFileTypePdf },
        { value: 'evidence',  label: 'Evidence',     Icon: IconScan, badge: evidenceCount || undefined },
        { value: 'timeline',  label: 'Timeline',     Icon: IconClock },
      ],
    },
    {
      label: 'Compliance',
      items: [
        { value: 'insurance',  label: 'Insurance',             Icon: IconShieldCheck },
        { value: 'compliance', label: 'Compliance',            Icon: IconShieldCheck },
        { value: 'dob',        label: 'Duplication of Benefits', Icon: IconCurrencyDollar },
        { value: 'mitigation', label: '406 Mitigation',        Icon: IconLeaf },
      ],
    },
    {
      label: 'Admin',
      items: [
        { value: 'rfis',           label: 'RFIs',           Icon: IconMessageQuestion, badge: rfiCount || undefined },
        { value: 'versions',       label: 'PW Versions',    Icon: IconHistory },
        { value: 'notes',          label: 'Notes',          Icon: IconNotes },
        { value: 'extensions',     label: 'Extensions',     Icon: IconCalendarTime },
        { value: 'reimbursements', label: 'Reimbursements', Icon: IconReceipt },
      ],
    },
  ];

  const chipStyle = (active: boolean): import('react').CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '5px 16px', fontSize: 14, fontWeight: 500,
    borderRadius: 4, cursor: 'pointer', border: '1px solid',
    transition: 'all 0.15s', height: 30, flexShrink: 0,
    ...(active ? { background: ACENT, color: '#fff', borderColor: ACENT }
               : { background: 'rgba(0,0,0,0.04)', color: '#444', borderColor: 'transparent' }),
  });

  const dropBtnStyle = (groupActive: boolean): import('react').CSSProperties => ({
    ...chipStyle(groupActive),
    gap: 5,
  });

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4,
      background: 'white', border: '1px solid #e2e4e8', borderRadius: 4,
      padding: '8px 16px', marginBottom: 16,
      
    }}>
      {/* Inline primary chips */}
      {inline.map(chip => (
        <button key={chip.value} style={chipStyle(activeTab === chip.value)} onClick={() => onSelect(chip.value)}>
          <chip.Icon size={14} />
          <span>{chip.label}</span>
          {chip.badge !== undefined && (
            <span style={{
              background: activeTab === chip.value ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
              color: activeTab === chip.value ? '#fff' : '#7B7D7F',
              borderRadius: 4, fontSize: 12, padding: '0 5px', fontWeight: 600, lineHeight: '16px',
            }}>{chip.badge}</span>
          )}
        </button>
      ))}

      {/* Divider */}
      <div style={{ width: 1, height: 20, background: '#dde1e7', margin: '0 2px', flexShrink: 0 }} />

      {/* Group dropdowns */}
      {groups.map(group => {
        const groupActive = group.items.some(i => i.value === activeTab);
        const activeItem = group.items.find(i => i.value === activeTab);
        return (
          <div key={group.label} style={{ position: 'relative' }}
            onMouseEnter={() => openDrop(group.label)} onMouseLeave={scheduleDrop}>
            <button style={dropBtnStyle(groupActive)} onClick={() => setOpenGroup(o => o === group.label ? null : group.label)}>
              {activeItem ? <activeItem.Icon size={14} /> : null}
              <span>{activeItem ? activeItem.label : group.label}</span>
              {!activeItem && <IconChevronDown size={12} style={{ opacity: 0.6, transform: openGroup === group.label ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />}
            </button>
            {openGroup === group.label && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 300,
                background: '#fff', border: '1px solid #e2e4e8',
                borderRadius: 4, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                minWidth: 180, overflow: 'hidden',
              }}>
                {group.items.map(item => (
                  <button key={item.value} onClick={() => { onSelect(item.value); setOpenGroup(null); }} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', padding: '9px 16px', fontSize: 14,
                    background: activeTab === item.value ? `${ACENT}18` : 'transparent',
                    color: activeTab === item.value ? ACENT : '#333',
                    fontWeight: activeTab === item.value ? 600 : 400,
                    border: 'none', cursor: 'pointer', textAlign: 'left',
                    borderLeft: activeTab === item.value ? `3px solid ${ACENT}` : '3px solid transparent',
                  }}>
                    <item.Icon size={14} />
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {item.badge !== undefined && (
                      <span style={{ background: '#e2e8f0', color: '#7B7D7F', borderRadius: 4, fontSize: 12, padding: '0 5px', fontWeight: 600 }}>{item.badge}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Component ────────────────────────────────────────────────

export default function ProjectWorkspace() {
  const { orgId, projectId } = useParams<{ orgId: string; projectId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [costFilter, setCostFilter] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');

  const {
    organizations, disasters, damageItems, projects, projectCosts,
    insurance, compliance, documents, timelineEvents, notes,
    timeExtensions, reimbursementRequests, laborRecords, equipRecords, materials,
    loadingProjects, fetchAll, fetchLabor, fetchProjectCosts,
    fetchInsurance, fetchCompliance, fetchTimeline, fetchNotes, fetchDocuments,
    fetchEquipment, fetchMaterials, fetchContracts, fetchTimeExtensions,
    createProjectCost, updateProjectCost, deleteProjectCost,
    updateProject, createTimelineEvent, updateTimelineEvent, deleteTimelineEvent,
    deleteDocument, updateDocument, uploadDocumentFile, getDocumentDownloadUrl,
    createDamageItem, createReimbursement, updateReimbursement, fetchReimbursements,
    rfis, rfiResponses, rfisLoading, fetchRfis, fetchRfiResponses,
    createRfi, updateRfi, deleteRfi, createRfiResponse, deleteRfiResponse,
  } = usePAGrantsStore();
  const { assessments, loadAssessments } = useImpactStore();
  const { token } = useAuthStore();
  const myOrgIds = useMyPAOrgIds();
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isApplicantRole = ['pa_applicant_mgr', 'pa_applicant_staff', 'pa_applicant_viewer'].includes(effectiveRole?.roleSlug ?? '');
  const isApplicantViewer = effectiveRole?.roleSlug === 'pa_applicant_viewer';
  void isApplicantViewer; // used by conditional UI below

  useEffect(() => {
    fetchAll();
    loadAssessments();
  }, []);

  // ── Dynamic checklist from database ───────────────────────
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>(PA_PROJECT_CHECKLIST_FALLBACK);
  useEffect(() => {
    (async () => {
      try {
        const FC = FIELDS.paChecklistTemplates;
        const raw = await fetchTable<Record<string, unknown>>(TABLES.paChecklistTemplates);
        const paItems = raw
          .filter(r => String(r[FC.isActive] ?? '') === 'true' && (String(r[FC.programType] ?? '') === 'pa' || String(r[FC.programType] ?? '') === 'both'))
          .sort((a, b) => (parseInt(String(a[FC.sortOrder] ?? '0')) || 0) - (parseInt(String(b[FC.sortOrder] ?? '0')) || 0))
          .map(r => ({
            key: String(r[FC.itemKey] ?? ''),
            label: String(r[FC.label] ?? ''),
            category: String(r[FC.category] ?? 'document') as ChecklistItem['category'],
            description: String(r[FC.description] ?? ''),
            required: String(r[FC.required] ?? '') === 'true',
          }));
        if (paItems.length > 0) setChecklistItems(paItems);
      } catch { /* fallback to hardcoded */ }
    })();
  }, []);

  const DOC_TYPE_OPTIONS = useMemo(() => [
    { value: '', label: '— Select document type —' },
    { label: 'Documents', group: 'Documents' },
    ...checklistItems
      .filter(c => c.category === 'document')
      .map(c => ({ value: c.key, label: c.label })),
    { label: 'Forms', group: 'Forms' },
    ...checklistItems
      .filter(c => c.category === 'form')
      .map(c => ({ value: c.key, label: c.label })),
    { value: 'other', label: 'Other' },
  ], [checklistItems]);

  // ── Active project tab ────────────────────────────────────
  const initialTab = searchParams.get('tab') || 'checklist';
  const [activeTab, setActiveTab] = useState(initialTab);

  // ── Cost modal state ──────────────────────────────────────
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [femaPickerOpen, setFemaPickerOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<PAProjectCost | null>(null);
  const [costForm, setCostForm] = useState<CostFormState>(emptyCostForm());
  const [costSaving, setCostSaving] = useState(false);

  // ── Insurance modal state ─────────────────────────────────
  const [insModalOpen, setInsModalOpen] = useState(false);
  const [editingIns, setEditingIns] = useState<{ Rid: number } | null>(null);
  const [insForm, setInsForm] = useState<InsFormState>(emptyInsForm());
  const [insSaving, setInsSaving] = useState(false);

  // ── Compliance modal state ────────────────────────────────
  const [compModalOpen, setCompModalOpen] = useState(false);
  const [compForm, setCompForm] = useState<CompFormState>(emptyCompForm());
  const [compSaving, setCompSaving] = useState(false);

  // Compliance dependency auto-enforcement
  useEffect(() => {
    setCompForm(prev => {
      const next = { ...prev };
      let changed = false;
      // Historic property = yes → Section 106 required
      if (prev.historicProperty === 'yes' && prev.section106Required !== 'yes') {
        next.section106Required = 'yes'; changed = true;
      }
      // In floodplain = yes → flood insurance required, EO 11988 compliance required
      if (prev.inFloodplain === 'yes') {
        if (prev.floodInsuranceRequired !== 'yes') { next.floodInsuranceRequired = 'yes'; changed = true; }
        if (prev.eo11988Compliance !== 'yes') { next.eo11988Compliance = 'yes'; changed = true; }
      }
      // Hazmat present = yes → NEPA required
      if (prev.hazmatPresent === 'yes' && prev.nepaRequired !== 'yes') {
        next.nepaRequired = 'yes'; changed = true;
      }
      return changed ? next : prev;
    });
  }, [compForm.historicProperty, compForm.inFloodplain, compForm.hazmatPresent]);

  // ── Timeline event modal state ────────────────────────────
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventForm, setEventForm] = useState<EventFormState>(emptyEventForm());
  const [eventSaving, setEventSaving] = useState(false);

  // ── Edit Project modal state ──────────────────────────────
  const [editProjModalOpen, setEditProjModalOpen] = useState(false);
  const [editProjForm, setEditProjForm] = useState<EditProjectFormState>(emptyEditProjectForm());
  const [editProjSaving, setEditProjSaving] = useState(false);
  const [editProjTab, setEditProjTab] = useState<string | null>('general');

  // ── Notes saving state ────────────────────────────────────
  const [noteSaving, setNoteSaving] = useState(false);

  // ── Task modal state ────────────────────────────────────────
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ description: '', dueDate: '', status: 'pending' });
  const [taskSaving, setTaskSaving] = useState(false);
  const [templatePopoverOpen, setTemplatePopoverOpen] = useState(false);

  // ── Document upload state ──────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingDamage, setEditingDamage] = useState(false);
  const [editingScope, setEditingScope]   = useState(false);
  const [damageText, setDamageText]       = useState('');
  const [scopeText, setScopeText]         = useState('');
  const [savingScope, setSavingScope]     = useState(false);

  // ── RFR detail / review (inline) ─────────────────────────
  const [selectedRfr, setSelectedRfr] = useState<any | null>(null);
  const [rfrReviewNotes, setRfrReviewNotes] = useState('');
  const [rfrAdjustedAmount, setRfrAdjustedAmount] = useState('');
  const [savingRfrAction, setSavingRfrAction] = useState(false);

  // ── New RFR modal ─────────────────────────────────────────
  const [rfrModalOpen, setRfrModalOpen] = useState(false);
  const [rfrForm, setRfrForm] = useState({ periodStart: '', periodEnd: '', notes: '' });
  const [rfrLinked, setRfrLinked] = useState<Set<string>>(new Set());
  const [rfrExtra, setRfrExtra] = useState<{ description: string; category: string; amount: string }[]>([]);
  const [savingRfr, setSavingRfr] = useState(false);

  // ── Add Damage Site modal ─────────────────────────────────
  const [addSiteOpen, setAddSiteOpen] = useState(false);
  const emptySiteForm = () => ({ siteName: '', description: '', damageCategory: '', severity: '', estimatedCost: '', address: '', city: '', state: '' });
  const [siteForm, setSiteForm] = useState(emptySiteForm());
  const [savingSite, setSavingSite] = useState(false);

  // ── Milestone tracker ─────────────────────────────────────
  const [milestoneForm, setMilestoneForm] = useState({ title: '', eventDate: '' });
  const [savingMilestone, setSavingMilestone] = useState(false);
  const [addingMilestone, setAddingMilestone] = useState(false);
  const [togglingMilestone, setTogglingMilestone] = useState<number | null>(null);
  const [editingMilestone, setEditingMilestone] = useState<number | null>(null);
  const [editMilestoneForm, setEditMilestoneForm] = useState({ title: '', eventDate: '' });
  const [savingEditMilestone, setSavingEditMilestone] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateSelected, setTemplateSelected] = useState<Record<string, boolean>>({});
  const [savingFromTemplate, setSavingFromTemplate] = useState(false);

  // ── Duplication of Benefits state ─────────────────────────────
  const [dobSources, setDobSources] = useState<DobSource[]>([]);
  const [dobCert, setDobCert] = useState<DobCertification>({ name: '', title: '', date: '' });
  const [savingDob, setSavingDob] = useState(false);
  const [dobModalOpen, setDobModalOpen] = useState(false);
  const [dobEditIdx, setDobEditIdx] = useState<number | null>(null);
  const [dobDraft, setDobDraft] = useState<DobSource>({ id: '', type: 'insurance', program: '', amount: 0, notes: '', linkedCostKeys: [] });
  const [dobAmountStr, setDobAmountStr] = useState('');
  const [dobExpanded, setDobExpanded] = useState<Set<string>>(new Set());

  // ── Closeout state ─────────────────────────────────────────────
  const [rtmDate, setRtmDate] = useState('');
  const [savingCloseout, setSavingCloseout] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState('');
  const [viewDoc, setViewDoc] = useState<any>(null);
  const [docFileAvailable, setDocFileAvailable] = useState<boolean | null>(null); // null=checking, true=ok, false=unavailable
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editingDoc, setEditingDoc] = useState(false);
  const [editDocForm, setEditDocForm] = useState({ title: '', documentType: '', tags: '', notes: '' });
  const [editDocFile, setEditDocFile] = useState<File | null>(null);
  const editDocFileRef = useRef<HTMLInputElement>(null);
  const [uploadNotes, setUploadNotes] = useState('');
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiDocResult, setAiDocResult] = useState<string>('');

  // ── Checklist completion modal state ───────────────────────
  const [checkModalOpen, setCheckModalOpen] = useState(false);
  const [checkingItem, setCheckingItem] = useState<ChecklistItem | null>(null);
  const [checkNotes, setCheckNotes] = useState('');
  const [checkSaving, setCheckSaving] = useState(false);
  const [checkDate, setCheckDate] = useState('');
  const [checkReviewerName, setCheckReviewerName] = useState('');
  const [checkReviewerOrg, setCheckReviewerOrg] = useState('');
  const [checkOutcome, setCheckOutcome] = useState('');
  const [checkRefNumber, setCheckRefNumber] = useState('');

  // ── Revision request state ──────────────────────────────────
  const [revisionItem, setRevisionItem] = useState<ChecklistItem | null>(null);
  const [revisionNote, setRevisionNote] = useState('');
  const [revisionSaving, setRevisionSaving] = useState(false);

  // ── AI Cost Extraction state ─────────────────────────────────
  const [extracting, setExtracting] = useState(false);
  const [extractedCosts, setExtractedCosts] = useState<CostFormState[]>([]);
  const [extractModalOpen, setExtractModalOpen] = useState(false);
  const [extractSource, setExtractSource] = useState('');

  // ── AI Assist state ────────────────────────────────────────────
  const [aiAssistOpen, setAiAssistOpen] = useState(false);
  const [aiAssistLoading, setAiAssistLoading] = useState(false);
  const [aiAssistResult, setAiAssistResult] = useState('');

  // ── Special Consideration Questions state ────────────────────
  const [scqAnswers, setScqAnswers] = useState<ScqAnswers>({});
  const [scqSaving, setScqSaving] = useState(false);
  const [scqExpanded, setScqExpanded] = useState(true);

  // ── AI Scan Receipt state ─────────────────────────────────────
  const scanInputRef = useRef<HTMLInputElement>(null);
  const [scanning, setScanning] = useState(false);
  const [scannedCosts, setScannedCosts] = useState<CostFormState[]>([]);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scanSource, setScanSource] = useState('');

  // ── Timesheet import state ─────────────────────────────────
  const [timesheetImportOpen, setTimesheetImportOpen] = useState(false);
  const [timesheetSelected, setTimesheetSelected] = useState<Set<number>>(new Set());
  const [timesheetImporting, setTimesheetImporting] = useState(false);
  const [tsSearch, setTsSearch] = useState('');
  const [tsCatFilter, setTsCatFilter] = useState<string | null>(null);

  // ── Evidence / linked assessments state ──────────────────
  const [evidenceSearch, setEvidenceSearch] = useState('');
  const [evidenceSaving, setEvidenceSaving] = useState(false);

  // ── Derived data ───────────────────────────────────────────

  const project = useMemo(
    () => projects.find(p => p.Rid === Number(projectId)),
    [projects, projectId],
  );

  const org = useMemo(
    () => organizations.find(o => o.Rid === Number(orgId)),
    [organizations, orgId],
  );

  const disaster = useMemo(
    () => project ? disasters.find(d => String(d.Rid) === project.disasterId) : undefined,
    [disasters, project],
  );

  const projRid = String(project?.Rid ?? '');

  // Parse linked assessment IDs from project notes (marker: |||EVIDENCE_IDS|||[1,2]|||)
  const linkedAssessmentIds = useMemo<number[]>(() => {
    const raw = project?.notes ?? '';
    const match = raw.match(/\|\|\|EVIDENCE_IDS\|\|\|([\s\S]*?)\|\|\|/);
    if (!match) return [];
    try { return JSON.parse(match[1]); } catch { return []; }
  }, [project?.notes]);

  // Load all project sub-records once project resolves — must be after project useMemo to avoid TDZ
  useEffect(() => {
    if (!project?.Rid) return;
    const id = project.Rid;
    fetchProjectCosts(id);
    fetchInsurance(id);
    fetchCompliance(id);
    fetchTimeline(id);
    fetchNotes(id);
    fetchDocuments(id);
    fetchEquipment(id);
    fetchMaterials(id);
    fetchContracts(id);
    fetchLabor();
    fetchTimeExtensions(id);
    fetchRfis(id);
  }, [project?.Rid]); // eslint-disable-line react-hooks/exhaustive-deps

  const projDamageItems = useMemo(
    () => damageItems.filter(d => d.projectId === projRid),
    [damageItems, projRid],
  );

  const projRfis = useMemo(
    () => rfis.filter(r => r.projectId === projRid),
    [rfis, projRid],
  );

  const projCosts = useMemo(() => {
    const filtered = projectCosts.filter(c => c.projectId === projRid);
    if (!costFilter) return filtered;
    return filtered.filter(c => c.costType === costFilter);
  }, [projectCosts, projRid, costFilter]);

  const projInsurance = useMemo(
    () => insurance.filter(i => i.projectId === projRid),
    [insurance, projRid],
  );

  const projCompliance = useMemo(
    () => compliance.filter(c => c.projectId === projRid),
    [compliance, projRid],
  );

  const projDocuments = useMemo(
    () => documents.filter(d => d.projectId === projRid),
    [documents, projRid],
  );

  const projTimeline = useMemo(
    () => timelineEvents.filter(t => t.projectId === projRid)
      .sort((a, b) => (a.eventDate || '').localeCompare(b.eventDate || '')),
    [timelineEvents, projRid],
  );

  const projNotes = useMemo(
    () => notes.filter(n => String(n.projectId) === projRid),
    [notes, projRid],
  );

  const projTasks = useMemo(
    () => timelineEvents
      .filter(t => t.projectId === projRid && (t.eventType === 'task' || t.eventType === 'deadline'))
      .sort((a, b) => (a.dueDate || '9').localeCompare(b.dueDate || '9')),
    [timelineEvents, projRid],
  );

  const todayStr = new Date().toISOString().split('T')[0];

  // Cost summary by type
  const costsByType = useMemo(() => {
    const all = projectCosts.filter(c => c.projectId === projRid);
    const groups: Record<string, number> = {};
    for (const c of all) {
      const t = c.costType || 'other';
      groups[t] = (groups[t] || 0) + num(c.totalCost);
    }
    return groups;
  }, [projectCosts, projRid]);

  // Labor records assigned to this project
  const projLabor = useMemo(
    () => laborRecords.filter(r => r.projectId === projRid),
    [laborRecords, projRid],
  );

  // Unassigned labor records for import (only supervisor-approved)
  const unassignedLabor = useMemo(() => {
    let filtered = laborRecords.filter(r => !r.projectId && r.timesheetOnFile === 'approved');
    if (tsSearch) {
      const q = tsSearch.toLowerCase();
      filtered = filtered.filter(r =>
        (r.employeeName || '').toLowerCase().includes(q) ||
        (r.activityDescription || '').toLowerCase().includes(q)
      );
    }
    if (tsCatFilter) {
      filtered = filtered.filter(r => r.category === tsCatFilter);
    }
    return filtered.sort((a, b) => (b.workDate || '').localeCompare(a.workDate || ''));
  }, [laborRecords, tsSearch, tsCatFilter]);

  // Labor assigned to OTHER projects (for grayed-out display)
  const otherProjectLabor = useMemo(
    () => laborRecords.filter(r => r.projectId && r.projectId !== projRid),
    [laborRecords, projRid],
  );

  // Documents grouped by type
  const docsByType = useMemo(() => {
    const groups: Record<string, typeof projDocuments> = {};
    for (const d of projDocuments) {
      const t = d.documentType || 'Other';
      if (!groups[t]) groups[t] = [];
      groups[t].push(d);
    }
    return groups;
  }, [projDocuments]);

  // ── Checklist completion tracking ───────────────────────────
  // A checklist item is "done" if a matching document tag or timeline event exists
  // A revision request overrides "done" — item needs rework
  const checklistStatus = useMemo(() => {
    const status = new Map<string, { done: boolean; revision?: boolean; revisionNote?: string; revisionBy?: string; revisionDate?: string; date?: string; by?: string; docRid?: number }>();
    for (const item of checklistItems) {
      const matchingDoc = projDocuments.find(d => d.tags === item.key);
      const matchingEvent = projTimeline.find(t =>
        t.description?.includes(`[checklist:${item.key}]`)
      );
      // Check for revision requests — most recent by Rid (higher Rid = created later)
      const revisionEvents = projTimeline
        .filter(t => t.description?.includes(`[revision:${item.key}]`))
        .sort((a, b) => b.Rid - a.Rid);
      const latestRevision = revisionEvents[0];
      // Revision is active if it's newer than the completion event (both are timeline events, so Rid comparison works)
      // For documents, compare revision date >= upload date (same-day revision should win)
      const completionEventRid = matchingEvent?.Rid ?? 0;
      const hasRevision = latestRevision && (
        latestRevision.Rid > completionEventRid &&
        (!matchingDoc || latestRevision.eventDate >= (matchingDoc.uploadDate || ''))
      );

      if (hasRevision) {
        const note = latestRevision.description.replace(/\[revision:[^\]]+\]\s*/, '').replace(/^—\s*/, '');
        status.set(item.key, { done: false, revision: true, revisionNote: note, revisionBy: latestRevision.creatorId, revisionDate: latestRevision.eventDate });
      } else if (matchingDoc) {
        status.set(item.key, { done: true, date: matchingDoc.uploadDate, by: matchingDoc.uploadedBy, docRid: matchingDoc.Rid });
      } else if (matchingEvent) {
        status.set(item.key, { done: true, date: matchingEvent.eventDate, by: matchingEvent.creatorId });
      } else {
        status.set(item.key, { done: false });
      }
    }
    return status;
  }, [projDocuments, projTimeline, checklistItems]);

  const checklistDone = useMemo(() =>
    checklistItems.filter(c => checklistStatus.get(c.key)?.done).length,
    [checklistStatus, checklistItems],
  );
  const checklistRequired = useMemo(() => checklistItems.filter(c => c.required).length, [checklistItems]);
  const checklistRequiredDone = useMemo(() =>
    checklistItems.filter(c => c.required && checklistStatus.get(c.key)?.done).length,
    [checklistStatus, checklistItems],
  );

  // ── Initialize SCQ from compliance record ──────────────────
  const scqInitialized = useRef(false);
  useEffect(() => {
    if (scqInitialized.current || projCompliance.length === 0) return;
    scqInitialized.current = true;
    const comp = projCompliance[0];
    // Parse stored SCQ data from complianceNotes — handles both formats:
    // Format A (from save): {scq1: "comment text", scq2: "comment"}
    // Format B (from seed): {scq1: {answer: "Yes", comment: "text"}}
    let storedData: Record<string, { answer?: string; comment?: string } | string> = {};
    try {
      if (comp.complianceNotes) storedData = JSON.parse(comp.complianceNotes);
    } catch { /* not JSON, ignore */ }

    const answers: ScqAnswers = {};
    for (const q of SCQ_QUESTIONS) {
      const fieldVal = q.compField ? (comp as unknown as Record<string, string>)[q.compField] || '' : '';
      const stored = storedData[q.key];
      let answer = '';
      let comment = '';
      if (typeof stored === 'object' && stored !== null) {
        answer = stored.answer || '';
        comment = stored.comment || '';
      } else if (typeof stored === 'string') {
        comment = stored;
      }
      // Field value takes precedence for answer if available
      if (fieldVal) {
        const norm = fieldVal.toLowerCase();
        answer = norm === 'yes' ? 'yes' : norm === 'no' ? 'no' : norm === 'unsure' ? 'unsure' : answer;
      } else if (answer) {
        const norm = answer.toLowerCase();
        answer = norm === 'yes' ? 'yes' : norm === 'no' ? 'no' : norm === 'unsure' ? 'unsure' : '';
      }
      answers[q.key] = { answer, comment };
    }
    // Q5 uses project-level hazardMitigationIncluded
    if (project) {
      const hm = (project.hazardMitigationIncluded || '').toLowerCase();
      const existingComment = answers.scq5?.comment || '';
      answers.scq5 = { answer: hm === 'yes' ? 'yes' : hm === 'no' ? 'no' : '', comment: existingComment };
    }
    setScqAnswers(answers);

    // Load DOB data from compliance notes
    const sd = storedData as Record<string, unknown>;
    if (Array.isArray(sd.dobSources)) setDobSources(sd.dobSources as DobSource[]);
    if (sd.dobCertification && typeof sd.dobCertification === 'object') {
      setDobCert(sd.dobCertification as DobCertification);
    }
  }, [projCompliance, project]);

  // Sync rtmDate from project when it loads
  useEffect(() => {
    if (project?.rtmDate) setRtmDate(project.rtmDate);
  }, [project?.rtmDate]);

  // ── Document upload handlers ────────────────────────────────

  function onFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFile(file);
    // Don't clear uploadDocType here — checklist buttons pre-set it before triggering this input
    setUploadNotes('');
    setAiDocResult('');
    setUploadModalOpen(true);
    e.target.value = '';
  }

  // ── Evidence linking helpers ─────────────────────────────────
  async function handleLinkAssessment(assessmentId: number) {
    if (!project?.Rid) return;
    const newIds = [...new Set([...linkedAssessmentIds, assessmentId])];
    setEvidenceSaving(true);
    try {
      const notes = project.notes ?? '';
      const marker = '|||EVIDENCE_IDS|||';
      const hasMarker = notes.includes(marker);
      const blob = `${marker}${JSON.stringify(newIds)}|||`;
      const newNotes = hasMarker
        ? notes.replace(/\|\|\|EVIDENCE_IDS\|\|\|[\s\S]*?\|\|\|/, blob)
        : (notes ? `${notes}\n${blob}` : blob);
      await updateProject(project.Rid, { notes: newNotes });
      await fetchAll();
    } catch { /* ignore */ } finally { setEvidenceSaving(false); }
  }

  async function handleUnlinkAssessment(assessmentId: number) {
    if (!project?.Rid) return;
    const newIds = linkedAssessmentIds.filter(id => id !== assessmentId);
    setEvidenceSaving(true);
    try {
      const notes = project.notes ?? '';
      const blob = `|||EVIDENCE_IDS|||${JSON.stringify(newIds)}|||`;
      const newNotes = notes.replace(/\|\|\|EVIDENCE_IDS\|\|\|[\s\S]*?\|\|\|/, blob);
      await updateProject(project.Rid, { notes: newNotes });
      await fetchAll();
    } catch { /* ignore */ } finally { setEvidenceSaving(false); }
  }

  async function handleAiAnalyze() {
    if (!uploadFile) return;
    setAiAnalyzing(true);
    setAiDocResult('');
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(uploadFile);
      });

      const isImage = uploadFile.type.startsWith('image/');
      const isPdf = uploadFile.type === 'application/pdf';

      const contentBlocks: any[] = [];
      if (isImage) {
        contentBlocks.push({ type: 'image', source: { type: 'base64', media_type: uploadFile.type, data: base64 } });
      } else if (isPdf) {
        contentBlocks.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } });
      }
      contentBlocks.push({ type: 'text', text: `Analyze this document for a FEMA Public Assistance disaster recovery project. The document is named "${uploadFile.name}".

Identify what type of document this is (insurance policy, adjuster report, invoice, receipt, contract, inspection report, environmental assessment, photo, permit, etc.).

Extract any key data that should be entered into the project management system, such as:
- Insurance: policy numbers, carrier, coverage amounts, claim numbers, settlement amounts
- Contracts: contractor name, contract number, amounts, dates, scope
- Invoices: vendor, line items, quantities, costs, invoice numbers
- Inspection reports: inspector name, date, findings, recommendations
- Environmental: NEPA level, compliance status, protected species findings
- Any other relevant data points

Format your response as a clear summary with the document type at the top, then the extracted data as a bulleted list. Be specific with numbers, dates, and names.` });

      const result = await callClaude([
        { role: 'user', content: contentBlocks },
      ], {
        system: 'You are a document analysis assistant for FEMA Public Assistance grant management. Extract structured data from documents accurately. Only report what you can actually see in the document — do not make up values.',
        maxTokens: 2000,
      });
      setAiDocResult(result);
    } catch (err) {
      setAiDocResult(`Analysis failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setAiAnalyzing(false);
    }
  }

  async function handleAiExtractCosts() {
    if (!uploadFile) return;
    setAiAnalyzing(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(uploadFile);
      });

      const isImage = uploadFile.type.startsWith('image/');
      const isPdf = uploadFile.type === 'application/pdf';
      const contentBlocks: any[] = [];
      if (isImage) {
        contentBlocks.push({ type: 'image', source: { type: 'base64', media_type: uploadFile.type, data: base64 } });
      } else if (isPdf) {
        contentBlocks.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } });
      }
      contentBlocks.push({ type: 'text', text: `Extract ALL cost line items from this document. This is for a FEMA Public Assistance disaster recovery project.

For each line item return a JSON object with these fields:
- description: string (what the item is)
- quantity: string (number)
- unit: string (EA, LF, SF, HR, LS, etc.)
- unitCost: string (dollar amount without $ sign)
- totalCost: string (dollar amount without $ sign)
- costType: string (one of: labor, equipment, materials, contracts, rented_equip, donated, other)
- cefPart: string (A=Force Account Labor, B=Force Account Equipment, C=Materials, D=Rented Equipment, E=Contract Work, F=Direct Admin)

Return ONLY a raw JSON array. No explanation, no markdown fences. Extract the REAL numbers from the document — do NOT make up values. If you cannot read specific values, use "0".` });

      const result = await callClaude([
        { role: 'user', content: contentBlocks },
      ], {
        system: 'You extract cost data from invoices, receipts, and financial documents for FEMA PA grant management. Return only valid JSON arrays. Never fabricate data.',
        maxTokens: 4096,
      });

      // Parse JSON from response
      let cleaned = result.trim();
      if (cleaned.includes('```')) {
        const fenceMatch = cleaned.match(/```(?:json)?\s*\n?([\s\S]*?)\n?\s*```/);
        if (fenceMatch) cleaned = fenceMatch[1];
      }
      const match = cleaned.match(/\[[\s\S]*\]/);
      if (!match) throw new Error('No cost data found in document');
      const items = JSON.parse(match[0]);
      if (!Array.isArray(items) || items.length === 0) throw new Error('No line items extracted');

      const costForms: CostFormState[] = items.map((item: any) => ({
        costType: item.costType || 'other',
        cefPart: item.cefPart || 'F',
        description: item.description || '',
        quantity: String(item.quantity || '1'),
        unit: item.unit || 'EA',
        unitCost: String(item.unitCost || '0'),
        totalCost: String(item.totalCost || '0'),
        vendorEmployee: '',
        sourceReference: uploadFile.name,
        estimatedOrActual: 'actual',
        eligible: 'true',
        notes: `Extracted by AI from ${uploadFile.name}`,
      }));

      setScanSource(uploadFile.name);
      setScannedCosts(costForms);
      setUploadModalOpen(false);
      setScanModalOpen(true);
      notifications.show({ message: `Found ${costForms.length} cost items — review before importing`, color: 'var(--data-viz-650)' });
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Cost extraction failed', color: 'var(--color-error)' });
    } finally {
      setAiAnalyzing(false);
    }
  }

  async function handleUpload() {
    if (!uploadFile || !project) return;
    setUploading(true);
    try {
      const FDoc = FIELDS.paDocuments;
      const today = new Date().toISOString().split('T')[0];
      const user = useAuthStore.getState().user;
      const fileType = deriveDocType(uploadFile.name);

      // Determine the document type label for display
      const checkItem = checklistItems.find(c => c.key === uploadDocType);
      const docTypeLabel = checkItem?.label || fileType;

      // Step 1: Create document metadata record
      const fields: Array<[string, string]> = [
        [FDoc.projectId, projRid],
        [FDoc.organizationId, project.organizationId || ''],
        [FDoc.title, uploadFile.name],
        [FDoc.documentType, docTypeLabel],
        [FDoc.uploadedBy, user?.email || ''],
        [FDoc.tags, uploadDocType],
      ];
      const rid = await createRecord(TABLES.paDocuments, fields, token ?? undefined);

      // Step 2: Upload via store (mirrors debris/fileLibrary pattern — stores documentId in notes meta marker)
      await uploadDocumentFile(rid, uploadFile, uploadNotes || '');

      // Step 3: If tagged to a checklist item, create a timeline event
      if (checkItem) {
        await createTimelineEvent({
          projectId: projRid,
          organizationId: project.organizationId || '',
          eventType: 'submission',
          eventDate: today,
          description: `${checkItem.label} uploaded [checklist:${checkItem.key}]`,
          status: 'completed',
          creatorId: user?.email || '',
        });
      }

      // Explicit project-scoped refresh so checklist updates immediately
      await fetchDocuments(Number(projRid));
      setUploadModalOpen(false);
      setUploadDocType('');
      notifications.show({ title: 'Uploaded', message: `${uploadFile.name} — ${docTypeLabel}`, color: 'var(--color-success)' });
    } catch (e: unknown) {
      notifications.show({ title: 'Upload failed', message: e instanceof Error ? e.message : 'Error', color: 'var(--color-error)' });
    } finally {
      setUploading(false);
    }
  }

  // ── Duplication of Benefits helpers ────────────────────────────

  function openAddDob() {
    setDobDraft({ id: crypto.randomUUID(), type: 'insurance', program: '', amount: 0, notes: '', linkedCostKeys: [] });
    setDobAmountStr('');
    setDobEditIdx(null);
    setDobModalOpen(true);
  }
  function openEditDob(idx: number) {
    const src = dobSources[idx];
    setDobDraft({ ...src, linkedCostKeys: src.linkedCostKeys || [] });
    setDobAmountStr(src.amount > 0 ? fmt(src.amount) : '');
    setDobEditIdx(idx);
    setDobModalOpen(true);
  }
  function saveDobDraft() {
    if (dobEditIdx === null) {
      setDobSources(prev => [...prev, dobDraft]);
    } else {
      setDobSources(prev => prev.map((r, i) => i === dobEditIdx ? dobDraft : r));
    }
    setDobModalOpen(false);
  }
  function removeDobRow(idx: number) {
    setDobSources(prev => prev.filter((_, i) => i !== idx));
  }

  async function saveDobData() {
    if (!project) return;
    const comp = projCompliance[0];
    setSavingDob(true);
    try {
      const FC2 = FIELDS.paCompliance;
      let existingNotes: Record<string, unknown> = {};
      try { if (comp?.complianceNotes) existingNotes = JSON.parse(comp.complianceNotes); } catch { /* ignore */ }
      const merged = { ...existingNotes, dobSources, dobCertification: dobCert };
      if (comp) {
        await updateRecord(TABLES.paCompliance, comp.Rid, [[FC2.complianceNotes, JSON.stringify(merged)]]);
        await fetchCompliance(Number(projRid));
      } else {
        // Create compliance record just to hold DOB data
        await createRecord(TABLES.paCompliance, [
          [FC2.projectId, projRid],
          [FC2.complianceNotes, JSON.stringify(merged)],
        ]);
        await fetchCompliance(Number(projRid));
      }
      notifications.show({ message: 'DOB data saved', color: 'var(--color-success)' });
    } catch (e) {
      notifications.show({ message: e instanceof Error ? e.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSavingDob(false); }
  }

  // ── Closeout helpers ────────────────────────────────────────────

  async function handleSaveRtm() {
    if (!project) return;
    setSavingCloseout(true);
    try {
      await updateProject(project.Rid, { rtmDate });
      notifications.show({ message: 'RTM date saved', color: 'var(--color-success)' });
    } finally { setSavingCloseout(false); }
  }

  async function handleCloseoutSubmit() {
    if (!project) return;
    setSavingCloseout(true);
    try {
      const now = new Date().toISOString();
      await updateProject(project.Rid, { closeoutSubmittedAt: now });
      await createTimelineEvent({
        projectId: projRid,
        organizationId: project.organizationId || '',
        eventType: 'milestone',
        eventDate: now,
        description: 'Closeout package submitted to State [checklist:closeout_submitted]',
        status: 'completed',
        creatorId: useAuthStore.getState().user?.fullName || useAuthStore.getState().user?.email || '',
      });
      notifications.show({ message: 'Closeout submitted. 3-year retention clock starts today.', color: 'var(--color-success)' });
    } finally { setSavingCloseout(false); }
  }

  // ── Checklist completion (for non-document items) ──────────

  function openCheckOff(item: ChecklistItem) {
    setCheckingItem(item);
    setCheckNotes('');
    setCheckDate(new Date().toISOString().split('T')[0]);
    setCheckReviewerName('');
    setCheckReviewerOrg('');
    setCheckOutcome('');
    setCheckRefNumber('');
    setCheckModalOpen(true);
  }

  async function handleCheckOff() {
    if (!checkingItem || !project) return;
    setCheckSaving(true);
    try {
      const user = useAuthStore.getState().user;
      let desc = `${checkingItem.label} completed [checklist:${checkingItem.key}]`;
      if (checkingItem.category === 'review') {
        if (checkReviewerName) desc += ` — Reviewer: ${checkReviewerName}`;
        if (checkReviewerOrg) desc += ` (${checkReviewerOrg})`;
        if (checkOutcome) desc += ` · Outcome: ${checkOutcome}`;
        if (checkRefNumber) desc += ` · Ref #: ${checkRefNumber}`;
      }
      if (checkNotes) desc += ` — ${checkNotes}`;
      await createTimelineEvent({
        projectId: projRid,
        organizationId: project.organizationId || '',
        eventType: checkingItem.category === 'milestone' ? 'milestone' : checkingItem.category === 'review' ? 'approval' : 'submission',
        eventDate: checkDate || new Date().toISOString().split('T')[0],
        description: desc,
        status: 'completed',
        creatorId: user?.email || '',
      });
      await fetchAll();
      setCheckModalOpen(false);
      notifications.show({ title: 'Checked off', message: checkingItem.label, color: 'var(--color-success)' });
    } catch (e: unknown) {
      notifications.show({ title: 'Error', message: e instanceof Error ? e.message : 'Failed', color: 'var(--color-error)' });
    } finally {
      setCheckSaving(false);
    }
  }

  // ── Revision request handler ───────────────────────────────

  async function handleRequestRevision() {
    if (!revisionItem || !project) return;
    setRevisionSaving(true);
    try {
      const user = useAuthStore.getState().user;
      const today = new Date().toISOString().split('T')[0];
      await createTimelineEvent({
        projectId: projRid,
        organizationId: project.organizationId || '',
        eventType: 'correspondence',
        eventDate: today,
        description: `[revision:${revisionItem.key}] — ${revisionNote || 'Revision requested'}`,
        status: 'pending',
        creatorId: user?.email || '',
      });
      notifications.show({ color: '#F86A0B', title: 'Revision Requested', message: `"${revisionItem.label}" sent back for revision.` });
      setRevisionItem(null);
      setRevisionNote('');
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Failed', color: 'var(--color-error)' });
    } finally {
      setRevisionSaving(false);
    }
  }

  // ── AI Cost Extraction handlers ────────────────────────────

  async function handleExtractCosts(doc: { documentName: string; Rid: number; mimeType?: string; tags?: string }) {
    setExtracting(true);
    setExtractSource(doc.documentName);
    try {
      const systemPrompt = `You are a FEMA Public Assistance cost extraction specialist. Extract all cost line items from the uploaded document (invoice, receipt, contract, or estimate).

For each line item, return a JSON array with objects containing:
- description: string (what the item/service is)
- quantity: string (number of units, default "1")
- unit: string (unit of measure: hours, EA, LF, CY, LS, etc.)
- unitCost: string (cost per unit as a number string)
- totalCost: string (total cost as a number string)
- costType: string (one of: labor, equipment, materials, contracts, rented_equip, donated, other)
- cefPart: string (one of: A, B, C, D, E, F — A=Force Account Labor, B=Force Account Equipment, C=Materials, D=Rented Equipment, E=Contract Work, F=Direct Administrative Costs)

Categorize intelligently:
- Labor/wages/salaries → costType: "labor", cefPart: "A"
- Equipment ownership/usage → costType: "equipment", cefPart: "B"
- Materials/supplies purchased → costType: "materials", cefPart: "C"
- Rented equipment → costType: "rented_equip", cefPart: "D"
- Contractor/subcontractor work → costType: "contracts", cefPart: "E"
- Admin/overhead → costType: "other", cefPart: "F"

Return ONLY a JSON array. No markdown, no explanation.`;

      const userMessage = `Extract all cost line items from this document: "${doc.documentName}"

Document type: ${doc.mimeType || 'unknown'}
Tagged as: ${doc.tags || 'untagged'}

Please analyze the document name and any available context to extract realistic cost line items. If this appears to be an invoice, extract each line item. If a contract, extract the major cost categories. If a receipt, extract each purchased item.`;

      const response = await callClaude(
        [{ role: 'user', content: userMessage }],
        { system: systemPrompt, maxTokens: 4096 }
      );

      // Parse JSON response — extract JSON array even if surrounded by text
      let cleaned = response.trim();
      if (cleaned.includes('```')) {
        const fenceMatch = cleaned.match(/```(?:json)?\s*\n?([\s\S]*?)\n?\s*```/);
        if (fenceMatch) cleaned = fenceMatch[1];
      }
      const arrayMatch = cleaned.match(/\[[\s\S]*\]/);
      if (!arrayMatch) throw new Error('AI did not return a valid cost list');

      const items = JSON.parse(arrayMatch[0]) as Array<{
        description: string; quantity: string; unit: string;
        unitCost: string; totalCost: string; costType: string; cefPart: string;
      }>;

      const costForms: CostFormState[] = items.map(item => ({
        costType: item.costType || 'other',
        description: item.description || '',
        quantity: String(item.quantity || '1'),
        unit: item.unit || 'EA',
        unitCost: String(item.unitCost || '0'),
        totalCost: String(item.totalCost || '0'),
        cefPart: item.cefPart || '',
        vendorEmployee: '',
        sourceReference: doc.documentName,
        estimatedOrActual: 'actual',
        eligible: 'true',
        notes: `Extracted by AI from ${doc.documentName}`,
      }));

      setExtractedCosts(costForms);
      setExtractModalOpen(true);
    } catch (err) {
      console.error('AI extraction failed:', err);
      notifications.show({
        title: 'Extraction failed',
        message: err instanceof Error ? err.message : 'Could not extract costs from this document',
        color: 'var(--color-error)',
      });
    } finally {
      setExtracting(false);
    }
  }

  async function handleImportExtractedCosts() {
    if (extractedCosts.length === 0) return;
    setExtracting(true);
    try {
      let imported = 0;
      for (const cost of extractedCosts) {
        await createProjectCost({
          projectId: projRid,
          ...cost,
        });
        imported++;
      }
      await fetchAll();
      notifications.show({
        title: 'Costs imported',
        message: `${imported} line item${imported !== 1 ? 's' : ''} added from ${extractSource}`,
        color: 'var(--color-success)',
      });
      setExtractModalOpen(false);
      setExtractedCosts([]);
    } catch (err) {
      notifications.show({
        title: 'Import failed',
        message: err instanceof Error ? err.message : 'Failed to import costs',
        color: 'var(--color-error)',
      });
    } finally {
      setExtracting(false);
    }
  }

  // ── AI Assist handler ──────────────────────────────────────

  async function handleAiAssist() {
    if (!project) return;
    setAiAssistOpen(true);
    setAiAssistLoading(true);
    setAiAssistResult('');
    try {
      // Build project context for Claude
      const checklistSummary = checklistItems.map(item => {
        const status = checklistStatus.get(item.key);
        return `- [${status?.done ? 'x' : ' '}] ${item.label} (${item.category}${item.required ? ', REQUIRED' : ''})${status?.done && status.date ? ` — completed ${status.date}` : ''}`;
      }).join('\n');

      const costSummary = projCosts.map(c =>
        `- ${COST_TYPE_LABELS[c.costType] || c.costType}: ${c.description} — $${parseFloat(c.totalCost || '0').toLocaleString()} (CEF Part ${c.cefPart || 'N/A'})`
      ).join('\n') || 'No cost line items entered';

      const docList = projDocuments.map(d =>
        `- ${d.documentName} (${d.documentType || 'unknown type'}${d.tags ? `, tagged: ${d.tags}` : ''})`
      ).join('\n') || 'No documents uploaded';

      const timelineList = projTimeline.slice(0, 20).map(e =>
        `- ${e.eventDate}: ${e.description} (${e.eventType}, ${e.status})`
      ).join('\n') || 'No timeline events';

      const systemPrompt = `You are a FEMA Public Assistance project review specialist. Review the project data below and provide actionable guidance. Be specific and practical. Format your response in clear sections with markdown.

Focus on:
1. **Checklist Gaps** — What required items are missing? What should be done next?
2. **Cost Review** — Are costs complete? Any categories missing? Does the total seem reasonable?
3. **Documentation** — What key documents are missing or need attention?
4. **Risk Flags** — Any compliance concerns, deadline issues, or red flags?
5. **Next Steps** — Top 3-5 recommended actions in priority order.

Keep it concise and actionable. Don't repeat information the user already knows — focus on what they should DO.`;

      const userMessage = `Review this PA project and provide guidance:

**Project:** ${project.projectNumber} — ${project.projectTitle}
**Category:** ${project.category || 'Not set'} | **Size:** ${project.projectSize || 'Not set'} | **Phase:** ${project.phase} | **Status:** ${project.status}
**Estimated Cost:** $${parseFloat(project.estimatedCost || '0').toLocaleString()} | **Federal Share:** ${project.federalSharePct || '75'}%
**Work Type:** ${project.workType || 'Not set'}
**DDD:** ${project.damageDescription ? project.damageDescription.substring(0, 500) : 'Not provided'}
**SOW:** ${project.scopeOfWork ? project.scopeOfWork.substring(0, 500) : 'Not provided'}

**Checklist (${checklistDone}/${checklistItems.length} complete, ${checklistRequiredDone}/${checklistRequired} required):**
${checklistSummary}

**Cost Line Items (${projCosts.length} items, total: $${projCosts.reduce((s, c) => s + (parseFloat(c.totalCost) || 0), 0).toLocaleString()}):**
${costSummary}

**Documents (${projDocuments.length}):**
${docList}

**Recent Timeline (${projTimeline.length} events):**
${timelineList}

**Insurance:** ${projInsurance.length} policy record(s)
**Compliance:** ${projCompliance.length} record(s)`;

      const response = await callClaude(
        [{ role: 'user', content: userMessage }],
        { system: systemPrompt, maxTokens: 2048 }
      );
      setAiAssistResult(response);
    } catch (err) {
      setAiAssistResult(`**Error:** ${err instanceof Error ? err.message : 'AI review failed. Please try again.'}`);
    } finally {
      setAiAssistLoading(false);
    }
  }

  // ── AI Scan Receipt handler ────────────────────────────────

  async function handleScanFile(file: File) {
    setScanning(true);
    setScanSource(file.name);
    try {
      // Read file as base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const isImg = file.type.startsWith('image/');
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const mediaType = file.type || (isImg ? 'image/jpeg' : 'application/pdf');

      const systemPrompt = `You are a FEMA Public Assistance cost extraction specialist. You are given the ACTUAL image or document of a receipt, invoice, contract, or estimate. Read every line item visible in the document and extract them exactly as shown.

CRITICAL: Extract the REAL numbers and descriptions from the document. Do NOT make up or estimate values. Only include items you can actually see in the document.

For each line item, return a JSON array with objects containing:
- description: string (exactly as shown on the document)
- quantity: string (the quantity shown, default "1")
- unit: string (unit of measure: hours, EA, LF, CY, LS, SF, etc.)
- unitCost: string (unit price as shown, as a number string without $ or commas)
- totalCost: string (line total as shown, as a number string without $ or commas)
- costType: string (one of: labor, equipment, materials, contracts, rented_equip, donated, other)
- cefPart: string (A=Force Account Labor, B=Force Account Equipment, C=Materials, D=Rented Equipment, E=Contract Work, F=Direct Admin)

STEP 1 — DETERMINE DOCUMENT TYPE:
First, identify what kind of document this is:
- CONTRACTOR INVOICE: From a licensed contractor, construction company, or professional services firm billing for WORK PERFORMED. Identified by: contractor license #, "invoice" from a company that did the work, service descriptions, work period dates.
- STORE RECEIPT / MATERIAL PURCHASE: Purchase of physical goods from a retail store, supplier, or distributor. Identified by: store name (Home Depot, Lowe's, Grainger, Amazon, etc.), SKU numbers, "sold to" rather than "bill to", no labor descriptions.
- EQUIPMENT RENTAL: From an equipment rental company (United Rentals, Sunbelt, HERC, etc.). Identified by: rental period, daily/weekly rates, equipment serial numbers.
- PAYROLL / TIMESHEET: Employee wages, timesheets, payroll summaries.
- ESTIMATE / QUOTE: Not yet incurred — proposed costs.

STEP 2 — CATEGORIZE EACH LINE ITEM BASED ON DOCUMENT TYPE:

If this is a CONTRACTOR INVOICE (work done by an outside contractor under contract):
- Categorize each line item by WHAT it actually is:
  - Line items for physical materials the contractor installed (roof panels, windows, drywall, lumber, pipe, concrete, etc.) → costType "materials", cefPart "C"
  - Line items for the contractor's labor/services (installation, remediation, inspection, supervision, management, cleanup, demolition) → costType "contracts", cefPart "E"
  - Line items for equipment the contractor rented → costType "rented_equip", cefPart "D"
  This breakdown matters for FEMA's Cost Estimating Format which separates materials from labor even within contracted work.

If this is a STORE RECEIPT / MATERIAL PURCHASE:
- ALL items → costType "materials", cefPart "C"

If this is an EQUIPMENT RENTAL receipt:
- ALL items → costType "rented_equip", cefPart "D"

If this is PAYROLL / TIMESHEETS:
- ALL items → costType "labor", cefPart "A"

Return ONLY a JSON array. No markdown fences, no explanation, no commentary.`;

      // Build message content blocks for Anthropic vision/document API
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let content: any[];
      if (isImg) {
        content = [
          { type: 'image', source: { type: 'base64', media_type: mediaType, data: base64 } },
          { type: 'text', text: 'Read this receipt/invoice image. Extract every line item with the exact amounts shown. Return ONLY a JSON array.' },
        ];
      } else if (isPdf) {
        content = [
          { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } },
          { type: 'text', text: 'Read this invoice/receipt PDF. Extract every line item with the exact amounts shown. Return ONLY a JSON array.' },
        ];
      } else {
        // Fallback for other file types — send as text description
        content = [
          { type: 'text', text: `Extract cost line items from this document: "${file.name}" (${file.type}). I cannot send the file directly, so please provide a template JSON array with reasonable line items for a document of this type. Return ONLY a JSON array.` },
        ];
      }

      const response = await callClaude(
        [{ role: 'user', content }],
        { system: systemPrompt, maxTokens: 4096 },
      );

      // Parse JSON response — extract JSON array even if surrounded by text
      let cleaned = response.trim();
      // Strip markdown fences
      if (cleaned.includes('```')) {
        const fenceMatch = cleaned.match(/```(?:json)?\s*\n?([\s\S]*?)\n?\s*```/);
        if (fenceMatch) cleaned = fenceMatch[1];
      }
      // Find the JSON array in the response
      const arrayMatch = cleaned.match(/\[[\s\S]*\]/);
      if (!arrayMatch) {
        throw new Error('AI did not return a valid cost list. Try again or use a clearer image.');
      }

      const items = JSON.parse(arrayMatch[0]) as Array<{
        description: string; quantity: string; unit: string;
        unitCost: string; totalCost: string; costType: string; cefPart: string;
      }>;

      const costForms: CostFormState[] = items.map(item => ({
        costType: item.costType || 'other',
        description: item.description || '',
        quantity: String(item.quantity || '1'),
        unit: item.unit || 'EA',
        unitCost: String(item.unitCost || '0'),
        totalCost: String(item.totalCost || '0'),
        cefPart: item.cefPart || '',
        vendorEmployee: '',
        sourceReference: file.name,
        estimatedOrActual: 'actual',
        eligible: 'true',
        notes: `Scanned by AI from ${file.name}`,
      }));

      if (costForms.length === 0) {
        notifications.show({
          title: 'No costs found',
          message: `AI could not extract any cost line items from "${file.name}"`,
          color: '#C68700',
        });
      } else {
        setScannedCosts(costForms);
        setScanModalOpen(true);
      }
    } catch (err) {
      console.error('AI scan failed:', err);
      notifications.show({
        title: 'Scan failed',
        message: err instanceof Error ? err.message : 'Could not read this document',
        color: 'var(--color-error)',
      });
    } finally {
      setScanning(false);
    }
  }

  async function handleImportScannedCosts() {
    if (scannedCosts.length === 0) return;
    setScanning(true);
    try {
      let imported = 0;
      for (const cost of scannedCosts) {
        await createProjectCost({ projectId: projRid, ...cost });
        imported++;
      }
      await fetchAll();
      notifications.show({
        title: 'Costs imported',
        message: `${imported} line item${imported !== 1 ? 's' : ''} added from ${scanSource}`,
        color: 'var(--color-success)',
      });
      setScanModalOpen(false);
      setScannedCosts([]);
    } catch (err) {
      notifications.show({
        title: 'Import failed',
        message: err instanceof Error ? err.message : 'Failed to import costs',
        color: 'var(--color-error)',
      });
    } finally {
      setScanning(false);
    }
  }

  // ── Cost modal handlers ────────────────────────────────────

  const CEF_MAP: Record<string, string> = { labor: 'A', equipment: 'B', materials: 'C', rented_equip: 'D', contracts: 'E', other: 'F', donated: '' };

  function openAddCost() {
    setEditingCost(null);
    const type = costFilter || '';
    setCostForm({ ...emptyCostForm(), costType: type, cefPart: CEF_MAP[type] ?? '' });
    setCostModalOpen(true);
  }

  function applyFemaRate(r: FemaEquipRate) {
    const sizeLabel = [r.specification, r.capacityOrSize].filter(Boolean).join(', ');
    const desc = r.equipment + (r.manufacturer ? ` (${r.manufacturer})` : '') + (sizeLabel ? ` — ${sizeLabel}` : '');
    setCostForm(prev => ({
      ...prev,
      costType: 'equipment',
      cefPart: 'B',
      description: desc,
      unit: r.unit,
      unitCost: String(r.rate),
      notes: prev.notes ? prev.notes : `FEMA Cost Code: ${r.costCode}`,
    }));
    setFemaPickerOpen(false);
  }

  function openEditCost(c: PAProjectCost) {
    setEditingCost(c);
    setCostForm({
      costType: c.costType, description: c.description,
      quantity: c.quantity, unit: c.unit,
      unitCost: c.unitCost, totalCost: c.totalCost,
      cefPart: c.cefPart, vendorEmployee: c.linkedRecordId || '',
      sourceReference: c.sourceReference,
      estimatedOrActual: c.estimatedOrActual || 'estimated',
      eligible: c.eligible || 'true', notes: c.notes,
    });
    setCostModalOpen(true);
  }

  async function handleSaveCost() {
    if (!costForm.costType) {
      notifications.show({ message: 'Cost type is required', color: '#C68700' });
      return;
    }
    setCostSaving(true);
    try {
      const data: Partial<PAProjectCost> = {
        projectId: projRid,
        costType: costForm.costType,
        description: costForm.description,
        quantity: costForm.quantity,
        unit: costForm.unit,
        unitCost: costForm.unitCost,
        totalCost: costForm.totalCost,
        cefPart: costForm.cefPart,
        linkedRecordId: costForm.vendorEmployee,
        sourceReference: costForm.sourceReference,
        estimatedOrActual: costForm.estimatedOrActual,
        eligible: costForm.eligible,
        notes: costForm.notes,
      };
      if (editingCost) {
        await updateProjectCost(editingCost.Rid, data);
        notifications.show({ message: 'Cost line updated', color: 'var(--color-success)' });
      } else {
        await createProjectCost(data);
        notifications.show({ message: 'Cost line added', color: 'var(--color-success)' });
      }
      setCostModalOpen(false);

      // Auto-update project estimatedCost from cost line items
      // Read fresh from store after fetchAll (closure has stale data)
      try {
        await fetchAll();
        const freshCosts = usePAGrantsStore.getState().projectCosts.filter(c => c.projectId === projRid);
        const total = freshCosts.reduce((s, c) => s + (parseFloat(c.totalCost) || 0), 0);
        const actTotal = freshCosts.filter(c => c.estimatedOrActual === 'actual').reduce((s, c) => s + (parseFloat(c.totalCost) || 0), 0);
        if (project && total > 0) {
          await updateProject(project.Rid, {
            estimatedCost: String(total),
            ...(actTotal > 0 ? { actualCost: String(actTotal) } : {}),
          });
        }
      } catch { /* best effort */ }
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally {
      setCostSaving(false);
    }
  }

  async function handleDeleteCost(c: PAProjectCost) {
    if (!confirm(`Delete cost line "${c.description || c.costType}"?`)) return;
    try {
      await deleteProjectCost(c.Rid);
      notifications.show({ message: 'Cost line deleted', color: '#939598' });
      // Recalculate project estimated cost from fresh store data
      try {
        await fetchAll();
        const remaining = usePAGrantsStore.getState().projectCosts.filter(cc => cc.projectId === projRid);
        const total = remaining.reduce((s, cc) => s + (parseFloat(cc.totalCost) || 0), 0);
        if (project) await updateProject(project.Rid, { estimatedCost: String(total) });
      } catch { /* best effort */ }
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    }
  }

  // ── Timesheet import handler ────────────────────────────────

  async function handleTimesheetImport() {
    if (timesheetSelected.size === 0 || !project) return;
    setTimesheetImporting(true);
    try {
      const selected = laborRecords.filter(r => timesheetSelected.has(r.Rid));
      let imported = 0;
      for (const lr of selected) {
        // Link labor record to this project
        await updateRecord(TABLES.paForceAccountLabor, lr.Rid,
          [[FIELDS.paForceAccountLabor.projectId, projRid]], undefined);
        // Use approved hours/cost if available, fall back to logged
        const approvedReg = lr.approvedRegHours ? parseFloat(lr.approvedRegHours) : num(lr.hoursRegular);
        const approvedOT = lr.approvedOTHours ? parseFloat(lr.approvedOTHours) : num(lr.hoursOvertime);
        const totalHrs = approvedReg + approvedOT;
        const rate = num(lr.rateRegular);
        const costAmount = lr.approvedCost ? lr.approvedCost : (lr.totalCost || String(num(lr.laborCost) + num(lr.fringeCost)));
        // Create a matching cost line item
        await createProjectCost({
          projectId: projRid,
          costType: 'labor',
          description: `Force Account Labor: ${lr.employeeName}${lr.activityDescription ? ' — ' + lr.activityDescription : ''}`,
          quantity: String(totalHrs),
          unit: 'HR',
          unitCost: String(rate),
          totalCost: costAmount,
          estimatedOrActual: 'actual',
          eligible: 'true',
          cefPart: 'A',
          linkedRecordId: String(lr.Rid),
        });
        imported++;
      }
      await fetchLabor();
      await fetchProjectCosts();
      // Recalculate project estimated cost
      try {
        const allCosts = usePAGrantsStore.getState().projectCosts.filter(cc => cc.projectId === projRid);
        const total = allCosts.reduce((s, cc) => s + (parseFloat(cc.totalCost) || 0), 0);
        await updateProject(project.Rid, { estimatedCost: String(total) });
      } catch { /* best effort */ }
      notifications.show({
        title: 'Timesheets Imported',
        message: `Imported ${imported} time ${imported === 1 ? 'entry' : 'entries'} totaling ${fmt(selected.reduce((s, r) => s + num(r.totalCost), 0))}`,
        color: 'var(--color-success)',
      });
      setTimesheetImportOpen(false);
      setTimesheetSelected(new Set());
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Import failed', color: 'var(--color-error)' });
    } finally {
      setTimesheetImporting(false);
    }
  }

  // ── Insurance modal handlers ───────────────────────────────

  function openAddInsurance() {
    setEditingIns(null);
    setInsForm(emptyInsForm());
    setInsModalOpen(true);
  }

  function openEditInsurance(ins: { Rid: number; policyNumber?: string; carrier?: string; policyType?: string; coverageAmount?: string; deductible?: string; policyEffectiveDate?: string; policyExpirationDate?: string; claimFiled?: string; claimNumber?: string; claimDate?: string; settlementAmount?: string; settlementDate?: string; denialReceived?: string; denialReason?: string; obtainMaintainRequired?: string; obtainMaintainCommitted?: string; femaDeductionAmount?: string }) {
    setEditingIns(ins);
    setInsForm({
      policyNumber: ins.policyNumber || '', carrier: ins.carrier || '',
      policyType: ins.policyType || '', coverageAmount: ins.coverageAmount || '',
      deductible: ins.deductible || '',
      policyEffectiveDate: ins.policyEffectiveDate || '',
      policyExpirationDate: ins.policyExpirationDate || '',
      claimFiled: ins.claimFiled || 'no', claimNumber: ins.claimNumber || '',
      claimDate: ins.claimDate || '',
      settlementAmount: ins.settlementAmount || '',
      settlementDate: ins.settlementDate || '',
      denialReceived: ins.denialReceived || 'no', denialReason: ins.denialReason || '',
      obtainMaintainRequired: ins.obtainMaintainRequired || 'no',
      obtainMaintainCommitted: ins.obtainMaintainCommitted || 'no',
      femaDeductionAmount: ins.femaDeductionAmount || '',
    });
    setInsModalOpen(true);
  }

  async function handleSaveInsurance() {
    setInsSaving(true);
    try {
      const token = useAuthStore.getState().token ?? undefined;
      const FI = FIELDS.paInsurance;
      const fields: Array<[string, string]> = [];
      fields.push([FI.projectId, projRid]);
      if (org) fields.push([FI.organizationId, String(org.Rid)]);
      if (insForm.policyNumber) fields.push([FI.policyNumber, insForm.policyNumber]);
      if (insForm.carrier) fields.push([FI.carrier, insForm.carrier]);
      if (insForm.policyType) fields.push([FI.policyType, insForm.policyType]);
      if (insForm.coverageAmount) fields.push([FI.coverageAmount, insForm.coverageAmount]);
      if (insForm.deductible) fields.push([FI.deductible, insForm.deductible]);
      if (insForm.policyEffectiveDate) fields.push([FI.policyEffectiveDate, insForm.policyEffectiveDate]);
      if (insForm.policyExpirationDate) fields.push([FI.policyExpirationDate, insForm.policyExpirationDate]);
      if (insForm.claimFiled) fields.push([FI.claimFiled, insForm.claimFiled]);
      if (insForm.claimNumber) fields.push([FI.claimNumber, insForm.claimNumber]);
      if (insForm.claimDate) fields.push([FI.claimDate, insForm.claimDate]);
      // Always push settlement and deduction amounts (even if empty/zero) to ensure updates clear old values
      fields.push([FI.settlementAmount, insForm.settlementAmount || '0']);
      if (insForm.settlementDate) fields.push([FI.settlementDate, insForm.settlementDate]);
      if (insForm.denialReceived) fields.push([FI.denialReceived, insForm.denialReceived]);
      if (insForm.denialReason) fields.push([FI.denialReason, insForm.denialReason]);
      if (insForm.obtainMaintainRequired) fields.push([FI.obtainMaintainRequired, insForm.obtainMaintainRequired]);
      if (insForm.obtainMaintainCommitted) fields.push([FI.obtainMaintainCommitted, insForm.obtainMaintainCommitted]);
      fields.push([FI.femaDeductionAmount, insForm.femaDeductionAmount || '0']);

      if (editingIns) {
        await updateRecord(TABLES.paInsurance, editingIns.Rid, fields, token);
        notifications.show({ message: 'Insurance record updated', color: 'var(--color-success)' });
      } else {
        await createRecord(TABLES.paInsurance, fields, token);
        notifications.show({ message: 'Insurance record created', color: 'var(--color-success)' });
      }
      setInsModalOpen(false);
      await fetchAll();

      // Auto-update project insuranceDeduction from sum of all settlement amounts
      // Re-read from store AFTER fetchAll to get fresh data (closure holds stale value)
      try {
        const freshInsurance = usePAGrantsStore.getState().insurance;
        const allIns = freshInsurance.filter(i => i.projectId === projRid);
        const totalDeduction = allIns.reduce((sum, i) => {
          return sum + (parseFloat((i as any).settlementAmount || '0') || 0);
        }, 0);
        if (project) {
          await updateProject(project.Rid, { insuranceDeduction: String(totalDeduction) });
          // Re-fetch so the UI updates the deduction display immediately
          await fetchAll();
        }
      } catch { /* best effort */ }
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally {
      setInsSaving(false);
    }
  }

  async function handleDeleteInsurance(rid: number) {
    if (!confirm('Delete this insurance record?')) return;
    try {
      const token = useAuthStore.getState().token ?? undefined;
      await deleteRecord(TABLES.paInsurance, rid, token);
      notifications.show({ message: 'Insurance record deleted', color: '#939598' });
      await fetchAll();
      // Recalculate insurance deduction after delete
      try {
        const freshInsurance = usePAGrantsStore.getState().insurance;
        const allIns = freshInsurance.filter(i => i.projectId === projRid);
        const totalDeduction = allIns.reduce((sum, i) => {
          return sum + (parseFloat((i as any).settlementAmount || '0') || 0);
        }, 0);
        if (project) {
          await updateProject(project.Rid, { insuranceDeduction: String(totalDeduction) });
          await fetchAll();
        }
      } catch { /* best effort */ }
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    }
  }

  // ── Compliance modal handlers ──────────────────────────────

  function openEditCompliance() {
    const comp = projCompliance[0];
    if (comp) {
      setCompForm({
        nepaRequired: comp.nepaRequired || '', nepaLevel: comp.nepaLevel || '',
        nepaStatus: comp.nepaStatus || '', nepaCompletionDate: comp.nepaCompletionDate || '',
        section106Required: comp.section106Required || '', section106Status: comp.section106Status || '',
        historicProperty: comp.historicProperty || '', shpoConsultation: comp.shpoConsultation || '',
        tribalConsultation: comp.tribalConsultation || '',
        inFloodplain: comp.inFloodplain || '', floodZone: comp.floodZone || '',
        floodInsuranceRequired: comp.floodInsuranceRequired || '', eo11988Compliance: comp.eo11988Compliance || '',
        coastalBarrierArea: comp.coastalBarrierArea || '', coastalZone: comp.coastalZone || '',
        esaConsultationRequired: comp.esaConsultationRequired || '', hazmatPresent: comp.hazmatPresent || '',
      });
    } else {
      setCompForm(emptyCompForm());
    }
    setCompModalOpen(true);
  }

  async function handleSaveCompliance() {
    setCompSaving(true);
    try {
      const token = useAuthStore.getState().token ?? undefined;
      const FC = FIELDS.paCompliance;
      const fields: Array<[string, string]> = [];
      fields.push([FC.projectId, projRid]);
      if (org) fields.push([FC.organizationId, String(org.Rid)]);
      if (compForm.nepaRequired) fields.push([FC.nepaRequired, compForm.nepaRequired]);
      if (compForm.nepaLevel) fields.push([FC.nepaLevel, compForm.nepaLevel]);
      if (compForm.nepaStatus) fields.push([FC.nepaStatus, compForm.nepaStatus]);
      if (compForm.nepaCompletionDate) fields.push([FC.nepaCompletionDate, compForm.nepaCompletionDate]);
      if (compForm.section106Required) fields.push([FC.section106Required, compForm.section106Required]);
      if (compForm.section106Status) fields.push([FC.section106Status, compForm.section106Status]);
      if (compForm.historicProperty) fields.push([FC.historicProperty, compForm.historicProperty]);
      if (compForm.shpoConsultation) fields.push([FC.shpoConsultation, compForm.shpoConsultation]);
      if (compForm.tribalConsultation) fields.push([FC.tribalConsultation, compForm.tribalConsultation]);
      if (compForm.inFloodplain) fields.push([FC.inFloodplain, compForm.inFloodplain]);
      if (compForm.floodZone) fields.push([FC.floodZone, compForm.floodZone]);
      if (compForm.floodInsuranceRequired) fields.push([FC.floodInsuranceRequired, compForm.floodInsuranceRequired]);
      if (compForm.eo11988Compliance) fields.push([FC.eo11988Compliance, compForm.eo11988Compliance]);
      if (compForm.coastalBarrierArea) fields.push([FC.coastalBarrierArea, compForm.coastalBarrierArea]);
      if (compForm.coastalZone) fields.push([FC.coastalZone, compForm.coastalZone]);
      if (compForm.esaConsultationRequired) fields.push([FC.esaConsultationRequired, compForm.esaConsultationRequired]);
      if (compForm.hazmatPresent) fields.push([FC.hazmatPresent, compForm.hazmatPresent]);

      const existing = projCompliance[0];
      if (existing) {
        await updateRecord(TABLES.paCompliance, existing.Rid, fields, token);
        notifications.show({ message: 'Compliance updated', color: 'var(--color-success)' });
      } else {
        await createRecord(TABLES.paCompliance, fields, token);
        notifications.show({ message: 'Compliance record created', color: 'var(--color-success)' });
      }
      setCompModalOpen(false);
      await fetchAll();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally {
      setCompSaving(false);
    }
  }

  // ── SCQ save handler ────────────────────────────────────────

  async function handleSaveScq() {
    setScqSaving(true);
    try {
      const token = useAuthStore.getState().token ?? undefined;
      const FC = FIELDS.paCompliance;
      const fields: Array<[string, string]> = [];
      fields.push([FC.projectId, projRid]);
      if (org) fields.push([FC.organizationId, String(org.Rid)]);

      // Map SCQ answers back to compliance fields
      for (const q of SCQ_QUESTIONS) {
        if (q.compField) {
          const answer = scqAnswers[q.key]?.answer || '';
          fields.push([FC[q.compField], answer]);
        }
      }

      // Merge SCQ comments + existing DOB data into complianceNotes (preserve DOB when saving SCQ)
      const existingComp = projCompliance[0];
      let existingNotes: Record<string, unknown> = {};
      try { if (existingComp?.complianceNotes) existingNotes = JSON.parse(existingComp.complianceNotes); } catch { /* ignore */ }
      const scqComments: Record<string, string> = {};
      for (const q of SCQ_QUESTIONS) {
        if (scqAnswers[q.key]?.comment) scqComments[q.key] = scqAnswers[q.key].comment;
      }
      const mergedNotes = { ...existingNotes, ...scqComments };
      fields.push([FC.complianceNotes, JSON.stringify(mergedNotes)]);

      const existing = projCompliance[0];
      if (existing) {
        await updateRecord(TABLES.paCompliance, existing.Rid, fields, token);
      } else {
        await createRecord(TABLES.paCompliance, fields, token);
      }
      notifications.show({ message: 'Special Considerations saved', color: 'var(--color-success)' });
      await fetchAll();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally {
      setScqSaving(false);
    }
  }

  // ── Timeline event modal handlers ──────────────────────────

  function openAddEvent() {
    setEventForm(emptyEventForm());
    setEventModalOpen(true);
  }

  async function handleSaveEvent() {
    if (!eventForm.description?.trim()) {
      notifications.show({ message: 'Event description is required', color: '#C68700' });
      return;
    }
    setEventSaving(true);
    try {
      const token = useAuthStore.getState().token ?? undefined;
      const FT = FIELDS.paTimelineEvents;
      const fields: Array<[string, string]> = [];
      fields.push([FT.projectId, projRid]);
      if (org) fields.push([FT.organizationId, String(org.Rid)]);
      if (eventForm.eventType) fields.push([FT.eventType, eventForm.eventType]);
      if (eventForm.eventDate) fields.push([FT.eventDate, eventForm.eventDate]);
      if (eventForm.dueDate) fields.push([FT.dueDate, eventForm.dueDate]);
      if (eventForm.description) fields.push([FT.description, eventForm.description]);
      if (eventForm.status) fields.push([FT.status, eventForm.status]);

      await createRecord(TABLES.paTimelineEvents, fields, token);
      notifications.show({ message: 'Event added', color: 'var(--color-success)' });
      setEventModalOpen(false);
      await fetchAll();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally {
      setEventSaving(false);
    }
  }

  // ── Notes handler ──────────────────────────────────────────

  async function handleAddNote() {
    if (!noteText.trim()) return;
    setNoteSaving(true);
    try {
      const token = useAuthStore.getState().token ?? undefined;
      const FN = FIELDS.paNotes;
      const userName = useAuthStore.getState().user?.fullName || 'Unknown';
      const fields: Array<[string, string]> = [];
      fields.push([FN.parentType, 'project']);
      fields.push([FN.parentId, projRid]);
      fields.push([FN.content, noteText.trim()]);
      fields.push([FN.creatorId, userName]);
      fields.push([FN.createdDate, new Date().toISOString().split('T')[0]]);
      if (org) fields.push([FN.organizationId, String(org.Rid)]);

      await createRecord(TABLES.paNotes, fields, token);
      notifications.show({ message: 'Note added', color: 'var(--color-success)' });
      setNoteText('');
      await fetchAll();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Failed to add note', color: 'var(--color-error)' });
    } finally {
      setNoteSaving(false);
    }
  }

  // ── Task handlers ──────────────────────────────────────────

  function openAddTask() {
    setTaskForm({ description: '', dueDate: '', status: 'pending' });
    setTaskModalOpen(true);
  }

  async function handleSaveTask() {
    if (!taskForm.description.trim()) {
      notifications.show({ message: 'Description is required', color: '#C68700' });
      return;
    }
    setTaskSaving(true);
    try {
      await createTimelineEvent({
        projectId: projRid,
        organizationId: org ? String(org.Rid) : '',
        eventType: 'task',
        description: taskForm.description,
        dueDate: taskForm.dueDate,
        eventDate: new Date().toISOString().split('T')[0],
        status: taskForm.status,
      });
      notifications.show({ message: 'Task created', color: 'var(--color-success)' });
      setTaskModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally {
      setTaskSaving(false);
    }
  }

  async function handleToggleTask(t: { Rid: number; status: string }) {
    const isComplete = t.status === 'completed';
    try {
      await updateTimelineEvent(t.Rid, {
        status: isComplete ? 'pending' : 'completed',
        completedDate: isComplete ? '' : new Date().toISOString().split('T')[0],
      });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Update failed', color: 'var(--color-error)' });
    }
  }

  async function handleDeleteTask(rid: number) {
    if (!confirm('Delete this task?')) return;
    try {
      await deleteTimelineEvent(rid);
      notifications.show({ message: 'Task deleted', color: '#939598' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    }
  }

  // ── Edit Project modal handlers ────────────────────────────

  function openEditProject() {
    if (!project) return;
    setEditProjForm({
      projectNumber: project.projectNumber,
      projectTitle: project.projectTitle,
      category: project.category,
      projectSize: project.projectSize,
      workType: project.workType,
      projectAddress: project.projectAddress,
      damageDescription: project.damageDescription,
      scopeOfWork: project.scopeOfWork,
      costNarrative: project.costNarrative,
      estimatedCost: project.estimatedCost,
      actualCost: project.actualCost,
      federalSharePct: project.federalSharePct || '75',
      obligatedAmount: project.obligatedAmount || '',
      drawdownAmount: project.drawdownAmount || '',
      workStartDate: project.workStartDate,
      deadline: project.deadline,
      completionPctEstimate: project.completionPctEstimate,
      assignedSpecialist: project.assignedSpecialist,
      status: project.status,
      phase: project.phase,
      notes: project.notes,
    });
    setEditProjTab('general');
    setEditProjModalOpen(true);
  }

  async function handleSaveProject() {
    if (!project) return;
    if (!editProjForm.projectTitle?.trim()) {
      notifications.show({ message: 'Project title is required', color: '#C68700' });
      return;
    }
    setEditProjSaving(true);
    try {
      await updateProject(project.Rid, editProjForm);
      notifications.show({ message: 'Project updated', color: 'var(--color-success)' });
      setEditProjModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally {
      setEditProjSaving(false);
    }
  }

  // ── Computed values (must be before early returns — hooks rules) ──

  // Sum all cost sub-records for this project (projectCosts + labor + equipment + materials)
  const costRecordTotal = useMemo(() => {
    if (!project) return 0;
    const pcSum = projCosts.reduce((s, c) => s + (parseFloat(c.totalCost) || 0), 0);
    const laborSum = projLabor.reduce((s, r) => s + (parseFloat(r.totalCost) || 0), 0);
    const equipSum = equipRecords.filter(e => String(e.projectId) === projRid).reduce((s, e) => s + (parseFloat(e.totalCost) || 0), 0);
    const matSum = materials.filter(m => String(m.projectId) === projRid).reduce((s, m) => s + (parseFloat(m.totalCost) || 0), 0);
    return pcSum + laborSum + equipSum + matSum;
  }, [project, projCosts, projLabor, equipRecords, materials, projRid]);

  // ── Loading / not found ────────────────────────────────────

  if (loadingProjects && projects.length === 0) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!project) {
    return (
      <Center h={400}>
        <Stack align="center" gap="sm">
          <Text size="lg" c="dimmed">Project not found</Text>
          <Button variant="light" leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(orgId ? `/pagrants/pa/applicants/${orgId}` : '/pagrants/pa/projects')}>
            Back
          </Button>
        </Stack>
      </Center>
    );
  }

  // Org ownership guard: applicant roles can only view their own org's projects
  if (isApplicantRole && myOrgIds.length > 0 && !myOrgIds.includes(String(project.organizationId))) {
    return (
      <Center h={400}>
        <Stack align="center" gap="sm">
          <Text size="lg" c="dimmed">Access Denied</Text>
          <Text size="sm" c="dimmed">This project belongs to another organization.</Text>
          <Button variant="light" leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/pagrants/pa/projects')}>
            Back to My Projects
          </Button>
        </Stack>
      </Center>
    );
  }

  // Use manual estimatedCost if set, otherwise fall back to computed sum from cost records
  const manualEstimate = num(project.estimatedCost);
  const estimated = manualEstimate || costRecordTotal;
  const fedPct = num(project.federalSharePct) || 75;
  const federalShare = num(project.federalShareAmount) || estimated * (fedPct / 100);
  const applicantShare = num(project.applicantShareAmount) || estimated - federalShare;
  const insuranceDed = num(project.insuranceDeduction);
  const netEligible = num(project.netEligibleCost) || estimated - insuranceDed;
  const obligated = num(project.obligatedAmount);

  // ── DOB computed values ────────────────────────────────────
  const dobTotal = dobSources.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  const dobNetEligible = netEligible - dobTotal;
  const dobLinkedKeys = new Set(dobSources.flatMap(s => s.linkedCostKeys || []));

  // All cost line items for this project — used in DOB cost-linking picker
  const dobCostItems = [
    ...projectCosts.filter(c => String(c.projectId) === String(projRid)).map(c => ({
      key: `cost-${c.Rid}`,
      label: `${COST_TYPE_LABELS[c.costType] || c.costType}${c.description ? `: ${c.description}` : ''}`,
      amount: num(c.totalCost),
      badge: COST_TYPE_LABELS[c.costType] || c.costType,
      badgeColor: COST_TYPE_COLORS[c.costType] || '#939598',
    })),
    ...laborRecords.filter(l => String(l.projectId) === String(projRid)).map(l => ({
      key: `labor-${l.Rid}`,
      label: `Labor: ${l.employeeName || ''}${l.workDate ? ` (${fmtDate(l.workDate)})` : ''}`,
      amount: num(l.approvedCost || l.totalCost),
      badge: 'Labor', badgeColor: COST_TYPE_COLORS.labor,
    })),
    ...equipRecords.filter(e => String(e.projectId) === String(projRid)).map(e => ({
      key: `equip-${e.Rid}`,
      label: `Equipment: ${e.equipmentType || ''}${e.workDate ? ` (${fmtDate(e.workDate)})` : ''}`,
      amount: num(e.totalCost),
      badge: 'Equipment', badgeColor: COST_TYPE_COLORS.equipment,
    })),
    ...materials.filter(m => String(m.projectId) === String(projRid)).map(m => ({
      key: `mat-${m.Rid}`,
      label: `Materials: ${m.materialDescription || ''}`,
      amount: num(m.totalCost),
      badge: 'Materials', badgeColor: COST_TYPE_COLORS.materials,
    })),
  ];

  // ── Closeout readiness ─────────────────────────────────────
  const closeoutReadiness = {
    checklistReady: checklistRequiredDone === checklistRequired && checklistRequired > 0,
    workCompleted: checklistStatus.get('work_completed')?.done === true,
    costsEntered: costRecordTotal > 0 || manualEstimate > 0,
    insuranceDocs: checklistStatus.get('insurance_docs')?.done === true || checklistStatus.get('insurance_review')?.done === true,
  };
  const closeoutReadyCount = Object.values(closeoutReadiness).filter(Boolean).length;
  const milestones = projTimeline.filter(e => e.eventType === 'milestone' || e.eventType === 'deadline');
  const milestoneDone = milestones.filter(e => e.status === 'completed').length;
  const completionPct = milestones.length > 0 ? Math.round((milestoneDone / milestones.length) * 100) : 0;

  const orgName = org?.orgName || `Org #${orgId}`;
  const disasterName = disaster?.disasterName || project.disasterId || 'N/A';
  const cat = (project.category || 'A') as DamageCategory;

  // ── Compliance status helper ───────────────────────────────

  function complianceIcon(value: string | undefined) {
    const v = (value || '').toLowerCase();
    if (v === 'completed' || v === 'yes' || v === 'true') {
      return <ThemeIcon size={20} radius="xl" color="var(--color-success)" variant="filled"><IconCheck size={12} /></ThemeIcon>;
    }
    if (v === 'in_progress' || v === 'in progress' || v === 'pending') {
      return <ThemeIcon size={20} radius="xl" color="#C68700" variant="filled"><IconClock size={12} /></ThemeIcon>;
    }
    if (v === 'not_required' || v === 'n/a') {
      return <ThemeIcon size={20} radius="xl" color="#939598" variant="light"><IconCheck size={12} /></ThemeIcon>;
    }
    return <ThemeIcon size={20} radius="xl" color="#939598" variant="light"><IconClock size={12} /></ThemeIcon>;
  }

  // ── File icon helper ───────────────────────────────────────

  function openDocViewer(doc: any) {
    setViewDoc(doc);
    setDocFileAvailable(null);
    setPreviewUrl(null);
    if (doc.fileId) {
      setPreviewLoading(true);
      getDocumentDownloadUrl(doc.fileId)
        .then(url => setPreviewUrl(url))
        .catch(() => setPreviewUrl(null))
        .finally(() => setPreviewLoading(false));
    }
  }

  function fileIcon(mime: string) {
    if (mime.includes('pdf')) return <IconFileTypePdf size={18} />;
    if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv'))
      return <IconFileSpreadsheet size={18} />;
    return <IconFile size={18} />;
  }

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  return (
    <Stack gap="md">
      {/* ── HEADER ─────────────────────────────────────────── */}
      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Breadcrumbs items={isApplicantRole ? [
              { label: 'Disaster Grants', to: '/pagrants' },
              { label: 'My Projects', to: '/pagrants/pa/projects' },
              { label: project.projectNumber || 'Project' },
            ] : [
              { label: 'Disaster Grants', to: '/pagrants' },
              { label: 'PA', to: '/pagrants/pa' },
              { label: orgName, to: `/pagrants/pa/applicants/${orgId}` },
              { label: project.projectNumber || 'Project' },
            ]} />

            <Group gap="sm" align="center">
              <Title order={3}>
                {project.projectNumber || 'No Number'} &mdash; {project.projectTitle || 'Untitled Project'}
              </Title>
              {!isApplicantViewer && (
                <Tooltip label="Edit Project">
                  <ActionIcon variant="subtle" color="#939598" size="sm" onClick={openEditProject}>
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Badge
                style={{ backgroundColor: DAMAGE_CATEGORY_COLORS[cat] || '#6B7280' }}
                c="white" size="lg"
              >
                Cat {cat}
              </Badge>
              <Badge variant="light" size="lg" color="var(--color-primary-700)">
                {(project.projectSize || 'small').toUpperCase()}
              </Badge>
              <Badge variant="light" size="lg" color={
                project.status === 'completed' ? 'var(--color-success)' :
                project.status === 'appealed' || project.status === 'denied' ? 'var(--color-error)' :
                project.status === 'obligated' ? '#03AC9E' : '#939598'
              }>
                {PROJECT_STATUS_LABELS[project.status as ProjectStatus] || project.status}
              </Badge>
            </Group>

            <Text size="sm" c="dimmed">
              {orgName} | {disasterName}{!isApplicantRole && ` | Assigned: ${project.assignedSpecialist || 'Unassigned'}`}
            </Text>
          </Stack>

          <Group gap="xs">
            <Button variant="light" leftSection={<IconFileDescription size={16} />}
              onClick={() => navigate('/pagrants/pa/forms', { state: { projectId: String(project?.Rid) } })}>
              Generate FEMA Form
            </Button>
            {!isApplicantViewer && (
              <Button variant="light" color="var(--data-viz-650)" leftSection={<IconRobot size={16} />}
                loading={aiAssistLoading} onClick={handleAiAssist}>
                AI Assist
              </Button>
            )}
          </Group>
        </Group>
      </Paper>

      {/* ── COST SUMMARY BAR ───────────────────────────────── */}
      {(() => {
        const drawdown = num(project.drawdownAmount);
        const remaining = obligated - drawdown;
        const shortfall = obligated > 0 && netEligible > 0 && obligated < netEligible;
        const shortfallAmt = shortfall ? netEligible - obligated : 0;
        return (
          <Stack gap="xs">
          {shortfall && !isApplicantRole && (
            <Alert color="#F86A0B" title="Obligation shortfall" icon={<IconAlertTriangle size={16} />} style={{ padding: '8px 12px' }}>
              <Group justify="space-between" align="center">
                <Text size="sm">
                  Obligated amount ({fmt(obligated)}) is <strong>{fmt(shortfallAmt)} short</strong> of net eligible costs ({fmt(netEligible)}).
                  {' '}RFR approvals will be blocked once the drawdown reaches the obligation limit.
                </Text>
                <Button size="compact-xs" variant="light" color="#F86A0B"
                  onClick={() => { openEditProject(); setTimeout(() => setEditProjTab('costs'), 50); }}>
                  Update Obligation
                </Button>
              </Group>
            </Alert>
          )}
          <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }}>
            {/* Estimated Cost — computed from records, manual override shown as note */}
            <Paper p="sm" withBorder>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                {manualEstimate ? 'Estimated Cost' : 'Total Costs'}
              </Text>
              <Text size="lg" fw={700}>{fmt(estimated)}</Text>
              {manualEstimate > 0 && costRecordTotal > 0 && Math.abs(manualEstimate - costRecordTotal) > 1 && (
                <Text size="xs" c="dimmed">Records: {fmt(costRecordTotal)}</Text>
              )}
              {!manualEstimate && costRecordTotal === 0 && (
                <Text size="xs" c="dimmed">No costs entered</Text>
              )}
            </Paper>
            {[
              { label: `Federal Share (${fedPct}%)`, value: federalShare, color: undefined },
              { label: 'Applicant Share',           value: applicantShare, color: undefined },
              { label: 'Insurance Deduction',       value: insuranceDed, color: insuranceDed > 0 ? 'var(--color-error)' : undefined },
              { label: 'Net Eligible',              value: netEligible, color: 'var(--color-success)' },
            ].map((item) => (
              <Paper key={item.label} p="sm" withBorder>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{item.label}</Text>
                <Text size="lg" fw={700} c={item.color}>{fmt(item.value)}</Text>
              </Paper>
            ))}
            {/* Obligation card — shows drawdown + remaining, opens edit on click */}
            {!isApplicantRole ? (
              <Paper p="sm" withBorder
                style={{ cursor: 'pointer', borderColor: (shortfall || obligated === 0) ? '#F86A0B' : undefined, background: shortfall ? '#fff7ed' : undefined }}
                onClick={() => { openEditProject(); setTimeout(() => setEditProjTab('costs'), 50); }}
                title="Click to edit obligation">
                <Group justify="space-between" mb={2}>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Obligated</Text>
                  <Group gap={4}>
                    {shortfall && <IconAlertTriangle size={12} color="#F86A0B" />}
                    <IconPencil size={12} color="#94a3b8" />
                  </Group>
                </Group>
                <Text size="lg" fw={700} c={shortfall ? '#C68700' : undefined}>{fmt(obligated)}</Text>
                {obligated > 0 && !shortfall && (
                  <Text size="xs" c={remaining < 0 ? 'var(--color-error)' : remaining < obligated * 0.1 ? '#C68700' : 'dimmed'}>
                    {fmt(remaining)} remaining
                  </Text>
                )}
                {shortfall && <Text size="xs" c="#F86A0B">{fmt(shortfallAmt)} below eligible</Text>}
                {obligated === 0 && <Text size="xs" c="#F86A0B">Not set — click to add</Text>}
              </Paper>
            ) : (
              <Paper p="sm" withBorder>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Obligated</Text>
                <Text size="lg" fw={700}>{fmt(obligated)}</Text>
              </Paper>
            )}
          </SimpleGrid>
          </Stack>
        );
      })()}

      {/* ── CLOSEOUT PHASE BANNER ─────────────────────────────── */}
      {project.phase === 'closeout' && (
        <Paper p="sm" style={{ background: '#f5f3ff', border: '2px solid #7c3aed', borderRadius: 8 }}>
          <Group gap="sm">
            <ThemeIcon color="#7c3aed" variant="filled" size="md"><IconCircleCheck size={14} /></ThemeIcon>
            <Stack gap={0} style={{ flex: 1 }}>
              <Text size="sm" fw={700} c="#5b21b6">Project in Closeout Phase — {closeoutReadyCount}/4 conditions met</Text>
              <Text size="xs" c="#7c3aed">
                {project.closeoutSubmittedAt
                  ? `Submitted to State on ${fmtDate(project.closeoutSubmittedAt)}. Retain all records until ${fmtDate(new Date(new Date(project.closeoutSubmittedAt).setFullYear(new Date(project.closeoutSubmittedAt).getFullYear() + 3)).toISOString())}.`
                  : 'Complete the Closeout tab to submit final reconciliation and documentation to the State.'}
              </Text>
            </Stack>
            <Button size="xs" variant="light" color="violet" onClick={() => setActiveTab('closeout')}>
              Go to Closeout
            </Button>
          </Group>
        </Paper>
      )}

      {/* ── PROJECT TAB STRIP ──────────────────────────────── */}
      <ProjectTabStrip
        activeTab={activeTab}
        onSelect={setActiveTab}
        checklistDone={checklistDone}
        checklistTotal={checklistItems.length}
        docCount={projDocuments.length}
        taskCount={projTasks.length}
        evidenceCount={linkedAssessmentIds.length}
        rfiCount={projRfis.filter(r => !['resolved','closed'].includes(r.status)).length}
        isCloseout={project.phase === 'closeout'}
      />

      {/* ── TABS ───────────────────────────────────────────── */}
      <Tabs value={activeTab} onChange={v => v && setActiveTab(v)}>
        {/* Hidden file input — lives outside all tab panels so it's always mounted */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.pptx,.ppt,.png,.jpg,.jpeg,.gif,.zip,.rar"
          onChange={onFileSelect}
        />

        {/* ════════════════════════════════════════════════════
            TAB 0: PROJECT CHECKLIST
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="checklist" pt="md">
          <Stack gap="md">
            {/* Progress summary */}
            <Paper p="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <ThemeIcon variant="light" color="var(--color-primary-700)"><IconListCheck size={18} /></ThemeIcon>
                  <Text fw={600}>Project Checklist</Text>
                </Group>
                <Group gap="sm">
                  <Badge color="var(--color-success)" variant="light">{checklistDone} / {checklistItems.length} complete</Badge>
                  <Badge color={checklistRequiredDone === checklistRequired ? 'var(--color-success)' : '#F86A0B'} variant="light">
                    {checklistRequiredDone} / {checklistRequired} required
                  </Badge>
                </Group>
              </Group>
              <Progress
                value={(checklistDone / checklistItems.length) * 100}
                size="lg" radius="xl" color="var(--color-success)"
                style={{ background: '#e2e8f0' }}
              />
            </Paper>

            {/* Grouped checklist items */}
            {(['document', 'form', 'review', 'milestone'] as const).map(cat => {
              const items = checklistItems.filter(c => c.category === cat);
              const catDone = items.filter(c => checklistStatus.get(c.key)?.done).length;
              return (
                <Paper key={cat} p="md" withBorder>
                  <Group justify="space-between" mb="sm">
                    <Group gap="xs">
                      <Badge color={CHECKLIST_CATEGORY_COLORS[cat]} variant="filled" size="sm">
                        {CHECKLIST_CATEGORY_LABELS[cat]}s
                      </Badge>
                      <Text size="xs" c="dimmed">{catDone}/{items.length}</Text>
                    </Group>
                  </Group>
                  <Stack gap={4}>
                    {items.map(item => {
                      const st = checklistStatus.get(item.key);
                      const done = st?.done ?? false;
                      const hasRevision = st?.revision ?? false;
                      return (
                        <Group key={item.key} justify="space-between" py={6}
                          style={{
                            borderBottom: '1px solid #f1f5f9',
                            opacity: done ? 0.7 : 1,
                            background: hasRevision ? 'rgba(248,106,11,0.08)' : undefined,
                            padding: hasRevision ? '8px' : undefined,
                            borderRadius: hasRevision ? 4 : undefined,
                          }}>
                          <Group gap="sm" style={{ flex: 1 }}>
                            {done ? (
                              <ThemeIcon size="sm" variant="filled" color="var(--color-success)" radius="xl">
                                <IconCheck size={12} />
                              </ThemeIcon>
                            ) : hasRevision ? (
                              <ThemeIcon size="sm" variant="filled" color="#F86A0B" radius="xl">
                                <IconArrowLeft size={12} />
                              </ThemeIcon>
                            ) : (
                              <ThemeIcon size="sm" variant="light" color="#939598" radius="xl">
                                <IconCircle size={12} />
                              </ThemeIcon>
                            )}
                            <Stack gap={0}>
                              <Group gap={6}>
                                <Text size="sm" fw={500} td={done ? 'line-through' : undefined}>
                                  {item.label}
                                </Text>
                                {item.required && !done && !hasRevision && (
                                  <Badge size="xs" variant="outline" color="var(--color-error)">Required</Badge>
                                )}
                                {hasRevision && (
                                  <Badge size="xs" variant="filled" color="#F86A0B">Revision Needed</Badge>
                                )}
                              </Group>
                              <Text size="xs" c="dimmed">{item.description}</Text>
                              {!done && !hasRevision && (
                                <Text size="xs" c={
                                  cat === 'document' ? 'var(--color-primary-700)' :
                                  cat === 'form' ? 'var(--data-viz-650)' :
                                  cat === 'review' ? 'orange' : 'var(--color-success)'
                                } mt={2}>
                                  {cat === 'document' && '↑ Upload the file using the button →'}
                                  {cat === 'form' && '↑ Upload the signed form, or mark as signed manually →'}
                                  {cat === 'review' && '↑ Log when an external reviewer (FEMA, State) completes this →'}
                                  {cat === 'milestone' && '↑ Record when this milestone is physically reached →'}
                                </Text>
                              )}
                              {hasRevision && st?.revisionNote && (
                                <Paper p="xs" mt={4} style={{ background: 'rgba(248,106,11,0.1)', borderRadius: 4 }}>
                                  <Text size="xs" fw={500} c="orange.8">Reviewer feedback:</Text>
                                  <Text size="xs">{st.revisionNote}</Text>
                                  <Text size="xs" c="dimmed" mt={2}>{st.revisionBy} — {fmtDate(st.revisionDate)}</Text>
                                </Paper>
                              )}
                              {done && st?.date && (
                                <Text size="xs" c="var(--color-success)">
                                  Completed {st.date}{st.by ? ` by ${st.by}` : ''}
                                </Text>
                              )}
                            </Stack>
                          </Group>
                          <Group gap={4}>
                            {!done && cat === 'document' && (
                              <Button size="xs" variant="light" leftSection={<IconUpload size={12} />}
                                onClick={() => {
                                  setUploadDocType(item.key);
                                  fileInputRef.current?.click();
                                }}>
                                {hasRevision ? 'Re-upload' : 'Upload'}
                              </Button>
                            )}
                            {!done && cat === 'form' && (
                              <Button size="xs" variant="light" leftSection={<IconUpload size={12} />}
                                onClick={() => {
                                  setUploadDocType(item.key);
                                  fileInputRef.current?.click();
                                }}>
                                Upload Signed
                              </Button>
                            )}
                            {!done && cat === 'review' && (
                              <Button size="xs" variant="light" color="indigo"
                                leftSection={<IconCheckbox size={12} />}
                                onClick={() => openCheckOff(item)}>
                                Log Review
                              </Button>
                            )}
                            {!done && cat === 'milestone' && (
                              <Button size="xs" variant="light" color="var(--color-success)"
                                leftSection={<IconCheckbox size={12} />}
                                onClick={() => openCheckOff(item)}>
                                Mark Complete
                              </Button>
                            )}
                            {!done && cat === 'form' && (
                              <Button size="xs" variant="light" color="var(--color-success)"
                                leftSection={<IconCheckbox size={12} />}
                                onClick={() => openCheckOff(item)}>
                                Mark Signed
                              </Button>
                            )}
                            {done && !isApplicantRole && (
                              <Button size="xs" variant="light" color="#F86A0B"
                                leftSection={<IconArrowLeft size={12} />}
                                onClick={() => { setRevisionItem(item); setRevisionNote(''); }}>
                                Request Revision
                              </Button>
                            )}
                          </Group>
                        </Group>
                      );
                    })}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 1: SCOPE & DAMAGE
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="scope" pt="md">
          <Stack gap="md">
            {/* Damage Description + Scope of Work */}
            <SimpleGrid cols={{ base: 1, md: 2 }}>
              <Paper p="md" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text fw={600}>Damage Description</Text>
                  {!editingDamage ? (
                    <ActionIcon size="sm" variant="subtle" color="var(--color-primary-700)"
                      onClick={() => { setDamageText(project.damageDescription || ''); setEditingDamage(true); }}>
                      <IconPencil size={14} />
                    </ActionIcon>
                  ) : (
                    <Group gap="xs">
                      <Button size="xs" variant="subtle" onClick={() => setEditingDamage(false)}>Cancel</Button>
                      <Button size="xs" loading={savingScope} onClick={async () => {
                        setSavingScope(true);
                        try {
                          await updateProject(project.Rid, { damageDescription: damageText, siteDescription: damageText });
                          setEditingDamage(false);
                          notifications.show({ message: 'Damage description saved', color: 'var(--color-success)' });
                        } finally { setSavingScope(false); }
                      }}>Save</Button>
                    </Group>
                  )}
                </Group>
                {editingDamage ? (
                  <RichTextEditor
                    value={damageText}
                    onChange={setDamageText}
                    placeholder="Describe the damage cause, extent, and pre-disaster condition…"
                    minHeight={280}
                  />
                ) : (
                  <div className="sow-scroll-box tiptap" style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 4, padding: 16, background: '#F8F8F8', fontSize: 14, lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: project.damageDescription?.startsWith('<') ? project.damageDescription : renderSimpleMarkdown(project.damageDescription || 'No damage description provided.') }} />
                )}
              </Paper>

              <Paper p="md" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text fw={600}>Scope of Work</Text>
                  {!editingScope ? (
                    <ActionIcon size="sm" variant="subtle" color="var(--color-primary-700)"
                      onClick={() => { setScopeText(project.scopeOfWork || ''); setEditingScope(true); }}>
                      <IconPencil size={14} />
                    </ActionIcon>
                  ) : (
                    <Group gap="xs">
                      <Button size="xs" variant="subtle" onClick={() => setEditingScope(false)}>Cancel</Button>
                      <Button size="xs" loading={savingScope} onClick={async () => {
                        setSavingScope(true);
                        try {
                          await updateProject(project.Rid, { scopeOfWork: scopeText });
                          setEditingScope(false);
                          notifications.show({ message: 'Scope of work saved', color: 'var(--color-success)' });
                        } finally { setSavingScope(false); }
                      }}>Save</Button>
                    </Group>
                  )}
                </Group>
                {editingScope ? (
                  <RichTextEditor
                    value={scopeText}
                    onChange={setScopeText}
                    placeholder="Describe the complete scope of work to be performed…"
                    minHeight={280}
                  />
                ) : (
                  <div className="sow-scroll-box tiptap" style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 4, padding: 16, background: '#F8F8F8', fontSize: 14, lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: project.scopeOfWork?.startsWith('<') ? project.scopeOfWork : renderSimpleMarkdown(project.scopeOfWork || 'No scope of work defined.') }} />
                )}
              </Paper>
            </SimpleGrid>

            {/* Associated Damage Sites */}
            <Paper p="md" withBorder>
              <Group justify="space-between" mb="sm">
                <Text fw={600}>Associated Damage Sites</Text>
                <Button size="xs" leftSection={<IconPlus size={12} />}
                  onClick={() => { setSiteForm(emptySiteForm()); setAddSiteOpen(true); }}>
                  Add Damage Site
                </Button>
              </Group>
              {projDamageItems.length === 0 ? (
                <Text size="sm" c="dimmed">No damage sites associated with this project.</Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Site Name</Table.Th>
                      <Table.Th>Damage Description</Table.Th>
                      <Table.Th>Category</Table.Th>
                      <Table.Th>Severity</Table.Th>
                      <Table.Th>Est. Cost</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {projDamageItems.map(d => (
                      <Table.Tr key={d.Rid}>
                        <Table.Td>{d.siteName || 'N/A'}</Table.Td>
                        <Table.Td style={{ maxWidth: 300 }}>
                          <Text size="sm" lineClamp={2}>{d.description || '-'}</Text>
                        </Table.Td>
                        <Table.Td>
                          {d.damageCategory ? (
                            <Badge size="sm" style={{ background: DAMAGE_CATEGORY_COLORS[d.damageCategory as keyof typeof DAMAGE_CATEGORY_COLORS] || '#939598', color: '#fff' }}>
                              {d.damageCategory} — {DAMAGE_CATEGORY_LABELS[d.damageCategory as keyof typeof DAMAGE_CATEGORY_LABELS] || d.damageCategory}
                            </Badge>
                          ) : '-'}
                        </Table.Td>
                        <Table.Td>{d.severity || '-'}</Table.Td>
                        <Table.Td>{fmt(num(d.estimatedCost))}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Paper>

            {/* Add Damage Site modal */}
            <Modal opened={addSiteOpen} onClose={() => setAddSiteOpen(false)} title="Add Damage Site" size="lg">
              <Stack gap="sm">
                <TextInput label="Site Name" required placeholder="e.g. City Hall — West Wing"
                  value={siteForm.siteName}
                  onChange={e => setSiteForm(f => ({ ...f, siteName: e.target.value }))} />
                <Textarea label="Damage Description" placeholder="Describe the damage observed…" autosize minRows={3}
                  value={siteForm.description}
                  onChange={e => setSiteForm(f => ({ ...f, description: e.target.value }))} />
                <SimpleGrid cols={2}>
                  <Select label="Damage Category" clearable placeholder="Select category"
                    data={(['A','B','C','D','E','F','G'] as const).map(c => ({ value: c, label: `${c} — ${DAMAGE_CATEGORY_LABELS[c]}` }))}
                    value={siteForm.damageCategory || null}
                    onChange={v => setSiteForm(f => ({ ...f, damageCategory: v || '' }))} />
                  <Select label="Severity" clearable placeholder="Select severity"
                    data={[
                      { value: 'minor', label: 'Minor' },
                      { value: 'major', label: 'Major' },
                      { value: 'destroyed', label: 'Destroyed' },
                      { value: 'inaccessible', label: 'Inaccessible' },
                    ]}
                    value={siteForm.severity || null}
                    onChange={v => setSiteForm(f => ({ ...f, severity: v || '' }))} />
                </SimpleGrid>
                <TextInput label="Estimated Cost ($)" placeholder="0"
                  value={siteForm.estimatedCost}
                  onChange={e => setSiteForm(f => ({ ...f, estimatedCost: e.target.value }))} />
                <TextInput label="Address" placeholder="Street address"
                  value={siteForm.address}
                  onChange={e => setSiteForm(f => ({ ...f, address: e.target.value }))} />
                <SimpleGrid cols={2}>
                  <TextInput label="City"
                    value={siteForm.city}
                    onChange={e => setSiteForm(f => ({ ...f, city: e.target.value }))} />
                  <TextInput label="State" placeholder="NY"
                    value={siteForm.state}
                    onChange={e => setSiteForm(f => ({ ...f, state: e.target.value }))} />
                </SimpleGrid>
                <Group justify="flex-end" mt="xs">
                  <Button variant="subtle" onClick={() => setAddSiteOpen(false)}>Cancel</Button>
                  <Button loading={savingSite} disabled={!siteForm.siteName.trim()}
                    onClick={async () => {
                      setSavingSite(true);
                      try {
                        await createDamageItem({
                          projectId: project.Rid,
                          disasterId: project.disasterId,
                          organizationId: project.organizationId,
                          siteName: siteForm.siteName.trim(),
                          description: siteForm.description.trim(),
                          damageCategory: siteForm.damageCategory || undefined,
                          severity: siteForm.severity || undefined,
                          estimatedCost: siteForm.estimatedCost ? Number(siteForm.estimatedCost) : 0,
                          address: siteForm.address.trim() || undefined,
                          city: siteForm.city.trim() || undefined,
                          state: siteForm.state.trim() || undefined,
                          status: 'identified',
                        });
                        notifications.show({ message: `Damage site "${siteForm.siteName}" added`, color: 'var(--color-success)' });
                        setAddSiteOpen(false);
                      } finally {
                        setSavingSite(false);
                      }
                    }}>
                    Add Site
                  </Button>
                </Group>
              </Stack>
            </Modal>

            {/* Schedule & Milestones */}
            <Paper p="md" withBorder>
              <Group justify="space-between" mb="md">
                <Stack gap={2}>
                  <Text fw={600}>Schedule &amp; Milestones</Text>
                  {milestones.length > 0 && (
                    <Text size="xs" c="dimmed">{milestoneDone} of {milestones.length} complete</Text>
                  )}
                </Stack>
                <Group gap="xs">
                  <Button size="xs" variant="subtle"
                    onClick={() => {
                      const tpls = loadTemplates();
                      setTemplateSelected(Object.fromEntries(tpls.map(t => [t.id, true])));
                      setTemplateModalOpen(true);
                    }}>
                    From Template
                  </Button>
                  <Button size="xs" leftSection={<IconPlus size={12} />}
                    onClick={() => { setMilestoneForm({ title: '', eventDate: '' }); setAddingMilestone(true); }}>
                    Add Milestone
                  </Button>
                </Group>
              </Group>

              {/* Progress bar */}
              {milestones.length > 0 && (
                <Stack gap={4} mb="md">
                  <Progress value={completionPct} size="md"
                    color={completionPct === 100 ? 'var(--color-success)' : 'var(--color-primary-700)'} />
                  <Text size="xs" ta="right" c="dimmed">{completionPct}% complete</Text>
                </Stack>
              )}

              {/* Milestone rows */}
              {milestones.length === 0 && !addingMilestone ? (
                <Text size="sm" c="dimmed">No milestones yet. Add one to start tracking progress.</Text>
              ) : (
                <Stack gap={6}>
                  {milestones.map(m => {
                    const isDone = m.status === 'completed';
                    const dateStr = (m.eventDate || '').slice(0, 10);
                    const isOverdue = dateStr && dateStr < new Date().toISOString().slice(0, 10) && !isDone;
                    const isEditing = editingMilestone === m.Rid;

                    if (isEditing) {
                      return (
                        <Group key={m.Rid} gap="sm" wrap="nowrap" align="flex-end"
                          style={{ padding: '8px', borderRadius: 6, border: '1px solid var(--color-primary-700)', background: '#f5f4ff' }}>
                          <TextInput size="xs" placeholder="Milestone name" style={{ flex: 1 }}
                            value={editMilestoneForm.title}
                            onChange={e => setEditMilestoneForm(f => ({ ...f, title: e.target.value }))}
                            onKeyDown={e => e.key === 'Escape' && setEditingMilestone(null)} />
                          <TextInput size="xs" type="date" style={{ width: 140 }}
                            value={editMilestoneForm.eventDate}
                            onChange={e => setEditMilestoneForm(f => ({ ...f, eventDate: e.target.value }))} />
                          <Button size="xs" loading={savingEditMilestone} disabled={!editMilestoneForm.title.trim()}
                            onClick={async () => {
                              setSavingEditMilestone(true);
                              try {
                                await updateTimelineEvent(m.Rid, {
                                  title: editMilestoneForm.title.trim(),
                                  eventDate: editMilestoneForm.eventDate || undefined,
                                });
                                setEditingMilestone(null);
                              } finally { setSavingEditMilestone(false); }
                            }}>Save</Button>
                          <Button size="xs" variant="subtle" onClick={() => setEditingMilestone(null)}>Cancel</Button>
                        </Group>
                      );
                    }

                    return (
                      <Group key={m.Rid} gap="sm" wrap="nowrap" align="center"
                        style={{ padding: '6px 8px', borderRadius: 6, background: isDone ? '#f0fdf4' : '#fafafa', border: '1px solid #e2e8f0' }}>
                        {/* Checkbox */}
                        <div
                          onClick={async () => {
                            if (togglingMilestone === m.Rid) return;
                            setTogglingMilestone(m.Rid);
                            try {
                              await updateTimelineEvent(m.Rid, { status: isDone ? 'pending' : 'completed' });
                            } finally { setTogglingMilestone(null); }
                          }}
                          style={{ cursor: 'pointer', flexShrink: 0, width: 20, height: 20, borderRadius: 4,
                            border: `2px solid ${isDone ? 'var(--color-success)' : '#cbd5e1'}`,
                            background: isDone ? 'var(--color-success)' : '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                          {togglingMilestone === m.Rid
                            ? <Loader size={10} />
                            : isDone && <IconCheck size={12} color="#fff" stroke={3} />}
                        </div>
                        {/* Title */}
                        <Text size="sm" fw={500} style={{ flex: 1, textDecoration: isDone ? 'line-through' : 'none', color: isDone ? '#94a3b8' : '#1e293b' }}>
                          {m.title || '(untitled)'}
                        </Text>
                        {/* Date + badges */}
                        <Group gap={6} wrap="nowrap">
                          {dateStr && (
                            <Text size="xs" c={isOverdue ? 'var(--color-error)' : 'dimmed'} fw={isOverdue ? 600 : 400}>
                              {fmtDate(dateStr)}
                            </Text>
                          )}
                          {isOverdue && <Badge size="xs" color="var(--color-error)" variant="light">Overdue</Badge>}
                          {isDone && <Badge size="xs" color="var(--color-success)" variant="light">Done</Badge>}
                        </Group>
                        {/* Edit */}
                        <ActionIcon size="xs" variant="subtle" color="var(--color-primary-700)"
                          onClick={() => {
                            setEditMilestoneForm({ title: m.title || '', eventDate: dateStr });
                            setEditingMilestone(m.Rid);
                          }}>
                          <IconPencil size={12} />
                        </ActionIcon>
                        {/* Delete */}
                        <ActionIcon size="xs" variant="subtle" color="#939598"
                          onClick={() => deleteTimelineEvent(m.Rid)}>
                          <IconTrash size={12} />
                        </ActionIcon>
                      </Group>
                    );
                  })}

                  {/* Inline add form */}
                  {addingMilestone && (
                    <Group gap="sm" wrap="nowrap" align="flex-end"
                      style={{ padding: '8px', borderRadius: 6, border: '1px dashed #cbd5e1', background: '#f8fafc' }}>
                      <TextInput size="xs" placeholder="Milestone name" style={{ flex: 1 }}
                        value={milestoneForm.title}
                        onChange={e => setMilestoneForm(f => ({ ...f, title: e.target.value }))}
                        onKeyDown={e => e.key === 'Escape' && setAddingMilestone(false)} />
                      <TextInput size="xs" type="date" style={{ width: 140 }}
                        value={milestoneForm.eventDate}
                        onChange={e => setMilestoneForm(f => ({ ...f, eventDate: e.target.value }))} />
                      <Button size="xs" loading={savingMilestone} disabled={!milestoneForm.title.trim()}
                        onClick={async () => {
                          setSavingMilestone(true);
                          try {
                            await createTimelineEvent({
                              projectId: project.Rid,
                              eventType: 'milestone',
                              title: milestoneForm.title.trim(),
                              eventDate: milestoneForm.eventDate || undefined,
                              status: 'pending',
                            });
                            setMilestoneForm({ title: '', eventDate: '' });
                            setAddingMilestone(false);
                          } finally { setSavingMilestone(false); }
                        }}>Add</Button>
                      <Button size="xs" variant="subtle" onClick={() => setAddingMilestone(false)}>Cancel</Button>
                    </Group>
                  )}
                </Stack>
              )}

              {/* Footer: project deadline + timeline link */}
              <Group justify="space-between" mt="md" pt="sm" style={{ borderTop: '1px solid #f1f5f9' }}>
                <Group gap="xs">
                  <Text size="xs" c="dimmed">Project deadline:</Text>
                  <Text size="xs" fw={500}>{fmtDate((project.deadline || '').replace(/^\[|\]$/g, '')) || 'TBD'}</Text>
                  {project.repairOrReplace && (
                    <>
                      <Text size="xs" c="dimmed" ml="xs">Repair/Replace:</Text>
                      <Text size="xs" fw={500} tt="capitalize">{project.repairOrReplace}</Text>
                    </>
                  )}
                </Group>
                <Button size="xs" variant="subtle" onClick={() => setActiveTab('timeline')}>
                  View full timeline →
                </Button>
              </Group>
            </Paper>

            {/* From Template modal */}
            <Modal opened={templateModalOpen} onClose={() => setTemplateModalOpen(false)}
              title="Add Milestones from Template" size="sm">
              {(() => {
                const tpls = loadTemplates();
                const anySelected = Object.values(templateSelected).some(Boolean);
                return (
                  <Stack gap="sm">
                    <Text size="sm" c="dimmed">Select which milestones to add. Dates will be blank — set them after.</Text>
                    <Stack gap={6}>
                      <Group justify="flex-end" mb={4}>
                        <Button size="xs" variant="subtle"
                          onClick={() => setTemplateSelected(Object.fromEntries(tpls.map(t => [t.id, true])))}>
                          Select all
                        </Button>
                        <Button size="xs" variant="subtle"
                          onClick={() => setTemplateSelected(Object.fromEntries(tpls.map(t => [t.id, false])))}>
                          Clear
                        </Button>
                      </Group>
                      {tpls.map((t, idx) => (
                        <Group key={t.id} gap="sm" wrap="nowrap" align="center"
                          style={{ padding: '7px 10px', borderRadius: 6, cursor: 'pointer',
                            background: templateSelected[t.id] ? '#f5f4ff' : '#fafafa',
                            border: `1px solid ${templateSelected[t.id] ? 'var(--color-primary-700)' : '#e2e8f0'}` }}
                          onClick={() => setTemplateSelected(s => ({ ...s, [t.id]: !s[t.id] }))}>
                          <div style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                            border: `2px solid ${templateSelected[t.id] ? 'var(--color-primary-700)' : '#cbd5e1'}`,
                            background: templateSelected[t.id] ? 'var(--color-primary-700)' : '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {templateSelected[t.id] && <IconCheck size={11} color="#fff" stroke={3} />}
                          </div>
                          <Text size="xs" c="dimmed" style={{ minWidth: 18 }}>{idx + 1}.</Text>
                          <Text size="sm" style={{ flex: 1 }}>{t.name}</Text>
                        </Group>
                      ))}
                    </Stack>
                    <Group justify="flex-end" mt="xs">
                      <Button variant="subtle" onClick={() => setTemplateModalOpen(false)}>Cancel</Button>
                      <Button loading={savingFromTemplate} disabled={!anySelected}
                        onClick={async () => {
                          setSavingFromTemplate(true);
                          try {
                            const selected = tpls.filter(t => templateSelected[t.id]);
                            for (const t of selected) {
                              await createTimelineEvent({
                                projectId: project.Rid,
                                eventType: 'milestone',
                                title: t.name,
                                status: 'pending',
                              });
                            }
                            notifications.show({ message: `${selected.length} milestone${selected.length !== 1 ? 's' : ''} added`, color: 'var(--color-success)' });
                            setTemplateModalOpen(false);
                          } finally { setSavingFromTemplate(false); }
                        }}>
                        Add {Object.values(templateSelected).filter(Boolean).length} Milestone{Object.values(templateSelected).filter(Boolean).length !== 1 ? 's' : ''}
                      </Button>
                    </Group>
                  </Stack>
                );
              })()}
            </Modal>

            {/* Hazard Mitigation (conditional) */}
            {(project.hazardMitigationIncluded || '').toLowerCase() === 'yes' && (
              <Paper p="md" withBorder>
                <Text fw={600} mb="sm">Hazard Mitigation</Text>
                <SimpleGrid cols={{ base: 1, md: 3 }}>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">Description</Text>
                    <Text size="sm">{project.hazardMitigationDescription || '-'}</Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">Cost</Text>
                    <Text fw={500}>{fmt(num(project.hazardMitigationCost))}</Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">BCA</Text>
                    <Text fw={500}>{project.hazardMitigationBca || 'N/A'}</Text>
                  </Stack>
                </SimpleGrid>
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 2: COSTS
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="costs" pt="md">
          <Stack gap="md">
            {/* Cost summary by type */}
            <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }}>
              {(['contracts', 'labor', 'equipment', 'materials', 'rented_equip', 'donated'] as const).map(t => (
                <Paper key={t} p="sm" withBorder>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    {COST_TYPE_LABELS[t]}
                  </Text>
                  <Text size="lg" fw={700}>{fmt(costsByType[t] || 0)}</Text>
                </Paper>
              ))}
            </SimpleGrid>

            {/* Filter buttons */}
            <Group gap="xs">
              <Button
                size="xs" variant={costFilter === null ? 'filled' : 'light'}
                onClick={() => setCostFilter(null)}
              >
                All
              </Button>
              <div style={{ marginLeft: 'auto' }}>
                <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={() => {
                  exportToCSV(`costs-${project?.projectNumber || 'project'}.csv`,
                    ['Type', 'Description', 'Qty', 'Unit', 'Unit Cost', 'Total', 'Est/Actual', 'Eligible'],
                    projCosts.map(c => [c.costType, c.description, c.quantity, c.unit, c.unitCost, c.totalCost, c.estimatedOrActual, c.eligible])
                  );
                }}>Export</Button>
              </div>
              {Object.entries(COST_TYPE_LABELS).map(([key, label]) => (
                <Button
                  key={key} size="xs"
                  variant={costFilter === key ? 'filled' : 'light'}
                  color={COST_TYPE_COLORS[key]}
                  onClick={() => setCostFilter(costFilter === key ? null : key)}
                >
                  {label}
                </Button>
              ))}
            </Group>

            {/* Costs table */}
            <Paper p={0} withBorder style={{ overflow: 'hidden' }}>
              {projCosts.length === 0 ? (
                <Text size="sm" c="dimmed" p="md">No cost records{costFilter ? ` for ${COST_TYPE_LABELS[costFilter] || costFilter}` : ''}.</Text>
              ) : (
                <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '420px' }}>
                <Table striped highlightOnHover>
                  <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                    <Table.Tr>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Notes</Table.Th>
                      <Table.Th>Qty</Table.Th>
                      <Table.Th>Unit Cost</Table.Th>
                      <Table.Th>Total</Table.Th>
                      <Table.Th>Est/Actual</Table.Th>
                      <Table.Th>Eligible</Table.Th>
                      <Table.Th>DOB</Table.Th>
                      <Table.Th w={70}></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {projCosts.map(c => (
                      <Table.Tr key={c.Rid}>
                        <Table.Td>
                          <Badge color={COST_TYPE_COLORS[c.costType] || '#939598'} variant="light" size="sm">
                            {COST_TYPE_LABELS[c.costType] || c.costType}
                          </Badge>
                        </Table.Td>
                        <Table.Td>{c.description || '-'}</Table.Td>
                        <Table.Td>{c.notes || '-'}</Table.Td>
                        <Table.Td>{c.quantity || '-'}</Table.Td>
                        <Table.Td>{fmt(num(c.unitCost))}</Table.Td>
                        <Table.Td fw={700}>{fmt(num(c.totalCost))}</Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm" color={c.estimatedOrActual === 'actual' ? 'var(--color-success)' : '#939598'}>
                            {c.estimatedOrActual || 'est'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm" color={c.eligible === 'false' ? 'var(--color-error)' : 'var(--color-success)'}>
                            {c.eligible === 'false' ? 'No' : 'Yes'}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          {dobLinkedKeys.has(`cost-${c.Rid}`) && (
                            <Badge variant="light" size="sm" color="orange" title="This cost item is flagged for Duplication of Benefits deduction">
                              DOB
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <ActionIcon variant="subtle" size="sm" onClick={() => openEditCost(c)}>
                              <IconEdit size={14} />
                            </ActionIcon>
                            <ActionIcon variant="subtle" size="sm" color="var(--color-error)" onClick={() => handleDeleteCost(c)}>
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                </div>
              )}

              <Group mt="md" p="md">
                <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={openAddCost}>
                  Add Cost Line
                </Button>
                <input
                  type="file"
                  ref={scanInputRef}
                  style={{ display: 'none' }}
                  accept="image/*,.pdf,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleScanFile(f);
                    e.target.value = '';
                  }}
                />
                <Button size="xs" variant="light" color="var(--data-viz-650)"
                  leftSection={<IconScan size={14} />}
                  loading={scanning}
                  onClick={() => scanInputRef.current?.click()}>
                  Scan Receipt / Invoice
                </Button>
                <Button size="xs" variant="light" color="var(--data-viz-650)"
                  leftSection={<IconClock size={14} />}
                  onClick={() => { setTsCatFilter(project?.category || null); setTimesheetImportOpen(true); }}>
                  Import from Timesheets
                </Button>
              </Group>
            </Paper>

            {/* Assigned timesheets summary */}
            {projLabor.length > 0 && (
              <Paper p="md" withBorder mt="md" style={{ borderLeft: '3px solid var(--data-viz-650)' }}>
                <Text fw={600} mb="sm">Force Account Labor ({projLabor.length} timesheets)</Text>
                <Table striped fz="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Employee</Table.Th>
                      <Table.Th>Activity</Table.Th>
                      <Table.Th>Hours</Table.Th>
                      <Table.Th>Total Cost</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {projLabor.map(lr => (
                      <Table.Tr key={lr.Rid}>
                        <Table.Td>{fmtDate(lr.workDate)}</Table.Td>
                        <Table.Td>{lr.employeeName || '-'}</Table.Td>
                        <Table.Td>{lr.activityDescription || '-'}</Table.Td>
                        <Table.Td>{num(lr.hoursRegular) + num(lr.hoursOvertime)}</Table.Td>
                        <Table.Td>{fmt(num(lr.totalCost))}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                <Text size="sm" c="dimmed" mt="xs">
                  Total: {projLabor.reduce((s, r) => s + num(r.hoursRegular) + num(r.hoursOvertime), 0)} hrs
                  {' '}&mdash; {fmt(projLabor.reduce((s, r) => s + num(r.totalCost), 0))}
                </Text>
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 3: INSURANCE
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="insurance" pt="md">
          <Stack gap="md">
            {projInsurance.length === 0 ? (
              <Paper p="lg" withBorder>
                <Stack align="center" gap="sm">
                  <Text c="dimmed">No insurance records for this project.</Text>
                  <Button size="sm" variant="light" leftSection={<IconPlus size={14} />} onClick={openAddInsurance}>
                    Add Insurance Record
                  </Button>
                </Stack>
              </Paper>
            ) : (
              projInsurance.map(ins => (
                <Paper key={ins.Rid} p="md" withBorder>
                  <Group justify="space-between" mb="sm">
                    <Text fw={600}>Policy: {ins.policyNumber || 'N/A'}</Text>
                    <Group gap="xs">
                      <Badge variant="light" color={
                        ins.status === 'settled' ? 'var(--color-success)' :
                        ins.status === 'denied' ? 'var(--color-error)' :
                        ins.status === 'claim_filed' ? 'var(--color-primary-700)' : '#939598'
                      }>
                        {(ins.status || 'pending').replace(/_/g, ' ')}
                      </Badge>
                      <ActionIcon variant="subtle" size="sm" onClick={() => openEditInsurance(ins)}>
                        <IconEdit size={14} />
                      </ActionIcon>
                      <ActionIcon variant="subtle" size="sm" color="var(--color-error)" onClick={() => handleDeleteInsurance(ins.Rid)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </Group>

                  <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Carrier</Text>
                      <Text fw={500}>{ins.carrier || '-'}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Policy Type</Text>
                      <Text fw={500}>{ins.policyType || '-'}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Coverage</Text>
                      <Text fw={500}>{fmt(num(ins.coverageAmount))}</Text>
                    </Stack>
                    <Tooltip label="The deductible is not subtracted from eligible costs — it remains reimbursable per PAPPG Ch. 4.VII" multiline w={260} withArrow>
                      <Stack gap={2} style={{ cursor: 'help' }}>
                        <Text size="xs" c="dimmed" tt="uppercase">Deductible</Text>
                        <Text fw={500}>{fmt(num(ins.deductible))}</Text>
                      </Stack>
                    </Tooltip>
                  </SimpleGrid>

                  <Divider mb="sm" label="Claim Tracking" labelPosition="left" />
                  <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Claim Filed</Text>
                      <Text fw={500}>{ins.claimFiled || 'No'}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Claim Number</Text>
                      <Text fw={500}>{ins.claimNumber || '-'}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Settlement Amount</Text>
                      <Text fw={500}>{fmt(num(ins.settlementAmount))}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">Settlement Date</Text>
                      <Text fw={500}>{fmtDate(ins.settlementDate)}</Text>
                    </Stack>
                  </SimpleGrid>

                  <Divider mb="sm" label="Obtain & Maintain" labelPosition="left" />
                  <SimpleGrid cols={{ base: 2, md: 3 }}>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">O&amp;M Required</Text>
                      <Text fw={500}>{ins.obtainMaintainRequired || '-'}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">O&amp;M Committed</Text>
                      <Text fw={500}>{ins.obtainMaintainCommitted || '-'}</Text>
                    </Stack>
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed" tt="uppercase">FEMA Deduction</Text>
                      <Text fw={500} c={num(ins.femaDeductionAmount) > 0 ? 'var(--color-error)' : undefined}>
                        {fmt(num(ins.femaDeductionAmount))}
                      </Text>
                    </Stack>
                  </SimpleGrid>
                </Paper>
              ))
            )}
            {projInsurance.length > 0 && (
              <>
                <Paper p="sm" radius="sm" bg="blue.0" style={{ border: '1px solid rgba(75,63,255,0.18)' }}>
                  <Group gap="xs" wrap="nowrap">
                    <ThemeIcon size="sm" variant="light" color="var(--color-primary-700)" radius="xl"><IconShieldCheck size={12} /></ThemeIcon>
                    <Text size="xs" c="blue.8">
                      <Text span fw={600}>Insurance deduction note:</Text> Only the settlement amount (what insurance paid) is deducted from eligible project costs.
                      The policy deductible is <Text span fw={600}>not</Text> subtracted — it remains eligible for FEMA reimbursement per PAPPG Chapter 4.VII.
                    </Text>
                  </Group>
                </Paper>
                <Group>
                  <Button size="sm" variant="light" leftSection={<IconPlus size={14} />} onClick={openAddInsurance}>
                    Add Insurance Record
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 4: COMPLIANCE
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="compliance" pt="md">
          <Stack gap="md">
            {/* Header: overall status + edit */}
            {(() => {
              const comp = projCompliance[0];
              const scqFields = ['floodInsuranceRequired', 'inFloodplain', 'coastalBarrierArea', 'historicProperty', 'hazmatPresent', 'esaConsultationRequired'] as const;
              const answered = comp ? scqFields.filter(f => comp[f]).length : 0;
              return (
                <Group justify="space-between">
                  <Group gap="sm">
                    <IconShieldCheck size={18} color="#F86A0B" />
                    <Text fw={600}>EHP Compliance</Text>
                    <Badge size="xs" variant="light" color="#939598">FEMA 009-0-120</Badge>
                    {comp && (
                      <Badge size="xs" color={
                        comp.overallComplianceStatus === 'completed' ? 'var(--color-success)' :
                        comp.overallComplianceStatus === 'in_progress' ? '#C68700' : '#939598'
                      }>
                        {(comp.overallComplianceStatus || 'Not started').replace(/_/g, ' ')}
                      </Badge>
                    )}
                    <Badge size="xs" variant="light" color={answered >= 6 ? 'var(--color-success)' : '#F86A0B'}>
                      {answered}/6 questions answered
                    </Badge>
                  </Group>
                  <Button size="xs" variant="light" leftSection={comp ? <IconEdit size={13} /> : <IconPlus size={13} />}
                    onClick={openEditCompliance}>
                    {comp ? 'Edit Compliance' : 'Add Compliance Record'}
                  </Button>
                </Group>
              );
            })()}

            {projCompliance.length === 0 ? (
              <Paper p="lg" withBorder>
                <Text c="dimmed" ta="center" size="sm">No compliance record yet. Click "Add Compliance Record" to get started.</Text>
              </Paper>
            ) : (() => {
              const comp = projCompliance[0];

              const yesNoBadge = (val: string | undefined, yesIsRed = false) => {
                const v = val || '';
                const color = v === 'Yes' ? (yesIsRed ? 'var(--color-error)' : '#F86A0B')
                  : v === 'No' ? 'var(--color-success)' : '#939598';
                return (
                  <Badge size="xs" color={color} variant={v ? 'light' : 'outline'}>
                    {v || '—'}
                  </Badge>
                );
              };

              const statusBadge = (val: string | undefined) => {
                if (!val) return <Text size="xs" c="dimmed">—</Text>;
                const color = val === 'completed' ? 'var(--color-success)'
                  : val === 'in_progress' ? '#C68700' : '#939598';
                return <Badge size="xs" color={color} variant="light">{val.replace(/_/g, ' ')}</Badge>;
              };

              const row = (label: string, value: React.ReactNode) => (
                <Group key={label} justify="space-between" py={4} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <Text size="sm" c="dimmed">{label}</Text>
                  <div>{value}</div>
                </Group>
              );

              return (
                <Stack gap="md">
                  {/* Section 1: FEMA Special Consideration Questions */}
                  <Paper p="md" withBorder>
                    <Text fw={600} size="sm" mb="sm">FEMA Special Consideration Questions</Text>
                    <Stack gap={0}>
                      {row('1. Insurable risk — flood insurance required?', yesNoBadge(comp.floodInsuranceRequired))}
                      {row('2. Located in floodplain or coastal high-hazard area?', yesNoBadge(comp.inFloodplain))}
                      {row('3. Within Coastal Barrier Resource System unit?', yesNoBadge(comp.coastalBarrierArea))}
                      {row('4. Will repairs change the pre-disaster condition?', yesNoBadge(comp.eo11988Compliance === 'completed' ? 'Yes' : comp.eo11988Compliance ? 'Yes' : 'No'))}
                      {row('6. Historic property or older than 50 years?', yesNoBadge(comp.historicProperty))}
                      {row('7. Contamination or hazardous material on site?', yesNoBadge(comp.hazmatPresent, true))}
                      {row('8. Environmental or ecological concerns?', yesNoBadge(comp.esaConsultationRequired))}
                    </Stack>
                  </Paper>

                  {/* Section 2: Review Details */}
                  <SimpleGrid cols={{ base: 1, md: 2 }}>

                    {/* NEPA / Environmental */}
                    <Paper p="md" withBorder>
                      <Text fw={600} size="sm" mb="sm">NEPA / Environmental</Text>
                      <Stack gap={0}>
                        {row('NEPA Required', yesNoBadge(comp.nepaRequired))}
                        {row('NEPA Level', <Text size="sm">{comp.nepaLevel || '—'}</Text>)}
                        {row('NEPA Status', statusBadge(comp.nepaStatus))}
                        {comp.nepaCompletionDate && row('NEPA Completed', <Text size="sm">{fmtDate(comp.nepaCompletionDate)}</Text>)}
                        {row('ESA Consultation', yesNoBadge(comp.esaConsultationRequired))}
                        {comp.esaStatus && row('ESA Status', <Text size="sm">{comp.esaStatus}</Text>)}
                        {comp.wetlandsImpacted && row('Wetlands Impacted', yesNoBadge(comp.wetlandsImpacted))}
                      </Stack>
                    </Paper>

                    {/* Section 106 / Historic */}
                    <Paper p="md" withBorder>
                      <Text fw={600} size="sm" mb="sm">Section 106 / Historic</Text>
                      <Stack gap={0}>
                        {row('Section 106 Required', yesNoBadge(comp.section106Required))}
                        {row('Section 106 Status', statusBadge(comp.section106Status))}
                        {comp.section106CompletionDate && row('Completed', <Text size="sm">{fmtDate(comp.section106CompletionDate)}</Text>)}
                        {row('SHPO Consultation', statusBadge(comp.shpoConsultation))}
                        {comp.tribalConsultation && row('Tribal Consultation', statusBadge(comp.tribalConsultation))}
                        {comp.historicPropertyAge && row('Property Age', <Text size="sm">{comp.historicPropertyAge} years</Text>)}
                      </Stack>
                    </Paper>

                    {/* Floodplain */}
                    <Paper p="md" withBorder>
                      <Text fw={600} size="sm" mb="sm">Floodplain</Text>
                      <Stack gap={0}>
                        {row('In Floodplain', yesNoBadge(comp.inFloodplain))}
                        {comp.floodZone && row('Flood Zone', <Text size="sm">{comp.floodZone}</Text>)}
                        {comp.baseFloodElevation && row('Base Flood Elevation', <Text size="sm">{comp.baseFloodElevation}</Text>)}
                        {row('Insurance Required', yesNoBadge(comp.floodInsuranceRequired))}
                        {comp.floodInsuranceObtained && row('Insurance Obtained', yesNoBadge(comp.floodInsuranceObtained))}
                        {row('EO 11988 Status', statusBadge(comp.eo11988Compliance))}
                      </Stack>
                    </Paper>

                    {/* Coastal & Hazmat */}
                    <Paper p="md" withBorder>
                      <Text fw={600} size="sm" mb="sm">Coastal &amp; Hazmat</Text>
                      <Stack gap={0}>
                        {row('Coastal Barrier Area', yesNoBadge(comp.coastalBarrierArea))}
                        {comp.coastalZone && row('Coastal Zone', <Text size="sm">{comp.coastalZone}</Text>)}
                        {comp.coastalZoneConsistency && row('Zone Consistency', <Text size="sm">{comp.coastalZoneConsistency}</Text>)}
                        {row('Hazmat Present', yesNoBadge(comp.hazmatPresent, true))}
                        {comp.hazmatPresent === 'Yes' && comp.hazmatType && row('Hazmat Type', <Text size="sm">{comp.hazmatType}</Text>)}
                        {comp.hazmatMitigationRequired && row('Mitigation Required', yesNoBadge(comp.hazmatMitigationRequired))}
                        {comp.ehpReviewerId && row('EHP Reviewer', <Text size="sm">{comp.ehpReviewerId}</Text>)}
                      </Stack>
                    </Paper>

                  </SimpleGrid>
                </Stack>
              );
            })()}

          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            DUPLICATION OF BENEFITS TAB
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="dob" pt="md">
          <Stack gap="md">
            {/* Header */}
            <Group justify="space-between">
              <Group gap="sm">
                <IconCurrencyDollar size={18} color="#7c3aed" />
                <Text fw={600}>Duplication of Benefits (DOB)</Text>
                <Badge size="xs" variant="light" color="#939598">Stafford Act § 312 · 44 CFR § 206.191</Badge>
              </Group>
              <Button size="xs" variant="light" leftSection={<IconPlus size={12} />} onClick={openAddDob}>
                Add Source
              </Button>
            </Group>

            {/* Summary cards — always on top */}
            <SimpleGrid cols={3}>
              <Paper p="md" withBorder style={{ textAlign: 'center' }}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Total DOB Deduction</Text>
                <Text fw={800} size="xl" c={dobTotal > 0 ? 'var(--color-error)' : '#939598'}>{fmt(dobTotal)}</Text>
                <Text size="xs" c="dimmed">{dobSources.length} source{dobSources.length !== 1 ? 's' : ''}</Text>
              </Paper>
              <Paper p="md" withBorder style={{ textAlign: 'center' }}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Gross Eligible Cost</Text>
                <Text fw={800} size="xl">{fmt(netEligible)}</Text>
                <Text size="xs" c="dimmed">Before DOB deduction</Text>
              </Paper>
              <Paper p="md" withBorder style={{ textAlign: 'center', borderTop: `4px solid ${dobNetEligible < 0 ? 'var(--color-error)' : 'var(--color-success)'}` }}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Net After DOB</Text>
                <Text fw={800} size="xl" c={dobNetEligible < 0 ? 'var(--color-error)' : 'var(--color-success)'}>{fmt(dobNetEligible)}</Text>
                <Text size="xs" c="dimmed">Federal share ({fedPct}%): {fmt(dobNetEligible * fedPct / 100)}</Text>
              </Paper>
            </SimpleGrid>

            {/* Sources list */}
            <Paper p="md" withBorder>
              {dobSources.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="lg">
                  No other funding sources identified. Use "Add Source" to declare any insurance proceeds, SBA loans, or other assistance covering the same eligible costs.
                </Text>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Type</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Program / Policy #</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Amount</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Scope Overlap Notes</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#64748b' }}>Linked Costs</th>
                        <th style={{ width: 72 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {dobSources.map((src, idx) => {
                        const linked = (src.linkedCostKeys || []).length;
                        const linkedAmt = dobCostItems.filter(c => (src.linkedCostKeys || []).includes(c.key)).reduce((s, c) => s + c.amount, 0);
                        const isExpanded = dobExpanded.has(src.id);
                        const linkedItems = dobCostItems.filter(c => (src.linkedCostKeys || []).includes(c.key));
                        const typeLabel = src.type === 'insurance' ? 'Insurance' : src.type === 'sba' ? 'SBA Loan' : src.type === 'cdbg_dr' ? 'CDBG-DR' : 'Other';
                        const typeColor = src.type === 'insurance' ? 'blue' : src.type === 'sba' ? 'orange' : src.type === 'cdbg_dr' ? 'green' : 'gray';
                        return (
                          <Fragment key={src.id}>
                            <tr
                              style={{ borderBottom: isExpanded ? 'none' : '1px solid #f1f5f9', cursor: 'pointer', background: isExpanded ? '#faf5ff' : undefined }}
                              onClick={() => setDobExpanded(prev => {
                                const next = new Set(prev);
                                next.has(src.id) ? next.delete(src.id) : next.add(src.id);
                                return next;
                              })}
                            >
                              <td style={{ padding: '10px 12px' }}>
                                <Group gap="xs" wrap="nowrap">
                                  <span style={{ color: '#94a3b8', fontSize: 10, transition: 'transform 0.15s', display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                                  <Badge variant="light" color={typeColor}>{typeLabel}</Badge>
                                </Group>
                              </td>
                              <td style={{ padding: '10px 12px' }}><Text size="sm">{src.program || '—'}</Text></td>
                              <td style={{ padding: '10px 12px', textAlign: 'right' }}><Text size="sm" fw={600} c="var(--color-error)">{fmt(src.amount)}</Text></td>
                              <td style={{ padding: '10px 12px' }}><Text size="sm" c="dimmed" lineClamp={1}>{src.notes || '—'}</Text></td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                {linked > 0 ? (
                                  <Badge size="sm" variant="light" color="violet">
                                    {linked} item{linked !== 1 ? 's' : ''} · {fmt(linkedAmt)}
                                  </Badge>
                                ) : (
                                  <Text size="xs" c="dimmed">None</Text>
                                )}
                              </td>
                              <td style={{ padding: '10px 6px' }}>
                                <Group gap={4} justify="flex-end" onClick={e => e.stopPropagation()}>
                                  <ActionIcon size="sm" variant="subtle" onClick={() => openEditDob(idx)}><IconEdit size={14} /></ActionIcon>
                                  <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => removeDobRow(idx)}><IconTrash size={14} /></ActionIcon>
                                </Group>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#faf5ff' }}>
                                <td colSpan={6} style={{ padding: '0 16px 14px 40px' }}>
                                  <Stack gap={8}>
                                    {src.notes && (
                                      <Group gap="xs" align="flex-start">
                                        <Text size="xs" fw={600} c="dimmed" style={{ minWidth: 100 }}>Scope Notes:</Text>
                                        <Text size="xs">{src.notes}</Text>
                                      </Group>
                                    )}
                                    <Group gap="xs" align="flex-start">
                                      <Text size="xs" fw={600} c="dimmed" style={{ minWidth: 100 }}>DOB Amount:</Text>
                                      <Text size="xs" fw={700} c="var(--color-error)">{fmt(src.amount)}</Text>
                                    </Group>
                                    {linkedItems.length > 0 ? (
                                      <div>
                                        <Text size="xs" fw={600} c="dimmed" mb={4}>Covered Cost Items:</Text>
                                        <Stack gap={4}>
                                          {linkedItems.map(item => (
                                            <Group key={item.key} gap="xs" wrap="nowrap">
                                              <Badge size="xs" variant="light" style={{ background: item.badgeColor + '22', color: item.badgeColor, border: `1px solid ${item.badgeColor}44`, flexShrink: 0 }}>
                                                {item.badge}
                                              </Badge>
                                              <Text size="xs" style={{ flex: 1 }}>{item.label}</Text>
                                              <Text size="xs" fw={600}>{fmt(item.amount)}</Text>
                                            </Group>
                                          ))}
                                        </Stack>
                                      </div>
                                    ) : (
                                      <Text size="xs" c="dimmed">No cost items linked — edit this source to assign covered costs.</Text>
                                    )}
                                  </Stack>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Paper>

            {/* Cost Coverage Map — 1:1 reconciliation */}
            {dobCostItems.length > 0 && dobSources.length > 0 && (
              <Paper p="md" withBorder>
                <Text fw={600} size="sm" mb="xs">Cost Coverage Map</Text>
                <Text size="xs" c="dimmed" mb="sm">
                  Each eligible cost item mapped to its DOB source. Items with no source linked will be fully FEMA-eligible.
                </Text>
                <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 320 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                      <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Cost Item</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Amount</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>DOB Source</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#64748b' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dobCostItems.map(item => {
                        const coveringSource = dobSources.find(s => (s.linkedCostKeys || []).includes(item.key));
                        return (
                          <tr key={item.key} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '10px 12px' }}>
                              <Group gap="xs" wrap="nowrap">
                                <Badge size="xs" variant="light" style={{ background: item.badgeColor + '22', color: item.badgeColor, border: `1px solid ${item.badgeColor}44`, flexShrink: 0 }}>
                                  {item.badge}
                                </Badge>
                                <Text size="sm" style={{ wordBreak: 'break-word' }}>{item.label}</Text>
                              </Group>
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                              <Text size="sm" fw={500}>{fmt(item.amount)}</Text>
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              {coveringSource ? (
                                <Group gap="xs">
                                  <Badge size="xs" variant="light" color={coveringSource.type === 'insurance' ? 'blue' : coveringSource.type === 'sba' ? 'orange' : coveringSource.type === 'cdbg_dr' ? 'green' : 'gray'}>
                                    {coveringSource.type === 'insurance' ? 'Insurance' : coveringSource.type === 'sba' ? 'SBA' : coveringSource.type === 'cdbg_dr' ? 'CDBG-DR' : 'Other'}
                                  </Badge>
                                  <Text size="xs" c="dimmed">{coveringSource.program || ''}</Text>
                                </Group>
                              ) : (
                                <Text size="xs" c="dimmed">—</Text>
                              )}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              {coveringSource ? (
                                <Badge size="xs" color="orange" variant="light">DOB Deducted</Badge>
                              ) : (
                                <Badge size="xs" color="green" variant="light">FEMA Eligible</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Paper>
            )}

            {/* Certification */}
            <Paper p="md" withBorder>
              <Text fw={600} size="sm" mb="xs">DOB Certification</Text>
              <SimpleGrid cols={3} mb="sm">
                <TextInput label="Authorized Representative" value={dobCert.name}
                  onChange={e => setDobCert(c => ({ ...c, name: e.target.value }))} />
                <TextInput label="Title" value={dobCert.title}
                  onChange={e => setDobCert(c => ({ ...c, title: e.target.value }))} />
                <TextInput label="Date" type="date" value={dobCert.date}
                  onChange={e => setDobCert(c => ({ ...c, date: e.target.value }))} />
              </SimpleGrid>
              <Text size="xs" c="dimmed" mb="sm">
                I certify that all assistance received from the sources listed above has been accounted for and deducted from eligible costs in accordance with Stafford Act § 312 and 44 CFR § 206.191. The information provided is accurate and complete to the best of my knowledge.
              </Text>
              {dobCert.name && dobCert.date && (
                <Paper p="xs" withBorder style={{ background: '#f0fdf4', marginBottom: 8 }}>
                  <Text size="xs" c="var(--color-success)">
                    Certified by <strong>{dobCert.name}</strong>{dobCert.title ? `, ${dobCert.title}` : ''} on {fmtDate(dobCert.date)}
                  </Text>
                </Paper>
              )}
              <Button leftSection={<IconCheck size={14} />} loading={savingDob} onClick={saveDobData}>
                Save DOB Declaration
              </Button>
            </Paper>
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 5: DOCUMENTS
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="documents" pt="md">
          <Stack gap="md">
            {/* Upload zone */}
            <Paper
              p="lg" withBorder
              style={{
                border: '2px dashed rgba(75,63,255,0.28)',
                textAlign: 'center',
                cursor: uploading ? 'wait' : 'pointer',
                opacity: uploading ? 0.6 : 1,
              }}
              onClick={() => { if (!uploading) { setUploadDocType(''); fileInputRef.current?.click(); } }}
            >
              <Stack align="center" gap="xs">
                {uploading ? <Loader size={32} /> : <IconUpload size={32} color="var(--color-primary-700)" />}
                <Text size="sm" c="dimmed">
                  {uploading ? 'Uploading...' : 'Click to upload — you\'ll tag the document type next'}
                </Text>
                <Text size="xs" c="dimmed">PDF, DOCX, XLSX, images, ZIP accepted</Text>
              </Stack>
            </Paper>

            {projDocuments.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center">No documents for this project yet.</Text>
            ) : (
              Object.entries(docsByType).map(([type, docs]) => (
                <Paper key={type} p="md" withBorder>
                  <Text fw={600} mb="sm">{type}</Text>
                  <Stack gap="xs">
                    {docs.map(doc => {
                      const checkItem = checklistItems.find(c => c.key === doc.tags);
                      return (
                        <Group key={doc.Rid} justify="space-between" p="xs"
                          style={{ borderRadius: 4, background: '#F8F8F8', cursor: 'pointer' }}
                          onClick={() => openDocViewer(doc)}>
                          <Group gap="sm">
                            {fileIcon(doc.mimeType || '')}
                            <Stack gap={0}>
                              <Group gap={4}>
                                <Text size="sm" fw={500} c="var(--color-primary-700)">{doc.documentName || 'Unnamed'}</Text>
                              </Group>
                              <Text size="xs" c="dimmed">
                                {doc.uploadDate || '-'} {doc.uploadedBy ? `by ${doc.uploadedBy}` : ''}
                                {doc.fileSize ? ` | ${(parseInt(doc.fileSize) / 1024).toFixed(0)} KB` : ''}
                              </Text>
                            </Stack>
                          </Group>
                          <Group gap="xs">
                            {checkItem && (
                              <Badge size="xs" variant="light" color="var(--color-success)" leftSection={<IconCheck size={10} />}>
                                {checkItem.label}
                              </Badge>
                            )}
                            {doc.tags && !checkItem && (
                              <Badge size="xs" variant="light">{doc.tags}</Badge>
                            )}
                            <ActionIcon variant="subtle" size="sm" color="var(--data-viz-650)"
                              loading={extracting}
                              title="Extract costs with AI"
                              onClick={async (e) => { e.stopPropagation(); await handleExtractCosts(doc); }}>
                              <IconRobot size={14} />
                            </ActionIcon>
                            <ActionIcon variant="subtle" size="sm" color="var(--color-primary-700)"
                              title="Edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDocViewer(doc);
                                setEditingDoc(true);
                                setEditDocForm({
                                  title: doc.documentName || '',
                                  documentType: doc.documentType || '',
                                  tags: doc.tags || '',
                                  notes: doc.notes || '', // already stripped by store mapper
                                });
                              }}>
                              <IconPencil size={14} />
                            </ActionIcon>
                            <ActionIcon variant="subtle" size="sm" color="var(--color-error)"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await deleteDocument(doc.Rid);
                                  notifications.show({ message: 'Document deleted', color: '#939598' });
                                } catch { /* ignore */ }
                              }}>
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Group>
                        </Group>
                      );
                    })}
                  </Stack>
                </Paper>
              ))
            )}
          </Stack>
        </Tabs.Panel>

        {/* Document Viewer Modal */}
        <Modal opened={!!viewDoc} onClose={() => { setViewDoc(null); setEditingDoc(false); setDocFileAvailable(null); setPreviewUrl(null); }}
          title={editingDoc ? 'Edit Document' : (viewDoc?.documentName || 'Document')} size="xl">
          {viewDoc && (
            <Stack gap="sm">
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <div>
                  <Text size="xs" c="dimmed">Type</Text>
                  <Text size="sm" fw={500}>{viewDoc.documentType || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Uploaded</Text>
                  <Text size="sm">{viewDoc.uploadDate || '—'} {viewDoc.uploadedBy ? `by ${viewDoc.uploadedBy}` : ''}</Text>
                </div>
              </SimpleGrid>
              {viewDoc.description && (
                <div>
                  <Text size="xs" c="dimmed">Description</Text>
                  <Text size="sm">{viewDoc.description}</Text>
                </div>
              )}
              {viewDoc.tags && (
                <div>
                  <Text size="xs" c="dimmed">Tagged</Text>
                  <Badge size="sm" variant="light">{checklistItems.find(c => c.key === viewDoc.tags)?.label || viewDoc.tags}</Badge>
                </div>
              )}

              {/* Content display */}
              {(() => {
                const raw = viewDoc.notes || '';
                // Separate user notes from legacy embedded file data
                let userNotes = '';
                let legacyFileData = '';
                if (raw.includes('|||FILE|||')) {
                  userNotes = raw.split('|||FILE|||')[0].trim();
                  legacyFileData = raw.split('|||FILE|||')[1] || '';
                } else if (!raw.startsWith('data:')) {
                  userNotes = raw;
                } else {
                  legacyFileData = raw; // entire notes is a data URL (old format)
                }

                const isLegacyImage = legacyFileData.startsWith('data:image');
                const isLegacyPdf = legacyFileData.startsWith('data:application/pdf');
                const isLegacyDataUrl = legacyFileData.startsWith('data:');

                const downloadLegacyFile = (data: string, name: string) => {
                  const a = document.createElement('a');
                  a.href = data;
                  a.download = name;
                  a.click();
                };

                return (
                  <>
                    {userNotes && (
                      <div>
                        <Text size="xs" c="dimmed" mb={2}>Notes</Text>
                        <Text size="sm" mb="sm">{userNotes}</Text>
                      </div>
                    )}
                    {/* Native V2 attachment — inline preview */}
                    {viewDoc.fileId && (() => {
                      const mime = viewDoc.mimeType || '';
                      const name = viewDoc.documentName || '';
                      const isImage = mime.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(name);
                      const isPdf   = mime === 'application/pdf' || /\.pdf$/i.test(name);
                      const isOffice = /\.(docx?|xlsx?|pptx?|csv)$/i.test(name) ||
                        mime.includes('officedocument') || mime.includes('msword') ||
                        mime.includes('spreadsheet') || mime.includes('presentation');

                      if (previewLoading) {
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '20px 0', color: '#94a3b8' }}>
                            <Loader size={16} /> <Text size="sm" c="dimmed">Loading preview…</Text>
                          </div>
                        );
                      }
                      if (!previewUrl) {
                        return (
                          <Paper p="md" bg="gray.0" radius="sm" ta="center">
                            <IconFile size={28} color="#939598" style={{ marginBottom: 6 }} />
                            <Text size="sm" c="dimmed">Preview unavailable</Text>
                          </Paper>
                        );
                      }
                      return (
                        <Stack gap="xs">
                          {isImage && (
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', background: '#f8fafc', textAlign: 'center' }}>
                              <img
                                src={previewUrl}
                                alt={name}
                                style={{ maxWidth: '100%', maxHeight: 520, objectFit: 'contain', display: 'block', margin: '0 auto' }}
                              />
                            </div>
                          )}
                          {isPdf && (
                            <iframe
                              src={previewUrl}
                              title={name}
                              style={{ width: '100%', height: 560, border: '1px solid #e2e8f0', borderRadius: 6 }}
                            />
                          )}
                          {isOffice && !isImage && !isPdf && (
                            <iframe
                              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewUrl)}`}
                              title={name}
                              style={{ width: '100%', height: 560, border: '1px solid #e2e8f0', borderRadius: 6 }}
                            />
                          )}
                          {!isImage && !isPdf && !isOffice && (
                            <Paper p="md" bg="gray.0" radius="sm" ta="center">
                              <IconFile size={28} color="#939598" style={{ marginBottom: 6 }} />
                              <Text size="sm" c="dimmed">No preview available for this file type</Text>
                            </Paper>
                          )}
                          <Group gap="xs">
                            <Button size="xs" variant="light" leftSection={<IconDownload size={12} />}
                              onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}>
                              Download {name || 'File'}
                            </Button>
                            {isOffice && (
                              <Text size="xs" c="dimmed">Preview via Microsoft Office Online</Text>
                            )}
                          </Group>
                        </Stack>
                      );
                    })()}
                    {/* Legacy base64 embedded in notes field */}
                    {!viewDoc.fileId && isLegacyImage && (
                      <div>
                        <img src={legacyFileData} alt={viewDoc.documentName} style={{ maxWidth: '100%', borderRadius: 4 }} />
                        <Button size="xs" variant="light" mt="sm" leftSection={<IconDownload size={12} />}
                          onClick={() => downloadLegacyFile(legacyFileData, viewDoc.documentName || 'image')}>Download</Button>
                      </div>
                    )}
                    {!viewDoc.fileId && isLegacyPdf && (
                      <div>
                        <iframe src={legacyFileData} title={viewDoc.documentName}
                          style={{ width: '100%', height: 500, border: '1px solid #cbd5e1', borderRadius: 4 }} />
                        <Button size="xs" variant="light" mt="sm" leftSection={<IconDownload size={12} />}
                          onClick={() => downloadLegacyFile(legacyFileData, viewDoc.documentName || 'document.pdf')}>Download PDF</Button>
                        {legacyFileData.length >= 65000 && (
                          <Text size="xs" c="#F86A0B" mt={4}>This PDF may be truncated due to storage limits.</Text>
                        )}
                      </div>
                    )}
                    {!viewDoc.fileId && isLegacyDataUrl && !isLegacyImage && !isLegacyPdf && (
                      <div>
                        <Button size="sm" variant="light" leftSection={<IconDownload size={14} />}
                          onClick={() => downloadLegacyFile(legacyFileData, viewDoc.documentName || 'file')}>Download File</Button>
                      </div>
                    )}
                    {!viewDoc.fileId && !legacyFileData && !userNotes && (
                      <Paper p="md" bg="gray.0" radius="sm" ta="center">
                        <IconFile size={32} color="#939598" style={{ marginBottom: 8 }} />
                        <Text size="sm" c="dimmed">No file attached.</Text>
                      </Paper>
                    )}
                  </>
                );
              })()}

              {editingDoc ? (
                <Stack gap="sm" mt="sm">
                  <input type="file" ref={editDocFileRef} style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.pptx,.ppt,.png,.jpg,.jpeg,.gif,.zip"
                    onChange={e => setEditDocFile(e.target.files?.[0] || null)} />
                  <TextInput label="Title" size="sm"
                    value={editDocForm.title}
                    onChange={e => setEditDocForm(f => ({ ...f, title: e.target.value }))} />
                  <Select label="Document Type" size="sm"
                    data={DOC_TYPE_OPTIONS}
                    value={editDocForm.documentType}
                    onChange={v => setEditDocForm(f => ({ ...f, documentType: v || '' }))} />
                  <TextInput label="Tags / Checklist Link" size="sm" placeholder="optional"
                    value={editDocForm.tags}
                    onChange={e => setEditDocForm(f => ({ ...f, tags: e.target.value }))} />
                  <Textarea label="Notes" size="sm" rows={2}
                    value={editDocForm.notes}
                    onChange={e => setEditDocForm(f => ({ ...f, notes: e.target.value }))} />
                  {/* File swap */}
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Replace / Add File</Text>
                    <Group gap="xs">
                      <Button size="xs" variant="light" leftSection={<IconUpload size={12} />}
                        onClick={() => editDocFileRef.current?.click()}>
                        {editDocFile ? 'Change File' : 'Attach File'}
                      </Button>
                      {editDocFile && (
                        <Text size="xs" c="var(--color-success)">{editDocFile.name}</Text>
                      )}
                    </Group>
                  </div>
                  <Group justify="flex-end" gap="xs">
                    <Button variant="default" size="sm" onClick={() => { setEditingDoc(false); setEditDocFile(null); }}>Cancel</Button>
                    <Button size="sm" loading={uploading} onClick={async () => {
                      try {
                        let newNotes = editDocForm.notes;
                        let newTitle = editDocForm.title;
                        if (editDocFile) {
                          // Upload new file via store (same pattern as debris/fileLibrary)
                          if (!newTitle) newTitle = editDocFile.name;
                          await updateDocument(viewDoc.Rid, {
                            title: newTitle,
                            documentType: editDocForm.documentType,
                            tags: editDocForm.tags,
                          });
                          await uploadDocumentFile(viewDoc.Rid, editDocFile, editDocForm.notes);
                        } else {
                          // Preserve existing fileId as meta marker — viewDoc.notes is already stripped
                          const metaPrefix = viewDoc.fileId ? `|||DOCID|||${viewDoc.fileId}|||ATTACHID|||${viewDoc.fileId}|||` : '';
                          newNotes = metaPrefix + editDocForm.notes;
                          await updateDocument(viewDoc.Rid, {
                            title: newTitle,
                            documentType: editDocForm.documentType,
                            tags: editDocForm.tags,
                            notes: newNotes,
                          });
                        }
                        notifications.show({ message: 'Document updated', color: 'var(--color-success)' });
                        setEditingDoc(false);
                        setEditDocFile(null);
                        setViewDoc(null);
                      } catch {
                        notifications.show({ message: 'Failed to save', color: 'var(--color-error)' });
                      }
                    }}>Save</Button>
                  </Group>
                </Stack>
              ) : (
                <Group justify="flex-end" mt="xs">
                  <Button variant="light" size="sm" leftSection={<IconPencil size={14} />}
                    onClick={() => {
                      setEditingDoc(true);
                      setEditDocForm({
                        title: viewDoc?.documentName || '',
                        documentType: viewDoc?.documentType || '',
                        tags: viewDoc?.tags || '',
                        notes: viewDoc?.notes || '', // already stripped by store mapper
                      });
                    }}>Edit</Button>
                  <Button variant="default" size="sm" onClick={() => setViewDoc(null)}>Close</Button>
                </Group>
              )}
            </Stack>
          )}
        </Modal>

        {/* ════════════════════════════════════════════════════
            TAB 6: TASKS
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="tasks" pt="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={600}>Tasks &amp; Deadlines</Text>
              <Group gap="xs">
                <Popover opened={templatePopoverOpen} onChange={setTemplatePopoverOpen} position="bottom-end" width={320}>
                  <Popover.Target>
                    <Button size="xs" variant="light" color="var(--data-viz-650)"
                      leftSection={<IconListCheck size={14} />}
                      onClick={() => setTemplatePopoverOpen(o => !o)}>
                      Load Template
                    </Button>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Stack gap="xs">
                      <Text size="sm" fw={600}>Task Templates</Text>
                      <Text size="xs" c="dimmed">Select a template to add pre-built tasks to this project:</Text>
                      {PA_TASK_TEMPLATES.map(tmpl => (
                        <Paper key={tmpl.name} p="xs" withBorder style={{ cursor: 'pointer' }}
                          onClick={async () => {
                            setTemplatePopoverOpen(false);
                            const today = new Date();
                            let created = 0;
                            for (const task of tmpl.tasks) {
                              const dueDate = new Date(today.getTime() + task.daysFromStart * 86400000);
                              const dueDateStr = dueDate.toISOString().split('T')[0];
                              try {
                                await createTimelineEvent({
                                  projectId: projRid,
                                  organizationId: project?.organizationId || (org ? String(org.Rid) : ''),
                                  eventType: 'task',
                                  eventDate: today.toISOString().split('T')[0],
                                  dueDate: dueDateStr,
                                  description: task.description,
                                  status: 'pending',
                                });
                                created++;
                              } catch { /* continue */ }
                            }
                            notifications.show({ message: `${created} tasks added from "${tmpl.name}"`, color: 'var(--color-success)' });
                          }}>
                          <Text size="sm" fw={500}>{tmpl.name}</Text>
                          <Text size="xs" c="dimmed">{tmpl.tasks.length} tasks</Text>
                        </Paper>
                      ))}
                    </Stack>
                  </Popover.Dropdown>
                </Popover>
                <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={openAddTask}>
                  Add Task
                </Button>
              </Group>
            </Group>

            {projTasks.length === 0 ? (
              <Paper p="lg" withBorder>
                <Stack align="center" gap="sm">
                  <Text c="dimmed">No tasks for this project yet.</Text>
                </Stack>
              </Paper>
            ) : (
              <Stack gap="xs">
                {projTasks.map(t => {
                  const overdue = t.dueDate && t.dueDate < todayStr && t.status !== 'completed';
                  const dueSoon = t.dueDate && !overdue && t.status !== 'completed'
                    && t.dueDate <= new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
                  const borderColor = overdue ? '#B12525' : t.status === 'completed' ? '#059669' : dueSoon ? '#D97706' : '#16A34A';
                  return (
                    <Paper key={t.Rid} p="sm" withBorder
                      style={{ borderLeft: `3px solid ${borderColor}` }}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                          <ActionIcon
                            variant="subtle"
                            color={t.status === 'completed' ? 'var(--color-success)' : '#939598'}
                            onClick={() => handleToggleTask(t)}
                          >
                            {t.status === 'completed' ? <IconCircleCheck size={20} /> : <IconCircle size={20} />}
                          </ActionIcon>
                          <Stack gap={2} style={{ flex: 1 }}>
                            <Popover width={350} position="bottom-start" withArrow>
                              <Popover.Target>
                                <Text size="sm" fw={500} style={{ cursor: 'pointer' }}
                                  td={t.status === 'completed' ? 'line-through' : undefined}
                                  c={t.status === 'completed' ? 'dimmed' : undefined}
                                >
                                  {t.description}
                                </Text>
                              </Popover.Target>
                              <Popover.Dropdown>
                                <Textarea size="xs" label="Task description" autosize minRows={2}
                                  defaultValue={t.description}
                                  onBlur={async (e) => {
                                    const val = e.target.value.trim();
                                    if (val && val !== t.description) {
                                      try { await updateTimelineEvent(t.Rid, { description: val }); }
                                      catch { /* ignore */ }
                                    }
                                  }}
                                />
                              </Popover.Dropdown>
                            </Popover>
                            <Group gap="xs">
                              <Popover width={200} position="bottom" withArrow>
                                <Popover.Target>
                                  <Badge size="xs" variant="light" style={{ cursor: 'pointer' }}
                                    color={t.dueDate ? (overdue ? 'var(--color-error)' : dueSoon ? '#C68700' : 'var(--color-success)') : '#939598'}
                                    leftSection={<IconClock size={10} />}
                                  >
                                    {t.dueDate ? `Due ${fmtDate(t.dueDate)}` : 'Set due date'}
                                  </Badge>
                                </Popover.Target>
                                <Popover.Dropdown>
                                  <TextInput size="xs" type="date" label="Due Date"
                                    defaultValue={t.dueDate || ''}
                                    onChange={async (e) => {
                                      const val = e.target.value;
                                      try {
                                        await updateTimelineEvent(t.Rid, { dueDate: val });
                                      } catch { /* ignore */ }
                                    }}
                                  />
                                </Popover.Dropdown>
                              </Popover>
                              <Badge size="xs" variant="light"
                                color={t.status === 'completed' ? 'var(--color-success)' : t.status === 'in_progress' ? '#C68700' : '#939598'}
                              >
                                {(t.status || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                              </Badge>
                            </Group>
                          </Stack>
                        </Group>
                        <ActionIcon variant="subtle" color="var(--color-error)" size="sm" onClick={() => handleDeleteTask(t.Rid)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 7: TIMELINE
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="timeline" pt="md">
          <Stack gap="md">
            {projTimeline.length === 0 ? (
              <Paper p="lg" withBorder>
                <Stack align="center" gap="sm">
                  <Text c="dimmed">No timeline events for this project.</Text>
                  <Button size="sm" variant="light" leftSection={<IconPlus size={14} />} onClick={openAddEvent}>
                    Add Event
                  </Button>
                </Stack>
              </Paper>
            ) : (
              <>
                {/* Visual timeline bar chart */}
                <Paper p="md" withBorder mb="md">
                  <Text size="sm" fw={600} mb="sm">Project Timeline</Text>
                  <div style={{ position: 'relative', padding: '24px 0', minHeight: 80 }}>
                    {/* Horizontal axis line */}
                    <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: 2, background: '#dee2e6' }} />
                    {/* Today marker */}
                    {(() => {
                      const dates = projTimeline.map(e => (e.status === 'completed' && e.completedDate) ? e.completedDate : e.eventDate).filter(Boolean).sort();
                      if (dates.length < 2) return null;
                      const minD = new Date(dates[0]).getTime();
                      const maxD = new Date(dates[dates.length - 1]).getTime();
                      const range = maxD - minD || 1;
                      const nowPct = Math.min(100, Math.max(0, ((Date.now() - minD) / range) * 100));
                      return (
                        <>
                          <div style={{ position: 'absolute', left: `${nowPct}%`, top: 0, bottom: 0, width: 2, background: '#fa5252', zIndex: 2 }} />
                          <div style={{ position: 'absolute', left: `${nowPct}%`, top: -18, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: 12, color: '#fa5252', fontWeight: 600 }}>Today</div>
                        </>
                      );
                    })()}
                    {/* Event dots — use completedDate for completed items so late completions show actual date */}
                    {(() => {
                      const effectiveDate = (e: typeof projTimeline[number]) =>
                        (e.status === 'completed' && e.completedDate) ? e.completedDate : e.eventDate;
                      const dates = projTimeline.map(e => effectiveDate(e)).filter(Boolean).sort();
                      if (dates.length < 2) return null;
                      const minD = new Date(dates[0]).getTime();
                      const maxD = new Date(dates[dates.length - 1]).getTime();
                      const range = maxD - minD || 1;
                      return projTimeline.map(evt => {
                        const eDate = effectiveDate(evt);
                        if (!eDate) return null;
                        const pct = ((new Date(eDate).getTime() - minD) / range) * 100;
                        const color = evt.status === 'completed' ? '#2f9e44' :
                          evt.status === 'in_progress' ? '#f08c00' :
                          '#adb5bd';
                        const wasLate = evt.status === 'completed' && evt.completedDate && evt.dueDate && evt.completedDate > evt.dueDate;
                        return (
                          <Tooltip key={evt.Rid} multiline w={240}
                            label={<div><Text size="xs" fw={600}>{(evt.eventType || '').replace(/_/g, ' ')}</Text>{evt.dueDate && <Text size="xs">Due: {evt.dueDate}</Text>}{evt.completedDate && <Text size="xs" c="var(--color-success)">Completed: {evt.completedDate}</Text>}{wasLate && <Badge size="xs" color="#F86A0B" variant="light">Completed late</Badge>}<Text size="xs" c="dimmed">{evt.description}</Text>{evt.status && <Badge size="xs" mt={2} variant="light" color={evt.status === 'completed' ? 'var(--color-success)' : '#939598'}>{evt.status}</Badge>}</div>}
                            position="top" withArrow
                          >
                            <div
                              style={{
                                position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%, -50%)',
                                width: 18, height: 18, borderRadius: '50%', background: color,
                                border: '3px solid white', boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
                                cursor: 'pointer', zIndex: 3,
                              }}
                            />
                          </Tooltip>
                        );
                      });
                    })()}
                  </div>
                  {/* Date labels */}
                  {(() => {
                    const dates = projTimeline.map(e => (e.status === 'completed' && e.completedDate) ? e.completedDate : e.eventDate).filter(Boolean).sort();
                    if (dates.length < 2) return null;
                    return (
                      <Group justify="space-between" mt={4}>
                        <Text size="xs" c="dimmed">{fmtDate(dates[0])}</Text>
                        <Text size="xs" c="dimmed">{fmtDate(dates[dates.length - 1])}</Text>
                      </Group>
                    );
                  })()}
                </Paper>

                <div style={{ maxHeight: 'calc(100vh - 360px)', overflowY: 'auto', paddingRight: 4 }}>
                  <Stack gap="md">
                  {projTimeline.map((evt, idx) => (
                  <Group key={evt.Rid} gap="md" align="flex-start" wrap="nowrap">
                    {/* Vertical connector */}
                    <Stack align="center" gap={0} style={{ minWidth: 40 }}>
                      <ThemeIcon
                        size={32} radius="xl" variant="light"
                        color={TIMELINE_EVENT_COLORS[evt.eventType as TimelineEventType] || '#939598'}
                      >
                        <IconClock size={16} />
                      </ThemeIcon>
                      {idx < projTimeline.length - 1 && (
                        <div style={{
                          width: 2, height: 40,
                          background: '#cbd5e1',
                        }} />
                      )}
                    </Stack>

                    <Paper p="sm" withBorder style={{ flex: 1 }}>
                      <Group justify="space-between" mb={4}>
                        <Group gap="xs">
                          <Text size="sm" fw={600}>{fmtDate(evt.eventDate) || 'No date'}</Text>
                          <Badge
                            size="sm" variant="light"
                            color={TIMELINE_EVENT_COLORS[evt.eventType as TimelineEventType] || '#939598'}
                          >
                            {(evt.eventType || 'event').replace(/_/g, ' ')}
                          </Badge>
                        </Group>
                        {evt.status && (
                          <Badge size="sm" variant="light" color={
                            evt.status === 'completed' ? 'var(--color-success)' :
                            evt.status === 'overdue' ? 'var(--color-error)' : '#939598'
                          }>
                            {(evt.status || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                          </Badge>
                        )}
                      </Group>
                      <Text size="sm">{evt.description || '-'}</Text>
                    </Paper>
                  </Group>
                  ))}
                  </Stack>
                </div>

                <Group>
                  <Button size="sm" variant="light" leftSection={<IconPlus size={14} />} onClick={openAddEvent}>
                    Add Event
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB: PHOTOS
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="photos" pt="md">
          {project && (
            <ProjectPhotos
              projectId={projRid}
              organizationId={project.organizationId || ''}
              documents={projDocuments}
            />
          )}
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB: CONTRACTS (FEMA 009-0-126)
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="contracts" pt="md">
          <Contracts projectId={projRid} />
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB: PW VERSIONS
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="versions" pt="md">
          {project && <ProjectVersions project={project} />}
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            TAB 8: NOTES
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="notes" pt="md">
          <Stack gap="md">
            {/* Add note area */}
            <Paper p="md" withBorder>
              <Textarea
                placeholder="Add a note..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                minRows={3} mb="sm"
              />
              <Group justify="flex-end">
                <Button size="sm" leftSection={<IconPlus size={14} />}
                  disabled={!noteText.trim() || noteSaving}
                  loading={noteSaving}
                  onClick={handleAddNote}>
                  Add Note
                </Button>
              </Group>
            </Paper>

            {projNotes.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center">No notes yet.</Text>
            ) : (
              projNotes.map(note => (
                <Paper key={note.Rid} p="md" withBorder>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{note.content}</Text>
                  <Group mt="xs" gap="xs">
                    <Text size="xs" c="dimmed">{note.creatorId || 'Unknown'}</Text>
                    <Text size="xs" c="dimmed">|</Text>
                    <Text size="xs" c="dimmed">{fmtDateTime(note.createdDate)}</Text>
                  </Group>
                </Paper>
              ))
            )}
          </Stack>
        </Tabs.Panel>

        {/* ═══════ Extensions Tab ═══════ */}
        <Tabs.Panel value="extensions" pt="md">
          {(() => {
            const projExtensions = timeExtensions.filter(t => String(t.projectId) === String(projectId));
            const teStatusColors: Record<string, string> = { draft: '#939598', submitted: 'var(--color-primary-700)', grantee_approved: 'var(--color-success)', forwarded_to_fema: 'var(--data-viz-100)', approved: '#03AC9E', denied: 'var(--color-error)', revision_needed: '#F86A0B' };
            const approvedMonths = projExtensions
              .filter(t => ['grantee_approved', 'forwarded_to_fema', 'approved'].includes(t.status))
              .reduce((sum, t) => sum + (parseInt(t.extensionMonths) || 0), 0);
            const catLetter = project?.category || '';
            const authorityLimit = ['A', 'B'].includes(catLetter.toUpperCase()) ? 6 : 30;
            return (
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group gap="md">
                    <Text fw={600}>Time Extension Requests ({projExtensions.length})</Text>
                    {approvedMonths > 0 && (
                      <Badge size="sm" color={approvedMonths > authorityLimit ? '#F86A0B' : 'var(--color-primary-700)'} variant="light">
                        {approvedMonths} of {authorityLimit} mo used (Cat {catLetter})
                      </Badge>
                    )}
                  </Group>
                  <Button size="xs" variant="light" leftSection={<IconClock size={14} />} onClick={() => navigate('/pagrants/pa/time-extensions')}>Manage All</Button>
                </Group>
                {projExtensions.length === 0 ? (
                  <Text c="dimmed" size="sm">No time extension requests for this project.</Text>
                ) : (
                  <Paper withBorder>
                    <Table striped highlightOnHover>
                      <Table.Thead><Table.Tr>
                        <Table.Th>Current Deadline</Table.Th><Table.Th>Requested Deadline</Table.Th>
                        <Table.Th>Extension</Table.Th><Table.Th>Status</Table.Th><Table.Th>Date</Table.Th>
                      </Table.Tr></Table.Thead>
                      <Table.Tbody>
                        {projExtensions.map(te => (
                          <Table.Tr key={te.id}>
                            <Table.Td>{te.currentDeadline || '—'}</Table.Td>
                            <Table.Td>{te.requestedNewDeadline || '—'}</Table.Td>
                            <Table.Td>{te.extensionMonths ? `${te.extensionMonths} mo` : '—'}</Table.Td>
                            <Table.Td><Badge color={teStatusColors[te.status] || '#939598'} size="sm">{te.status}</Badge></Table.Td>
                            <Table.Td>{te.requestDate || '—'}</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Paper>
                )}
              </Stack>
            );
          })()}
        </Tabs.Panel>

        {/* ═══════ Reimbursements Tab ═══════ */}
        <Tabs.Panel value="reimbursements" pt="md">
          {(() => {
            const projRFRs = reimbursementRequests.filter(r => String(r.projectId) === String(projectId));
            const rfrStatusColors: Record<string, string> = { draft: '#939598', submitted: 'var(--color-primary-700)', under_review: 'var(--data-viz-100)', approved: 'var(--color-success)', denied: 'var(--color-error)', revision_needed: '#F86A0B', paid: '#03AC9E' };
            const totalRequested = projRFRs.reduce((s, r) => s + (parseFloat(r.requestedAmount) || 0), 0);
            const totalApproved = projRFRs.filter(r => r.status === 'approved' || r.status === 'paid').reduce((s, r) => s + (parseFloat(r.approvedAmount) || 0), 0);
            const totalPaid = projRFRs.filter(r => r.status === 'paid').reduce((s, r) => s + (parseFloat(r.approvedAmount) || 0), 0);
            const fmtC = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
            return (<>
              {selectedRfr ? (() => {
                // ── Inline detail / review ──────────────────────────
                const rfr = selectedRfr;
                let breakdown: { linked?: any[]; freeform?: any[] } = {};
                try { breakdown = JSON.parse(rfr.costBreakdown || '{}'); } catch {}
                const reqAmt = parseFloat(rfr.requestedAmount) || 0;
                const fedShare = parseFloat(rfr.federalShare) || 0;
                const appMatch = parseFloat(rfr.applicantMatch) || 0;
                const canReview = !isApplicantRole && (rfr.status === 'submitted' || rfr.status === 'under_review');
                const canApplicantAct = rfr.status === 'draft' || rfr.status === 'revision_needed';
                const canRetract = rfr.status === 'submitted';
                const canMarkPaid = !isApplicantRole && rfr.status === 'approved';

                async function rfrAction(action: 'approve' | 'deny' | 'revision' | 'submit' | 'retract' | 'paid') {
                  setSavingRfrAction(true);
                  try {
                    const now = new Date().toISOString().split('T')[0];
                    const actor = useAuthStore.getState().user?.fullName || useAuthStore.getState().user?.email || '';
                    let updates: any = {};
                    if (action === 'approve') {
                      const amt = parseFloat(rfrAdjustedAmount) || reqAmt;
                      updates = { status: 'approved', approvedAmount: String(amt), reviewedBy: actor, reviewDate: now, reviewNotes: rfrReviewNotes };
                      // update project drawdown
                      try {
                        const proj = projects.find(p => String(p.Rid) === String(project?.Rid));
                        if (proj) await updateProject(proj.Rid, { drawdownAmount: String((parseFloat(proj.drawdownAmount) || 0) + amt) });
                      } catch {}
                    } else if (action === 'deny') {
                      updates = { status: 'denied', reviewedBy: actor, reviewDate: now, reviewNotes: rfrReviewNotes };
                    } else if (action === 'revision') {
                      updates = { status: 'revision_needed', reviewedBy: actor, reviewDate: now, reviewNotes: rfrReviewNotes };
                    } else if (action === 'submit') {
                      updates = { status: 'submitted', submittedBy: actor, submittedDate: now };
                    } else if (action === 'retract') {
                      updates = { status: 'draft', submittedBy: '', submittedDate: '' };
                    } else if (action === 'paid') {
                      updates = { status: 'paid', paymentDate: now, paymentReference: rfrAdjustedAmount };
                    }
                    await updateReimbursement(rfr.Rid, updates);
                    await fetchReimbursements();
                    // refresh selectedRfr
                    const fresh = usePAGrantsStore.getState().reimbursementRequests.find((r: any) => r.Rid === rfr.Rid);
                    setSelectedRfr(fresh || null);
                    setRfrReviewNotes('');
                    setRfrAdjustedAmount('');
                  } finally { setSavingRfrAction(false); }
                }

                return (
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Button size="xs" variant="subtle" leftSection={<IconArrowLeft size={12} />}
                        onClick={() => { setSelectedRfr(null); setRfrReviewNotes(''); setRfrAdjustedAmount(''); }}>
                        Back to list
                      </Button>
                      <Group gap="xs">
                        <Badge size="md" color={rfrStatusColors[rfr.status] || '#939598'}>
                          {rfr.status?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                        </Badge>
                        <Button size="xs" variant="light"
                          onClick={() => navigate(`/pagrants/pa/reimbursements?rfr=${rfr.Rid}`)}>
                          Open in Reimbursements →
                        </Button>
                      </Group>
                    </Group>

                    <div>
                      <Text fw={700} size="lg">{rfr.requestNumber || 'Untitled RFR'}</Text>
                      <Text size="sm" c="dimmed">
                        Period: {rfr.periodStart ? new Date(rfr.periodStart).toLocaleDateString() : '—'} – {rfr.periodEnd ? new Date(rfr.periodEnd).toLocaleDateString() : '—'}
                        {rfr.submittedBy ? ` · Submitted by ${rfr.submittedBy}` : ''}
                        {rfr.submittedDate ? ` on ${new Date(rfr.submittedDate).toLocaleDateString()}` : ''}
                      </Text>
                    </div>

                    {/* Cost breakdown */}
                    <Paper withBorder p="md">
                      <Text fw={600} mb="xs">Cost Breakdown</Text>
                      {breakdown.linked?.map((item: any, i: number) => (
                        <Group key={i} justify="space-between" py={2}>
                          <Text size="sm">• {item.description}</Text>
                          <Text size="sm" fw={500}>{fmtC(item.amount)}</Text>
                        </Group>
                      ))}
                      {breakdown.freeform?.filter((i: any) => i.description || i.amount).map((item: any, i: number) => (
                        <Group key={i} justify="space-between" py={2}>
                          <Text size="sm">• {item.description}{item.category ? ` (${item.category})` : ''}</Text>
                          <Text size="sm" fw={500}>{fmtC(item.amount)}</Text>
                        </Group>
                      ))}
                      {!breakdown.linked?.length && !breakdown.freeform?.filter((i: any) => i.description || i.amount).length && (
                        <Text size="sm" c="dimmed">No cost items recorded.</Text>
                      )}
                      <Divider my="sm" />
                      <SimpleGrid cols={3}>
                        <Paper p="xs" withBorder style={{ textAlign: 'center' }}>
                          <Text size="xs" c="dimmed">Total</Text><Text fw={700}>{fmtC(reqAmt)}</Text>
                        </Paper>
                        <Paper p="xs" withBorder style={{ textAlign: 'center' }}>
                          <Text size="xs" c="dimmed">Federal Share</Text>
                          <Text fw={700} c="var(--color-primary-700)">{fmtC(fedShare)}</Text>
                        </Paper>
                        <Paper p="xs" withBorder style={{ textAlign: 'center' }}>
                          <Text size="xs" c="dimmed">Applicant Match</Text><Text fw={700}>{fmtC(appMatch)}</Text>
                        </Paper>
                      </SimpleGrid>
                    </Paper>

                    {/* Status banners */}
                    {rfr.status === 'approved' && (
                      <Alert color="var(--color-success)" title="Approved">
                        Approved {fmtC(parseFloat(rfr.approvedAmount) || 0)} by {rfr.reviewedBy} on {rfr.reviewDate ? new Date(rfr.reviewDate).toLocaleDateString() : '—'}.
                        {rfr.reviewNotes ? ` — ${rfr.reviewNotes}` : ''}
                      </Alert>
                    )}
                    {rfr.status === 'denied' && (
                      <Alert color="var(--color-error)" title="Denied">
                        Denied by {rfr.reviewedBy} on {rfr.reviewDate ? new Date(rfr.reviewDate).toLocaleDateString() : '—'}.
                        {rfr.reviewNotes ? ` — ${rfr.reviewNotes}` : ''}
                      </Alert>
                    )}
                    {rfr.status === 'revision_needed' && (
                      <Alert color="#F86A0B" title="Revision Requested">
                        {rfr.reviewedBy} on {rfr.reviewDate ? new Date(rfr.reviewDate).toLocaleDateString() : '—'}: {rfr.reviewNotes || 'No details provided.'}
                      </Alert>
                    )}
                    {rfr.status === 'paid' && (
                      <Alert color="#03AC9E" title="Paid">
                        Paid on {rfr.paymentDate ? new Date(rfr.paymentDate).toLocaleDateString() : '—'}.
                        {rfr.paymentReference ? ` Ref: ${rfr.paymentReference}` : ''}
                      </Alert>
                    )}

                    {/* Review actions (grantee/admin) */}
                    {canReview && (
                      <Paper withBorder p="md">
                        <Text fw={600} mb="sm">Review</Text>
                        <Stack gap="sm">
                          <TextInput label="Approved Amount" placeholder={fmtC(reqAmt)}
                            value={rfrAdjustedAmount}
                            onChange={e => setRfrAdjustedAmount(e.target.value)} />
                          <Textarea label="Review Notes" placeholder="Comments for the applicant…" rows={2}
                            value={rfrReviewNotes} onChange={e => setRfrReviewNotes(e.target.value)} />
                          <Group>
                            <Button color="var(--color-success)" leftSection={<IconCheck size={14} />}
                              loading={savingRfrAction} onClick={() => rfrAction('approve')}>Approve</Button>
                            <Button color="#F86A0B" loading={savingRfrAction}
                              onClick={() => rfrAction('revision')}>Request Revision</Button>
                            <Button color="var(--color-error)" loading={savingRfrAction}
                              onClick={() => rfrAction('deny')}>Deny</Button>
                          </Group>
                        </Stack>
                      </Paper>
                    )}

                    {/* Mark as paid (grantee/admin) */}
                    {canMarkPaid && (
                      <Paper withBorder p="md">
                        <Text fw={600} mb="sm">Mark as Paid</Text>
                        <Group align="flex-end">
                          <TextInput label="Payment Reference" placeholder="Check #, wire ref…"
                            value={rfrAdjustedAmount} onChange={e => setRfrAdjustedAmount(e.target.value)}
                            style={{ flex: 1 }} />
                          <Button color="#03AC9E" loading={savingRfrAction}
                            onClick={() => rfrAction('paid')}>Mark Paid</Button>
                        </Group>
                      </Paper>
                    )}

                    {/* Applicant actions */}
                    {canApplicantAct && (
                      <Group>
                        <Button leftSection={<IconSend size={14} />} loading={savingRfrAction}
                          onClick={() => rfrAction('submit')}>Submit</Button>
                      </Group>
                    )}
                    {canRetract && (
                      <Group>
                        <Button variant="light" loading={savingRfrAction}
                          onClick={() => rfrAction('retract')}>Retract to Draft</Button>
                      </Group>
                    )}
                  </Stack>
                );
              })() : (
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={600}>Reimbursement Requests ({projRFRs.length})</Text>
                  <Group gap="xs">
                    <Button size="xs" leftSection={<IconPlus size={12} />}
                      onClick={() => {
                        setRfrForm({ periodStart: '', periodEnd: '', notes: '' });
                        setRfrLinked(new Set());
                        setRfrExtra([]);
                        setRfrModalOpen(true);
                      }}>
                      New RFR
                    </Button>
                    <Button size="xs" variant="light" onClick={() => navigate('/pagrants/pa/reimbursements')}>Manage All</Button>
                  </Group>
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                  <Paper p="xs" withBorder><Text size="xs" c="dimmed">Requested</Text><Text fw={700}>{fmtC(totalRequested)}</Text></Paper>
                  <Paper p="xs" withBorder><Text size="xs" c="dimmed">Approved</Text><Text fw={700} c="var(--color-success)">{fmtC(totalApproved)}</Text></Paper>
                  <Paper p="xs" withBorder><Text size="xs" c="dimmed">Paid</Text><Text fw={700} c="#03AC9E">{fmtC(totalPaid)}</Text></Paper>
                </SimpleGrid>
                {projRFRs.length === 0 ? (
                  <Text c="dimmed" size="sm">No reimbursement requests for this project.</Text>
                ) : (
                  <Paper withBorder>
                    <div style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
                    <Table striped highlightOnHover>
                      <Table.Thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                        <Table.Tr>
                          <Table.Th>RFR #</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Requested</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Approved</Table.Th>
                          <Table.Th>Status</Table.Th>
                          <Table.Th>Date</Table.Th>
                          <Table.Th />
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {projRFRs.map(rfr => (
                          <Table.Tr key={rfr.id} style={{ cursor: 'pointer' }}
                            onClick={() => { setSelectedRfr(rfr); setRfrReviewNotes(''); setRfrAdjustedAmount(''); }}>
                            <Table.Td fw={500}>{rfr.requestNumber || '—'}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{rfr.requestedAmount ? fmtC(parseFloat(rfr.requestedAmount)) : '—'}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{rfr.approvedAmount ? fmtC(parseFloat(rfr.approvedAmount)) : '—'}</Table.Td>
                            <Table.Td>
                              <Badge color={rfrStatusColors[rfr.status] || '#939598'} size="sm">
                                {rfr.status?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                              </Badge>
                            </Table.Td>
                            <Table.Td>{rfr.submittedDate ? new Date(rfr.submittedDate).toLocaleDateString() : rfr.requestDate ? new Date(rfr.requestDate).toLocaleDateString() : '—'}</Table.Td>
                            <Table.Td onClick={e => e.stopPropagation()}>
                              <Button size="compact-xs" variant="subtle"
                                onClick={() => navigate(`/pagrants/pa/reimbursements?rfr=${rfr.Rid}`)}>
                                Open →
                              </Button>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                    </div>
                  </Paper>
                )}
              </Stack>
              )}
            </>);
          })()}
        </Tabs.Panel>

        {/* New RFR modal */}
        <Modal opened={rfrModalOpen} onClose={() => setRfrModalOpen(false)} title="New Reimbursement Request" size="xl">
          {project && (() => {
            const fedPctVal = num(project.federalSharePct) || 75;

            // Build cost record list for this project
            const costRecords = [
              ...projectCosts.filter(c => String(c.projectId) === String(projRid)).map(c => ({
                key: `cost-${c.Rid}`, label: `${COST_TYPE_LABELS[c.costType] || c.costType}: ${c.description || ''}`,
                amount: num(c.totalCost), badge: COST_TYPE_LABELS[c.costType] || c.costType, badgeColor: COST_TYPE_COLORS[c.costType] || '#939598',
              })),
              ...laborRecords.filter(l => String(l.projectId) === String(projRid)).map(l => ({
                key: `labor-${l.Rid}`, label: `Labor: ${l.employeeName || ''}${l.workDate ? ` (${fmtDate(l.workDate)})` : ''}`,
                amount: num(l.totalCost), badge: 'Labor', badgeColor: COST_TYPE_COLORS.labor,
              })),
              ...equipRecords.filter(e => String(e.projectId) === String(projRid)).map(e => ({
                key: `equip-${e.Rid}`, label: `Equipment: ${e.equipmentType || ''}${e.workDate ? ` (${fmtDate(e.workDate)})` : ''}`,
                amount: num(e.totalCost), badge: 'Equipment', badgeColor: COST_TYPE_COLORS.equipment,
              })),
              ...materials.filter(m => String(m.projectId) === String(projRid)).map(m => ({
                key: `mat-${m.Rid}`, label: `Materials: ${m.materialDescription || ''}`,
                amount: num(m.totalCost), badge: 'Materials', badgeColor: COST_TYPE_COLORS.materials,
              })),
            ];

            const linkedTotal = costRecords.filter(r => rfrLinked.has(r.key)).reduce((s, r) => s + r.amount, 0);
            const extraTotal = rfrExtra.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
            const total = linkedTotal + extraTotal;
            const fedShare = total * (fedPctVal / 100);
            const appMatch = total - fedShare;

            const toggleLinked = (key: string) => setRfrLinked(prev => {
              const next = new Set(prev);
              next.has(key) ? next.delete(key) : next.add(key);
              return next;
            });

            async function saveRfr(submit: boolean) {
              setSavingRfr(true);
              try {
                const existingRfrs = reimbursementRequests.filter(r => String(r.projectId) === String(project.Rid));
                const prefix = `RFR-${project.projectNumber || '000'}`;
                const nums = existingRfrs.filter(r => r.requestNumber?.startsWith(prefix)).map(r => parseInt(r.requestNumber.split('-').pop() || '0')).filter(n => !isNaN(n));
                const nextNum = nums.length > 0 ? Math.max(...nums) + 1 : 1;
                const requestNumber = `${prefix}-${String(nextNum).padStart(3, '0')}`;
                const linked = costRecords.filter(r => rfrLinked.has(r.key)).map(r => ({ description: r.label, amount: r.amount }));
                const freeform = rfrExtra.filter(i => i.description || i.amount).map(i => ({ description: i.description, category: i.category, amount: parseFloat(i.amount) || 0 }));
                await createReimbursement({
                  projectId: String(project.Rid),
                  organizationId: project.organizationId,
                  disasterId: project.disasterId,
                  requestNumber,
                  requestDate: new Date().toISOString().split('T')[0],
                  periodStart: rfrForm.periodStart,
                  periodEnd: rfrForm.periodEnd,
                  requestedAmount: String(total),
                  federalShare: String(fedShare),
                  applicantMatch: String(appMatch),
                  costBreakdown: JSON.stringify({ linked, freeform }),
                  notes: rfrForm.notes,
                  status: submit ? 'submitted' : 'draft',
                  ...(submit ? {
                    submittedBy: useAuthStore.getState().user?.fullName || useAuthStore.getState().user?.email || '',
                    submittedDate: new Date().toISOString().split('T')[0],
                  } : {}),
                });
                await fetchReimbursements();
                notifications.show({ message: `${requestNumber} ${submit ? 'submitted' : 'saved as draft'}`, color: 'var(--color-success)' });
                setRfrModalOpen(false);
              } finally { setSavingRfr(false); }
            }

            return (
              <Stack gap="md">
                <Text size="sm" c="dimmed">Project: <strong>{project.projectNumber} — {project.projectTitle}</strong></Text>

                <SimpleGrid cols={2}>
                  <TextInput label="Period Start" type="date" value={rfrForm.periodStart}
                    onChange={e => setRfrForm(f => ({ ...f, periodStart: e.target.value }))} />
                  <TextInput label="Period End" type="date" value={rfrForm.periodEnd}
                    onChange={e => setRfrForm(f => ({ ...f, periodEnd: e.target.value }))} />
                </SimpleGrid>

                {/* Cost records */}
                <div>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={600}>Cost Records</Text>
                    {costRecords.length > 0 && (
                      <Group gap="xs">
                        <Button size="xs" variant="subtle"
                          onClick={() => setRfrLinked(new Set(costRecords.map(r => r.key)))}>
                          Select all
                        </Button>
                        <Button size="xs" variant="subtle" onClick={() => setRfrLinked(new Set())}>
                          Clear
                        </Button>
                      </Group>
                    )}
                  </Group>
                  {costRecords.length === 0 ? (
                    <Paper p="sm" withBorder>
                      <Text size="sm" c="dimmed">No cost records found for this project. Add labor, equipment, materials, or cost entries on the Costs tab first.</Text>
                    </Paper>
                  ) : (
                    <Paper withBorder style={{ maxHeight: 240, overflowY: 'auto' }}>
                      {costRecords.map(r => {
                        const checked = rfrLinked.has(r.key);
                        return (
                          <Group key={r.key} gap="sm" wrap="nowrap" align="center"
                            onClick={() => toggleLinked(r.key)}
                            style={{ padding: '7px 12px', cursor: 'pointer',
                              background: checked ? '#f5f4ff' : 'transparent',
                              borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                              border: `2px solid ${checked ? 'var(--color-primary-700)' : '#cbd5e1'}`,
                              background: checked ? 'var(--color-primary-700)' : '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {checked && <IconCheck size={11} color="#fff" stroke={3} />}
                            </div>
                            <Badge size="xs" style={{ background: r.badgeColor, color: '#fff', flexShrink: 0 }}>{r.badge}</Badge>
                            <Text size="sm" style={{ flex: 1 }}>{r.label}</Text>
                            <Text size="sm" fw={600} style={{ flexShrink: 0 }}>{fmt(r.amount)}</Text>
                          </Group>
                        );
                      })}
                    </Paper>
                  )}
                  {rfrLinked.size > 0 && (
                    <Text size="xs" c="dimmed" mt={4}>
                      {rfrLinked.size} selected · subtotal {fmt(linkedTotal)}
                    </Text>
                  )}
                </div>

                {/* Additional items */}
                <div>
                  <Text size="sm" fw={600} mb="xs">Additional Items <Text span size="xs" c="dimmed">(costs not yet in the system)</Text></Text>
                  {rfrExtra.map((item, idx) => (
                    <Group key={idx} gap="xs" align="flex-end" mb="xs">
                      <TextInput size="xs" placeholder="Description" style={{ flex: 2 }}
                        value={item.description}
                        onChange={e => setRfrExtra(ex => { const n = [...ex]; n[idx] = { ...n[idx], description: e.target.value }; return n; })} />
                      <Select size="xs" placeholder="Category" style={{ flex: 1 }} clearable
                        data={['Labor', 'Equipment', 'Materials', 'Contracts', 'Rented Equipment', 'Other']}
                        value={item.category || null}
                        onChange={v => setRfrExtra(ex => { const n = [...ex]; n[idx] = { ...n[idx], category: v || '' }; return n; })} />
                      <TextInput size="xs" placeholder="$0.00" style={{ width: 100 }}
                        value={item.amount}
                        onChange={e => setRfrExtra(ex => { const n = [...ex]; n[idx] = { ...n[idx], amount: e.target.value }; return n; })} />
                      <ActionIcon size="sm" variant="subtle" color="var(--color-error)"
                        onClick={() => setRfrExtra(ex => ex.filter((_, i) => i !== idx))}>
                        <IconTrash size={12} />
                      </ActionIcon>
                    </Group>
                  ))}
                  <Button size="xs" variant="light" leftSection={<IconPlus size={12} />}
                    onClick={() => setRfrExtra(ex => [...ex, { description: '', category: '', amount: '' }])}>
                    Add Item
                  </Button>
                </div>

                {/* Totals */}
                <SimpleGrid cols={3}>
                  <Paper p="xs" withBorder style={{ textAlign: 'center' }}>
                    <Text size="xs" c="dimmed">Total Requested</Text>
                    <Text fw={700} size="lg">{fmt(total)}</Text>
                  </Paper>
                  <Paper p="xs" withBorder style={{ textAlign: 'center' }}>
                    <Text size="xs" c="dimmed">Federal ({fedPctVal}%)</Text>
                    <Text fw={700} size="lg" c="var(--color-primary-700)">{fmt(fedShare)}</Text>
                  </Paper>
                  <Paper p="xs" withBorder style={{ textAlign: 'center' }}>
                    <Text size="xs" c="dimmed">Applicant Match</Text>
                    <Text fw={700} size="lg">{fmt(appMatch)}</Text>
                  </Paper>
                </SimpleGrid>

                <Textarea label="Notes" rows={2} value={rfrForm.notes}
                  onChange={e => setRfrForm(f => ({ ...f, notes: e.target.value }))} />

                <Group justify="flex-end">
                  <Button variant="subtle" onClick={() => setRfrModalOpen(false)}>Cancel</Button>
                  <Button variant="light" loading={savingRfr} disabled={total === 0}
                    onClick={() => saveRfr(false)}>Save as Draft</Button>
                  <Button loading={savingRfr} disabled={total === 0}
                    onClick={() => saveRfr(true)}>Save &amp; Submit</Button>
                </Group>
              </Stack>
            );
          })()}
        </Modal>

        {/* ── 406 Mitigation ────────────────────────────── */}
        {/* ── RFIs ──────────────────────────────────────────── */}
        <Tabs.Panel value="rfis" pt="md">
          <RfiTab
            projectRid={projRid}
            project={project}
            rfis={projRfis}
            rfiResponses={rfiResponses}
            rfisLoading={rfisLoading}
            fetchRfiResponses={fetchRfiResponses}
            createRfi={createRfi}
            updateRfi={updateRfi}
            deleteRfi={deleteRfi}
            createRfiResponse={createRfiResponse}
            deleteRfiResponse={deleteRfiResponse}
            canEdit={!isApplicantViewer}
            isApplicant={isApplicantRole}
          />
        </Tabs.Panel>

        {/* ── 406 Mitigation ────────────────────────────── */}
        <Tabs.Panel value="mitigation" pt="md">
          {project && <MitigationTab project={project} orgRid={orgId ?? ''} disasterRid={project.disasterId ?? ''} canEdit={!isApplicantViewer} isApplicant={isApplicantRole} />}
        </Tabs.Panel>

        {/* ── Evidence / Linked Damage Assessments ─────── */}
        <Tabs.Panel value="evidence" pt="md">
          <Stack gap="md">
            <Paper p="md" withBorder>
              <Group justify="space-between" mb="sm">
                <Group gap="xs">
                  <Text fw={600}>Linked Damage Assessments</Text>
                  <Badge color="var(--color-primary-700)" variant="light">{linkedAssessmentIds.length} linked</Badge>
                </Group>
              </Group>
              <Text size="xs" c="dimmed" mb="md">
                Link impact assessments as supporting evidence for this PA project. Linked assessments
                are stored in the project notes and can be referenced during reimbursement.
              </Text>
              {linkedAssessmentIds.length === 0 ? (
                <Text size="sm" c="dimmed" fs="italic">No assessments linked yet. Search below to add evidence.</Text>
              ) : (
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                <Stack gap="xs" mb="md">
                  {linkedAssessmentIds.map(aid => {
                    const a = assessments.find(x => x.id === aid);
                    if (!a) return (
                      <Paper key={aid} p="xs" withBorder>
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">Assessment #{aid} (not loaded)</Text>
                          <ActionIcon size="sm" color="var(--color-error)" variant="subtle" loading={evidenceSaving} onClick={() => handleUnlinkAssessment(aid)}>
                            <IconTrash size={13} />
                          </ActionIcon>
                        </Group>
                      </Paper>
                    );
                    return (
                      <Paper key={aid} p="sm" withBorder>
                        <Group justify="space-between" wrap="nowrap">
                          <Stack gap={2}>
                            <Group gap="xs">
                              <Text size="sm" fw={500}>{a.address || `Assessment #${a.id}`}</Text>
                              <Badge size="xs" color={
                                a.damageCategory === 'destroyed' ? 'var(--color-error)' :
                                a.damageCategory === 'major' ? '#F86A0B' :
                                a.damageCategory === 'minor' ? '#C68700' : '#939598'
                              } variant="light">{a.damageCategory || 'unknown'}</Badge>
                              {a.status === 'verified' && <Badge size="xs" color="var(--color-success)" variant="light">Verified</Badge>}
                            </Group>
                            <Text size="xs" c="dimmed">
                              {[a.city, a.state].filter(Boolean).join(', ')}
                              {a.estimatedLoss ? ` · Est. loss: $${a.estimatedLoss.toLocaleString()}` : ''}
                              {a.assessmentDate ? ` · ${a.assessmentDate.slice(0, 10)}` : ''}
                            </Text>
                          </Stack>
                          <ActionIcon size="sm" color="var(--color-error)" variant="subtle" loading={evidenceSaving} onClick={() => handleUnlinkAssessment(aid)}>
                            <IconTrash size={13} />
                          </ActionIcon>
                        </Group>
                      </Paper>
                    );
                  })}
                </Stack>
                </div>
              )}
            </Paper>

            {/* ── Assessment picker ─────────────────────── */}
            <Paper p="md" withBorder>
              <Text fw={600} mb="sm">Add Evidence from Impact Assessments</Text>
              <TextInput
                placeholder="Search by address, city, or damage category..."
                value={evidenceSearch}
                onChange={e => setEvidenceSearch(e.target.value)}
                mb="sm"
                size="xs"
              />
              {(() => {
                const q = evidenceSearch.toLowerCase();
                const filtered = assessments
                  .filter(a => !linkedAssessmentIds.includes(a.id))
                  .filter(a =>
                    !q ||
                    (a.address || '').toLowerCase().includes(q) ||
                    (a.city || '').toLowerCase().includes(q) ||
                    (a.damageCategory || '').toLowerCase().includes(q) ||
                    (a.assessorName || '').toLowerCase().includes(q),
                  )
                  .slice(0, 20);
                if (assessments.length === 0) return <Text size="sm" c="dimmed" fs="italic">No assessments loaded.</Text>;
                if (filtered.length === 0) return <Text size="sm" c="dimmed" fs="italic">No matching assessments.</Text>;
                return (
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                  <Stack gap="xs">
                    {filtered.map(a => (
                      <Paper key={a.id} p="xs" withBorder style={{ background: '#fafafa' }}>
                        <Group justify="space-between" wrap="nowrap">
                          <Stack gap={2}>
                            <Group gap="xs">
                              <Text size="sm" fw={500}>{a.address || `Assessment #${a.id}`}</Text>
                              <Badge size="xs" color={
                                a.damageCategory === 'destroyed' ? 'var(--color-error)' :
                                a.damageCategory === 'major' ? '#F86A0B' :
                                a.damageCategory === 'minor' ? '#C68700' : '#939598'
                              } variant="light">{a.damageCategory || 'unknown'}</Badge>
                            </Group>
                            <Text size="xs" c="dimmed">
                              {[a.city, a.state].filter(Boolean).join(', ')}
                              {a.estimatedLoss ? ` · Est. loss: $${a.estimatedLoss.toLocaleString()}` : ''}
                              {a.assessorName ? ` · ${a.assessorName}` : ''}
                            </Text>
                          </Stack>
                          <Button size="compact-xs" variant="light" color="var(--color-primary-700)" loading={evidenceSaving} onClick={() => handleLinkAssessment(a.id)}>
                            Link
                          </Button>
                        </Group>
                      </Paper>
                    ))}
                    {assessments.filter(a => !linkedAssessmentIds.includes(a.id)).length > 20 && (
                      <Text size="xs" c="dimmed" ta="center">Showing first 20 — refine your search to find others.</Text>
                    )}
                  </Stack>
                  </div>
                );
              })()}
            </Paper>
          </Stack>
        </Tabs.Panel>

        {/* ════════════════════════════════════════════════════
            CLOSEOUT TAB
            ════════════════════════════════════════════════════ */}
        <Tabs.Panel value="closeout" pt="md">
          <Stack gap="md">
            {/* Readiness checklist */}
            <Paper p="md" withBorder>
              <Group gap="xs" mb="sm">
                <ThemeIcon color="#7c3aed" variant="light" size={22}><IconListCheck size={13} /></ThemeIcon>
                <Text fw={600} size="sm">Closeout Readiness</Text>
                <Badge size="xs" color={closeoutReadyCount === 4 ? 'var(--color-success)' : '#F86A0B'} variant="light">
                  {closeoutReadyCount}/4 conditions met
                </Badge>
              </Group>
              <Stack gap="xs">
                {([
                  {
                    label: 'All required checklist items completed',
                    met: closeoutReadiness.checklistReady,
                    action: !closeoutReadiness.checklistReady
                      ? <Button size="compact-xs" variant="light" onClick={() => setActiveTab('checklist')}>Go to Checklist</Button>
                      : null,
                  },
                  {
                    label: 'Work Completed milestone recorded',
                    met: closeoutReadiness.workCompleted,
                    action: !closeoutReadiness.workCompleted
                      ? <Button size="compact-xs" variant="light" color="var(--color-success)"
                          leftSection={<IconCheckbox size={11} />}
                          onClick={() => openCheckOff(checklistItems.find(c => c.key === 'work_completed')!)}>
                          Mark Complete
                        </Button>
                      : null,
                  },
                  {
                    label: 'Cost records entered',
                    met: closeoutReadiness.costsEntered,
                    action: !closeoutReadiness.costsEntered
                      ? <Button size="compact-xs" variant="light" onClick={() => setActiveTab('costs')}>Go to Costs</Button>
                      : null,
                  },
                  {
                    label: 'Insurance documentation uploaded or reviewed',
                    met: closeoutReadiness.insuranceDocs,
                    action: !closeoutReadiness.insuranceDocs
                      ? <Button size="compact-xs" variant="light"
                          leftSection={<IconUpload size={11} />}
                          onClick={() => { setUploadDocType('insurance_docs'); fileInputRef.current?.click(); }}>
                          Upload Docs
                        </Button>
                      : null,
                  },
                ] as { label: string; met: boolean; action: JSX.Element | null }[]).map(({ label, met, action }) => (
                  <Group key={label} gap="sm">
                    <ThemeIcon size={20} radius="xl" color={met ? 'var(--color-success)' : '#939598'} variant={met ? 'filled' : 'light'}>
                      {met ? <IconCheck size={12} /> : <IconClock size={12} />}
                    </ThemeIcon>
                    <Text size="sm" c={met ? undefined : 'dimmed'} style={{ flex: 1 }}>{label}</Text>
                    {met
                      ? <Badge size="xs" color="var(--color-success)" variant="light">Ready</Badge>
                      : action
                    }
                  </Group>
                ))}
              </Stack>
            </Paper>

            {/* Recovery Transition Meeting */}
            <Paper p="md" withBorder>
              <Group gap="xs" mb="sm">
                <ThemeIcon color="#7c3aed" variant="light" size={22}><IconCalendarTime size={13} /></ThemeIcon>
                <Text fw={600} size="sm">Recovery Transition Meeting (RTM)</Text>
              </Group>
              <Text size="xs" c="dimmed" mb="sm">
                The RTM with FEMA/State formally begins the closeout process. Record the meeting date once it is scheduled or completed.
              </Text>
              <Group align="flex-end" gap="sm">
                <TextInput type="date" label="RTM Date" value={rtmDate}
                  onChange={e => setRtmDate(e.target.value)} style={{ width: 200 }} />
                <Button size="sm" variant="light" color="violet" loading={savingCloseout} onClick={handleSaveRtm}>
                  Save
                </Button>
              </Group>
              {project.rtmDate && (
                <Text size="xs" c="var(--color-success)" mt="xs">RTM recorded: {fmtDate(project.rtmDate)}</Text>
              )}
            </Paper>

            {/* Final Cost Reconciliation */}
            <Paper p="md" withBorder>
              <Group gap="xs" mb="sm">
                <ThemeIcon color="#7c3aed" variant="light" size={22}><IconCurrencyDollar size={13} /></ThemeIcon>
                <Text fw={600} size="sm">Final Cost Reconciliation</Text>
              </Group>
              <SimpleGrid cols={4}>
                {([
                  { label: 'Obligated Amount', value: obligated, note: 'From project worksheet' },
                  { label: 'Actual Total Costs', value: costRecordTotal, note: 'From cost records' },
                  { label: 'DOB Deductions', value: dobTotal, note: 'From DOB declaration', color: dobTotal > 0 ? 'var(--color-error)' : undefined },
                  { label: 'Net Eligible After DOB', value: dobNetEligible, note: `${fedPct}% federal: ${fmt(dobNetEligible * fedPct / 100)}`,
                    color: dobNetEligible < 0 ? 'var(--color-error)' : 'var(--color-success)' },
                ] as { label: string; value: number; note: string; color?: string }[]).map(({ label, value, note, color }) => {
                  const variance = Math.abs(obligated - costRecordTotal);
                  const varianceColor = variance === 0 ? 'var(--color-success)' : variance / Math.max(obligated, 1) <= 0.05 ? '#C68700' : 'var(--color-error)';
                  return (
                    <Paper key={label} p="sm" withBorder style={{ textAlign: 'center' }}>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
                      <Text fw={700} size="lg" c={color}>{fmt(value)}</Text>
                      <Text size="xs" c="dimmed">{note}</Text>
                    </Paper>
                  );
                  void varianceColor;
                })}
              </SimpleGrid>
              {obligated > 0 && costRecordTotal > 0 && (
                <Group gap="xs" mt="sm">
                  {(() => {
                    const diff = Math.abs(obligated - costRecordTotal);
                    const pct = diff / Math.max(obligated, 1);
                    const color = pct === 0 ? 'var(--color-success)' : pct <= 0.05 ? '#C68700' : 'var(--color-error)';
                    const label = pct === 0 ? 'On Budget' : pct <= 0.05 ? 'Minor Variance' : 'Significant Variance';
                    return (
                      <>
                        <Badge color={color} variant="light">{label}</Badge>
                        {diff > 0 && <Text size="xs" c="dimmed">{fmt(diff)} variance ({(pct * 100).toFixed(1)}%)</Text>}
                      </>
                    );
                  })()}
                </Group>
              )}
            </Paper>

            {/* Submit to State */}
            <Paper p="md" withBorder style={project.closeoutSubmittedAt ? { borderColor: 'var(--color-success)', borderWidth: 2 } : {}}>
              <Group gap="xs" mb="sm">
                <ThemeIcon color={project.closeoutSubmittedAt ? 'var(--color-success)' : '#7c3aed'} variant={project.closeoutSubmittedAt ? 'filled' : 'light'} size={22}>
                  <IconCircleCheck size={13} />
                </ThemeIcon>
                <Text fw={600} size="sm">Submit Closeout to State</Text>
              </Group>
              {project.closeoutSubmittedAt ? (
                <>
                  <Alert color="var(--color-success)" title="Closeout Submitted">
                    Submitted to State on <strong>{fmtDate(project.closeoutSubmittedAt)}</strong>.
                    Retain all project documentation until <strong>{fmtDate(new Date(new Date(project.closeoutSubmittedAt).setFullYear(new Date(project.closeoutSubmittedAt).getFullYear() + 3)).toISOString())}</strong> (3-year requirement, 44 CFR § 200.334).
                  </Alert>
                </>
              ) : (
                <>
                  <Text size="xs" c="dimmed" mb="sm">
                    Once all documentation is complete and costs are reconciled, submit the closeout package to the State recipient. This action timestamps the submission and starts the 3-year documentation retention clock.
                  </Text>
                  {closeoutReadyCount < 4 && (
                    <Alert color="#F86A0B" mb="sm">
                      {4 - closeoutReadyCount} readiness condition{4 - closeoutReadyCount > 1 ? 's' : ''} not yet met. Review the checklist above before submitting.
                    </Alert>
                  )}
                  <Button color="violet" leftSection={<IconCheck size={14} />} loading={savingCloseout}
                    onClick={handleCloseoutSubmit}>
                    Submit Closeout Package to State
                  </Button>
                </>
              )}
            </Paper>
          </Stack>
        </Tabs.Panel>

      </Tabs>

      {/* ════════════════════════════════════════════════════════
          MODAL: FEMA Equipment Rate Picker
          ════════════════════════════════════════════════════════ */}
      <Modal opened={femaPickerOpen} onClose={() => setFemaPickerOpen(false)}
        title="FEMA 2025 Equipment Rate Schedule" size="xl">
        <FemaRatePickerPA onSelect={applyFemaRate} onClose={() => setFemaPickerOpen(false)} />
      </Modal>

      {/* ════════════════════════════════════════════════════════
          MODAL: Add/Edit Cost Line
          ════════════════════════════════════════════════════════ */}
      <Modal
        opened={costModalOpen}
        onClose={() => setCostModalOpen(false)}
        title={editingCost ? 'Edit Cost Line' : 'Add Cost Line'}
        size="lg"
      >
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select
              label="Cost Type" required
              description="Category of this cost line item"
              data={COST_TYPE_OPTIONS}
              value={costForm.costType}
              onChange={(v) => setCostForm(prev => ({ ...prev, costType: v || '' }))}
            />
            <Select
              label="Estimated or Actual"
              description="Whether this is a projected estimate or verified actual cost"
              data={EST_ACTUAL_OPTIONS}
              value={costForm.estimatedOrActual}
              onChange={(v) => setCostForm(prev => ({ ...prev, estimatedOrActual: v || 'estimated' }))}
            />
          </SimpleGrid>
          {costForm.costType === 'equipment' && (
            <Group justify="flex-end">
              <Button size="xs" variant="light" color="var(--color-primary-700)"
                leftSection={<IconListCheck size={13} />}
                onClick={() => setFemaPickerOpen(true)}>
                Look up FEMA Rate
              </Button>
            </Group>
          )}
          <TextInput
            label="Description"
            description="Brief description of the work, item, or service"
            value={costForm.description}
            onChange={(e) => setCostForm(prev => ({ ...prev, description: e.target.value }))}
          />
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <TextInput
              label="Quantity"
              description="Number of units (e.g., hours, cubic yards, each)"
              value={costForm.quantity ? Number(String(costForm.quantity).replace(/,/g, '')).toLocaleString() : ''}
              onChange={(e) => {
                const raw = e.target.value.replace(/,/g, '');
                setCostForm(prev => {
                  const qty = parseFloat(raw) || 0;
                  const uc = parseFloat(prev.unitCost) || 0;
                  return { ...prev, quantity: raw, totalCost: qty && uc ? (qty * uc).toFixed(2) : prev.totalCost };
                });
              }}
            />
            <TextInput
              label="Unit"
              description="Unit of measure (e.g., hours, CY, LF, EA, LS)"
              value={costForm.unit}
              onChange={(e) => setCostForm(prev => ({ ...prev, unit: e.target.value }))}
            />
            <TextInput
              label="Unit Cost"
              description="Cost per unit"
              value={formatCurrency(costForm.unitCost)}
              onChange={(e) => {
                const raw = stripCurrency(e.target.value);
                setCostForm(prev => {
                  const qty = parseFloat(String(prev.quantity || '').replace(/,/g, '') || '0') || 0;
                  const uc = parseFloat(raw) || 0;
                  return { ...prev, unitCost: raw, totalCost: qty && uc ? (qty * uc).toFixed(2) : prev.totalCost };
                });
              }}
              styles={{ input: { fontFamily: "'DM Mono', monospace" } }}
            />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Total Cost"
              description="Auto-calculated (Qty × Unit Cost) — editable for overrides"
              value={formatCurrency(costForm.totalCost)}
              onChange={(e) => setCostForm(prev => ({ ...prev, totalCost: stripCurrency(e.target.value) }))}
              styles={{ input: { fontFamily: "'DM Mono', monospace", fontWeight: 600 } }}
            />
            <Select
              label="CEF Part"
              description="FEMA Cost Estimating Format (CEF) section this cost falls under"
              data={CEF_PART_OPTIONS}
              value={costForm.cefPart}
              onChange={(v) => setCostForm(prev => ({ ...prev, cefPart: v || '' }))}
              clearable
            />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Vendor / Employee"
              description="Company name, contractor, or employee associated with this cost"
              value={costForm.vendorEmployee}
              onChange={(e) => setCostForm(prev => ({ ...prev, vendorEmployee: e.target.value }))}
            />
            <TextInput
              label="Source Reference"
              description="Invoice #, purchase order, timesheet, or other documentation reference"
              value={costForm.sourceReference}
              onChange={(e) => setCostForm(prev => ({ ...prev, sourceReference: e.target.value }))}
            />
          </SimpleGrid>
          <Select
            label="Eligible"
            description="Whether FEMA considers this cost eligible for reimbursement"
            data={ELIGIBLE_OPTIONS}
            value={costForm.eligible}
            onChange={(v) => setCostForm(prev => ({ ...prev, eligible: v || 'true' }))}
          />
          <Textarea
            label="Notes"
            description="Additional context or justification"
            value={costForm.notes}
            onChange={(e) => setCostForm(prev => ({ ...prev, notes: e.target.value }))}
            minRows={2}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setCostModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCost} loading={costSaving}>
              {editingCost ? 'Update' : 'Add'} Cost Line
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          MODAL: Add/Edit Insurance Record
          ════════════════════════════════════════════════════════ */}
      <Modal
        opened={insModalOpen}
        onClose={() => setInsModalOpen(false)}
        title={editingIns ? 'Edit Insurance Record' : 'Add Insurance Record'}
        size="lg"
      >
        <Tabs defaultValue="policy">
          <Tabs.List>
            <Tabs.Tab value="policy">Policy</Tabs.Tab>
            <Tabs.Tab value="claims">Claims</Tabs.Tab>
            <Tabs.Tab value="om">O&amp;M</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="policy" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Policy Number"
                  description="Unique identifier from the insurance policy"
                  value={insForm.policyNumber}
                  onChange={(e) => setInsForm(prev => ({ ...prev, policyNumber: e.target.value }))}
                />
                <TextInput
                  label="Carrier"
                  description="Insurance company name"
                  value={insForm.carrier}
                  onChange={(e) => setInsForm(prev => ({ ...prev, carrier: e.target.value }))}
                />
              </SimpleGrid>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Select
                  label="Policy Type"
                  description="Type of insurance coverage"
                  data={POLICY_TYPE_OPTIONS}
                  value={insForm.policyType}
                  onChange={(v) => setInsForm(prev => ({ ...prev, policyType: v || '' }))}
                  clearable
                />
                <TextInput
                  label="Coverage Amount"
                  description="Maximum amount covered under this policy"
                  value={formatCurrency(insForm.coverageAmount)}
                  onChange={(e) => setInsForm(prev => ({ ...prev, coverageAmount: stripCurrency(e.target.value) }))}
                  styles={{ input: { fontFamily: "'DM Mono', monospace" } }}
                />
              </SimpleGrid>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Deductible"
                  description="Amount paid out-of-pocket before coverage begins"
                  value={formatCurrency(insForm.deductible)}
                  onChange={(e) => setInsForm(prev => ({ ...prev, deductible: stripCurrency(e.target.value) }))}
                  styles={{ input: { fontFamily: "'DM Mono', monospace" } }}
                />
                <TextInput
                  label="Policy Effective Date"
                  type="date"
                  value={insForm.policyEffectiveDate}
                  onChange={(e) => setInsForm(prev => ({ ...prev, policyEffectiveDate: e.target.value }))}
                />
              </SimpleGrid>
              <TextInput
                label="Policy Expiration Date"
                type="date"
                value={insForm.policyExpirationDate}
                onChange={(e) => setInsForm(prev => ({ ...prev, policyExpirationDate: e.target.value }))}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="claims" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Select
                  label="Claim Filed"
                  data={YES_NO_OPTIONS}
                  value={insForm.claimFiled}
                  onChange={(v) => setInsForm(prev => ({ ...prev, claimFiled: v || 'no' }))}
                />
                <TextInput
                  label="Claim Number"
                  value={insForm.claimNumber}
                  onChange={(e) => setInsForm(prev => ({ ...prev, claimNumber: e.target.value }))}
                />
              </SimpleGrid>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Claim Date"
                  type="date"
                  value={insForm.claimDate}
                  onChange={(e) => setInsForm(prev => ({ ...prev, claimDate: e.target.value }))}
                />
                <TextInput
                  label="Settlement Amount"
                  description="Total amount received from insurance settlement"
                  value={formatCurrency(insForm.settlementAmount)}
                  onChange={(e) => setInsForm(prev => ({ ...prev, settlementAmount: stripCurrency(e.target.value) }))}
                  styles={{ input: { fontFamily: "'DM Mono', monospace" } }}
                />
              </SimpleGrid>
              <TextInput
                label="Settlement Date"
                type="date"
                value={insForm.settlementDate}
                onChange={(e) => setInsForm(prev => ({ ...prev, settlementDate: e.target.value }))}
              />
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Select
                  label="Denial Received"
                  data={YES_NO_OPTIONS}
                  value={insForm.denialReceived}
                  onChange={(v) => setInsForm(prev => ({ ...prev, denialReceived: v || 'no' }))}
                />
                <TextInput
                  label="Denial Reason"
                  value={insForm.denialReason}
                  onChange={(e) => setInsForm(prev => ({ ...prev, denialReason: e.target.value }))}
                />
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="om" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Select
                  label="O&M Required"
                  data={YES_NO_OPTIONS}
                  value={insForm.obtainMaintainRequired}
                  onChange={(v) => setInsForm(prev => ({ ...prev, obtainMaintainRequired: v || 'no' }))}
                />
                <Select
                  label="O&M Committed"
                  data={YES_NO_OPTIONS}
                  value={insForm.obtainMaintainCommitted}
                  onChange={(v) => setInsForm(prev => ({ ...prev, obtainMaintainCommitted: v || 'no' }))}
                />
              </SimpleGrid>
              <TextInput
                label="FEMA Deduction Amount"
                description="Amount FEMA deducts from eligible costs due to insurance proceeds"
                value={formatCurrency(insForm.femaDeductionAmount)}
                onChange={(e) => setInsForm(prev => ({ ...prev, femaDeductionAmount: stripCurrency(e.target.value) }))}
                styles={{ input: { fontFamily: "'DM Mono', monospace" } }}
              />
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setInsModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveInsurance} loading={insSaving}>
            {editingIns ? 'Update' : 'Add'} Insurance Record
          </Button>
        </Group>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          MODAL: Edit Compliance
          ════════════════════════════════════════════════════════ */}
      <Modal
        opened={compModalOpen}
        onClose={() => setCompModalOpen(false)}
        title="Edit Compliance"
        size="xl"
      >
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {/* NEPA column */}
          <Stack gap="sm">
            <Text fw={600} size="sm">NEPA / Environmental</Text>
            <Select
              label="NEPA Required"
              description="Whether a NEPA environmental review is required for this project"
              data={YES_NO_OPTIONS}
              value={compForm.nepaRequired}
              onChange={(v) => setCompForm(prev => ({ ...prev, nepaRequired: v || '' }))}
            />
            <Select
              label="NEPA Level"
              description="CatEx = Categorical Exclusion (most common), EA = Environmental Assessment, EIS = Environmental Impact Statement"
              data={NEPA_LEVEL_OPTIONS}
              value={compForm.nepaLevel}
              onChange={(v) => setCompForm(prev => ({ ...prev, nepaLevel: v || '' }))}
              clearable
            />
            <Select
              label="NEPA Status"
              description="Current status of the NEPA review process"
              data={COMPLIANCE_STATUS_OPTIONS}
              value={compForm.nepaStatus}
              onChange={(v) => setCompForm(prev => ({ ...prev, nepaStatus: v || '' }))}
              clearable
            />
            <TextInput
              label="NEPA Completion Date"
              type="date"
              value={compForm.nepaCompletionDate}
              onChange={(e) => setCompForm(prev => ({ ...prev, nepaCompletionDate: e.target.value }))}
            />

            <Divider my="xs" />
            <Text fw={600} size="sm">Floodplain</Text>
            <Select
              label="In Floodplain"
              description="Whether the project site is in a FEMA-designated flood zone"
              data={YES_NO_OPTIONS}
              value={compForm.inFloodplain}
              onChange={(v) => setCompForm(prev => ({ ...prev, inFloodplain: v || '' }))}
            />
            <TextInput
              label="Flood Zone"
              description="FEMA flood zone designation (e.g., A, AE, VE, X)"
              value={compForm.floodZone}
              onChange={(e) => setCompForm(prev => ({ ...prev, floodZone: e.target.value }))}
            />
            <Select
              label="Flood Insurance Required"
              data={YES_NO_OPTIONS}
              value={compForm.floodInsuranceRequired}
              onChange={(v) => setCompForm(prev => ({ ...prev, floodInsuranceRequired: v || '' }))}
            />
            <Select
              label="EO 11988 Compliance"
              description="Executive Order 11988 requires agencies to avoid floodplain development where possible"
              data={COMPLIANCE_STATUS_OPTIONS}
              value={compForm.eo11988Compliance}
              onChange={(v) => setCompForm(prev => ({ ...prev, eo11988Compliance: v || '' }))}
              clearable
            />
          </Stack>

          {/* Section 106 + Coastal column */}
          <Stack gap="sm">
            <Text fw={600} size="sm">Section 106 / Historic</Text>
            <Select
              label="Section 106 Required"
              description="Whether Section 106 historic preservation review is needed"
              data={YES_NO_OPTIONS}
              value={compForm.section106Required}
              onChange={(v) => setCompForm(prev => ({ ...prev, section106Required: v || '' }))}
            />
            <Select
              label="Section 106 Status"
              data={COMPLIANCE_STATUS_OPTIONS}
              value={compForm.section106Status}
              onChange={(v) => setCompForm(prev => ({ ...prev, section106Status: v || '' }))}
              clearable
            />
            <Select
              label="Historic Property"
              description="Whether the site is listed or eligible for the National Register of Historic Places"
              data={YES_NO_OPTIONS}
              value={compForm.historicProperty}
              onChange={(v) => setCompForm(prev => ({ ...prev, historicProperty: v || '' }))}
            />
            <Select
              label="SHPO Consultation"
              description="Status of consultation with the State Historic Preservation Officer"
              data={COMPLIANCE_STATUS_OPTIONS}
              value={compForm.shpoConsultation}
              onChange={(v) => setCompForm(prev => ({ ...prev, shpoConsultation: v || '' }))}
              clearable
            />
            <Select
              label="Tribal Consultation"
              data={COMPLIANCE_STATUS_OPTIONS}
              value={compForm.tribalConsultation}
              onChange={(v) => setCompForm(prev => ({ ...prev, tribalConsultation: v || '' }))}
              clearable
            />

            <Divider my="xs" />
            <Text fw={600} size="sm">Coastal / Other</Text>
            <Select
              label="Coastal Barrier Area"
              description="Whether the site is within a Coastal Barrier Resources System (CBRS) unit"
              data={YES_NO_OPTIONS}
              value={compForm.coastalBarrierArea}
              onChange={(v) => setCompForm(prev => ({ ...prev, coastalBarrierArea: v || '' }))}
            />
            <Select
              label="Coastal Zone"
              data={YES_NO_OPTIONS}
              value={compForm.coastalZone}
              onChange={(v) => setCompForm(prev => ({ ...prev, coastalZone: v || '' }))}
            />
            <Select
              label="ESA Consultation Required"
              description="Whether Endangered Species Act Section 7 consultation is needed"
              data={YES_NO_OPTIONS}
              value={compForm.esaConsultationRequired}
              onChange={(v) => setCompForm(prev => ({ ...prev, esaConsultationRequired: v || '' }))}
            />
            <Select
              label="Hazmat Present"
              description="Whether hazardous materials have been identified at the site"
              data={YES_NO_OPTIONS}
              value={compForm.hazmatPresent}
              onChange={(v) => setCompForm(prev => ({ ...prev, hazmatPresent: v || '' }))}
            />
          </Stack>
        </SimpleGrid>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setCompModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCompliance} loading={compSaving}>
            Save Compliance
          </Button>
        </Group>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          MODAL: Add Timeline Event
          ════════════════════════════════════════════════════════ */}
      <Modal
        opened={eventModalOpen}
        onClose={() => setEventModalOpen(false)}
        title="Add Timeline Event"
        size="md"
      >
        <Stack gap="sm">
          <Select
            label="Event Type" required
            description="Category of this timeline event"
            data={EVENT_TYPE_OPTIONS}
            value={eventForm.eventType}
            onChange={(v) => setEventForm(prev => ({ ...prev, eventType: v || 'milestone' }))}
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Event Date"
              description="Date the event occurred or was recorded"
              type="date"
              value={eventForm.eventDate}
              onChange={(e) => setEventForm(prev => ({ ...prev, eventDate: e.target.value }))}
            />
            <TextInput
              label="Due Date"
              description="Target completion or deadline date"
              type="date"
              value={eventForm.dueDate}
              onChange={(e) => setEventForm(prev => ({ ...prev, dueDate: e.target.value }))}
            />
          </SimpleGrid>
          <Textarea
            label="Description" required
            description="Details about this event or milestone"
            value={eventForm.description}
            onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
            minRows={3}
          />
          <Select
            label="Status"
            description="Current status of this event"
            data={EVENT_STATUS_OPTIONS}
            value={eventForm.status}
            onChange={(v) => setEventForm(prev => ({ ...prev, status: v || 'pending' }))}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setEventModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEvent} loading={eventSaving}>
              Add Event
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          MODAL: Edit Project
          ════════════════════════════════════════════════════════ */}
      <Modal
        opened={editProjModalOpen}
        onClose={() => setEditProjModalOpen(false)}
        title="Edit Project"
        size="xl"
      >
        <Tabs value={editProjTab} onChange={setEditProjTab}>
          <Tabs.List>
            <Tabs.Tab value="general">General</Tabs.Tab>
            <Tabs.Tab value="scope">Scope</Tabs.Tab>
            <Tabs.Tab value="costs">Costs</Tabs.Tab>
            <Tabs.Tab value="schedule">Schedule</Tabs.Tab>
            <Tabs.Tab value="status">Status</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="general" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Project Number"
                  description="Auto-assigned project identifier"
                  value={editProjForm.projectNumber}
                  readOnly
                  styles={{ input: { fontFamily: "'DM Mono', monospace", fontWeight: 700 } }}
                />
                <TextInput
                  label="Title" required
                  description="Descriptive name for the project"
                  value={editProjForm.projectTitle}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, projectTitle: e.target.value }))}
                />
              </SimpleGrid>
              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <Select
                  label="Category"
                  data={CATEGORY_OPTIONS}
                  value={editProjForm.category}
                  onChange={(v) => setEditProjForm(prev => ({ ...prev, category: v || '' }))}
                />
                <Select
                  label="Size"
                  data={SIZE_OPTIONS}
                  value={editProjForm.projectSize}
                  onChange={(v) => setEditProjForm(prev => ({ ...prev, projectSize: v || '' }))}
                />
                <Select
                  label="Work Type"
                  data={WORK_TYPE_OPTIONS}
                  value={editProjForm.workType}
                  onChange={(v) => setEditProjForm(prev => ({ ...prev, workType: v || '' }))}
                  searchable
                  clearable
                />
              </SimpleGrid>
              <TextInput
                label="Address"
                value={editProjForm.projectAddress}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, projectAddress: e.target.value }))}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="scope" pt="md">
            <Stack gap="sm">
              <Textarea
                label="Damage Description & Dimensions (DDD)"
                description="Describe all damage including facility name, pre-disaster condition, cause of damage, and dimensions"
                value={editProjForm.damageDescription}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, damageDescription: e.target.value }))}
                autosize minRows={8} maxRows={30}
              />
              <Textarea
                label="Scope of Work (SOW)"
                description="Detailed scope including materials, methods, labor, equipment, and quantities"
                value={editProjForm.scopeOfWork}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, scopeOfWork: e.target.value }))}
                autosize minRows={8} maxRows={30}
              />
              <Textarea
                label="Cost Narrative"
                description="Explain and justify project costs, including methodology for estimates"
                value={editProjForm.costNarrative}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, costNarrative: e.target.value }))}
                autosize minRows={4} maxRows={20}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="costs" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Estimated Cost"
                  description="Projected total eligible cost"
                  value={formatCurrency(editProjForm.estimatedCost)}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, estimatedCost: stripCurrency(e.target.value) }))}
                  styles={{ input: { fontFamily: "'DM Mono', monospace" } }}
                />
                <TextInput
                  label="Actual Cost"
                  description="Verified total cost to date"
                  value={formatCurrency(editProjForm.actualCost)}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, actualCost: stripCurrency(e.target.value) }))}
                  styles={{ input: { fontFamily: "'DM Mono', monospace" } }}
                />
              </SimpleGrid>
              <TextInput
                label="Federal Share %"
                description="Percentage of eligible costs covered by federal funding (typically 75%)"
                value={editProjForm.federalSharePct}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, federalSharePct: e.target.value }))}
                styles={{ input: { fontFamily: "'DM Mono', monospace" } }}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
              <Divider label="Federal Obligation" labelPosition="left" />
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Obligated Amount"
                  description="Total federal funds obligated by FEMA for this project"
                  value={formatCurrency(editProjForm.obligatedAmount)}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, obligatedAmount: stripCurrency(e.target.value) }))}
                  styles={{ input: { fontFamily: "'DM Mono', monospace" } }}
                  placeholder="$0"
                />
                <TextInput
                  label="Total Drawn Down"
                  description="Cumulative reimbursements approved to date (auto-updated on RFR approval)"
                  value={formatCurrency(editProjForm.drawdownAmount)}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, drawdownAmount: stripCurrency(e.target.value) }))}
                  styles={{ input: { fontFamily: "'DM Mono', monospace" } }}
                  placeholder="$0"
                />
              </SimpleGrid>
              {editProjForm.obligatedAmount && (
                <Text size="xs" c="dimmed">
                  Remaining balance: {fmt((parseFloat(stripCurrency(editProjForm.obligatedAmount)) || 0) - (parseFloat(stripCurrency(editProjForm.drawdownAmount)) || 0))}
                </Text>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="schedule" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Work Start Date"
                  description="Date work began or is planned to begin"
                  type="date"
                  value={editProjForm.workStartDate}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, workStartDate: e.target.value }))}
                />
                <TextInput
                  label="Deadline"
                  description="Required completion date per FEMA timeline"
                  type="date"
                  value={editProjForm.deadline}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, deadline: e.target.value }))}
                />
              </SimpleGrid>
              <TextInput
                label="Completion % Estimate"
                description="Estimated percentage of work completed"
                value={editProjForm.completionPctEstimate}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, completionPctEstimate: e.target.value }))}
                styles={{ input: { fontFamily: "'DM Mono', monospace" } }}
                rightSection={<Text size="sm" c="dimmed">%</Text>}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="status" pt="md">
            <Stack gap="sm">
              {!isApplicantRole && (
                <TextInput
                  label="Assigned Specialist"
                  description="FEMA PDMG or state specialist assigned to this project"
                  value={editProjForm.assignedSpecialist}
                  onChange={(e) => setEditProjForm(prev => ({ ...prev, assignedSpecialist: e.target.value }))}
                />
              )}
              {!isApplicantRole ? (
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <Select
                    label="Phase"
                    description="Current FEMA processing phase"
                    data={PHASE_OPTIONS}
                    value={editProjForm.phase}
                    onChange={(v) => setEditProjForm(prev => ({ ...prev, phase: v || 'formulation' }))}
                  />
                  <Select
                    label="Status"
                    description="Administrative status within the current phase"
                    data={PROJECT_STATUS_OPTIONS}
                    value={editProjForm.status}
                    onChange={(v) => setEditProjForm(prev => ({ ...prev, status: v || 'draft' }))}
                  />
                </SimpleGrid>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <TextInput label="Phase" value={editProjForm.phase} disabled />
                  <TextInput label="Status" value={editProjForm.status} disabled />
                </SimpleGrid>
              )}
              <Textarea
                label="Notes"
                description="Internal notes and comments"
                value={editProjForm.notes}
                onChange={(e) => setEditProjForm(prev => ({ ...prev, notes: e.target.value }))}
                minRows={4}
              />
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setEditProjModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveProject} loading={editProjSaving}>
            Save Changes
          </Button>
        </Group>
      </Modal>

      {/* ── Add Task Modal ─────────────────────────────────────── */}
      <Modal opened={taskModalOpen} onClose={() => setTaskModalOpen(false)} title="Add Task" size="md">
        <Stack gap="sm">
          <Textarea label="Description" required
            description="What needs to be done"
            value={taskForm.description}
            onChange={e => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
          />
          <TextInput label="Due Date" type="date"
            description="When this task needs to be completed"
            value={taskForm.dueDate}
            onChange={e => setTaskForm(prev => ({ ...prev, dueDate: e.target.value }))}
          />
          <Select label="Status"
            description="Current progress status"
            data={[
              { value: 'pending', label: 'Pending' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'completed', label: 'Complete' },
            ]}
            value={taskForm.status}
            onChange={val => setTaskForm(prev => ({ ...prev, status: val || 'pending' }))}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setTaskModalOpen(false)}>Cancel</Button>
            <Button loading={taskSaving} onClick={handleSaveTask}>Create Task</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Upload Tagging Modal ──────────────────────────────── */}
      <Modal
        opened={uploadModalOpen}
        onClose={() => { setUploadModalOpen(false); setUploadFile(null); setUploadDocType(''); }}
        title="Upload Document"
        size="md"
      >
        <Stack gap="sm">
          <Paper p="sm" withBorder style={{ background: '#F8F8F8' }}>
            <Group gap="sm">
              <IconFile size={20} />
              <Stack gap={0}>
                <Text size="sm" fw={500}>{uploadFile?.name}</Text>
                <Text size="xs" c="dimmed">{uploadFile ? `${(uploadFile.size / 1024).toFixed(0)} KB` : ''}</Text>
              </Stack>
            </Group>
          </Paper>

          <Select
            label="What is this document?"
            description="Tag this upload to a checklist item — it will be marked as complete and added to the project timeline"
            data={DOC_TYPE_OPTIONS}
            value={uploadDocType}
            onChange={v => setUploadDocType(v || '')}
            searchable
          />

          <Textarea
            label="Notes"
            description="Optional notes about this document"
            value={uploadNotes}
            onChange={e => setUploadNotes(e.target.value)}
            minRows={2}
          />

          {/* AI Document Analysis */}
          {uploadFile && (uploadFile.type.startsWith('image/') || uploadFile.type === 'application/pdf') && (
            <Paper p="sm" withBorder style={{ background: 'rgba(166,39,255,0.08)' }}>
              <Group justify="space-between" mb={aiDocResult ? 'xs' : 0}>
                <Group gap="xs">
                  <IconRobot size={16} color="var(--data-viz-650)" />
                  <Text size="sm" fw={600} c="var(--data-viz-650)">AI Document Analysis</Text>
                </Group>
                <Group gap="xs">
                  <Button size="xs" variant="light" color="var(--data-viz-650)" loading={aiAnalyzing}
                    onClick={handleAiAnalyze} leftSection={<IconRobot size={12} />}>
                    {aiDocResult ? 'Re-analyze' : 'Analyze'}
                  </Button>
                  <Button size="xs" variant="filled" color="var(--data-viz-650)" loading={aiAnalyzing}
                    onClick={handleAiExtractCosts} leftSection={<IconScan size={12} />}>
                    Extract Costs
                  </Button>
                </Group>
              </Group>
              {aiDocResult && (
                <Paper p="xs" mt="xs" style={{ background: 'white', maxHeight: 300, overflow: 'auto' }}>
                  <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{aiDocResult}</Text>
                </Paper>
              )}
            </Paper>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => { setUploadModalOpen(false); setUploadFile(null); setUploadDocType(''); }}>Cancel</Button>
            <Button loading={uploading} onClick={handleUpload} leftSection={<IconUpload size={14} />}>
              Upload
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Checklist Completion Modal (non-document items) ────── */}
      <Modal
        opened={checkModalOpen}
        onClose={() => setCheckModalOpen(false)}
        title={
          checkingItem?.category === 'review' ? `Log External Review: ${checkingItem?.label || ''}` :
          checkingItem?.category === 'milestone' ? `Record Milestone: ${checkingItem?.label || ''}` :
          `Mark Complete: ${checkingItem?.label || ''}`
        }
        size="md"
      >
        <Stack gap="sm">
          {checkingItem && (
            <Paper p="sm" withBorder style={{ background: 'rgba(3,119,48,0.08)', border: '1px solid rgba(3,119,48,0.25)' }}>
              <Text size="sm">{checkingItem.description}</Text>
            </Paper>
          )}

          {/* Review-specific fields */}
          {checkingItem?.category === 'review' && (
            <>
              <SimpleGrid cols={2}>
                <TextInput
                  label="Reviewer Name"
                  placeholder="e.g. Jane Smith"
                  required
                  value={checkReviewerName}
                  onChange={e => setCheckReviewerName(e.target.value)}
                />
                <Select
                  label="Reviewer Organization"
                  placeholder="Select…"
                  data={['FEMA', 'State EM Agency', 'Subrecipient', 'Third-Party Consultant', 'Other']}
                  value={checkReviewerOrg}
                  onChange={v => setCheckReviewerOrg(v || '')}
                />
              </SimpleGrid>
              <SimpleGrid cols={2}>
                <Select
                  label="Outcome / Decision"
                  placeholder="Select…"
                  data={['Approved', 'Conditionally Approved', 'No Action Required', 'Pending Additional Info', 'Denied']}
                  value={checkOutcome}
                  onChange={v => setCheckOutcome(v || '')}
                />
                <TextInput
                  label="Reference / Tracking #"
                  placeholder="Optional"
                  value={checkRefNumber}
                  onChange={e => setCheckRefNumber(e.target.value)}
                />
              </SimpleGrid>
              <TextInput
                label="Review Date"
                type="date"
                value={checkDate}
                onChange={e => setCheckDate(e.target.value)}
              />
            </>
          )}

          {/* Milestone / Form date field */}
          {(checkingItem?.category === 'milestone' || checkingItem?.category === 'form') && (
            <TextInput
              label={checkingItem.category === 'milestone' ? 'Completion Date' : 'Date Signed'}
              type="date"
              value={checkDate}
              onChange={e => setCheckDate(e.target.value)}
            />
          )}

          <Textarea
            label="Notes (optional)"
            description={
              checkingItem?.category === 'review'
                ? 'Conditions, caveats, or additional context from the reviewer'
                : checkingItem?.category === 'milestone'
                ? 'Any relevant context — inspector name, scope confirmation, etc.'
                : 'Optional notes'
            }
            value={checkNotes}
            onChange={e => setCheckNotes(e.target.value)}
            minRows={2}
          />

          <Text size="xs" c="dimmed">
            This will create a timeline event and mark the checklist item as complete.
          </Text>

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setCheckModalOpen(false)}>Cancel</Button>
            <Button
              color={checkingItem?.category === 'review' ? 'indigo' : 'var(--color-success)'}
              loading={checkSaving}
              onClick={handleCheckOff}
              disabled={checkingItem?.category === 'review' && !checkReviewerName.trim()}
              leftSection={<IconCheck size={14} />}
            >
              {checkingItem?.category === 'review' ? 'Log Review' : checkingItem?.category === 'form' ? 'Mark as Signed' : 'Mark Complete'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Revision Request Modal ──────────────────────────────── */}
      <Modal
        opened={!!revisionItem}
        onClose={() => setRevisionItem(null)}
        title={`Request Revision: ${revisionItem?.label || ''}`}
        size="md"
      >
        <Stack gap="sm">
          {revisionItem && (
            <Paper p="sm" withBorder style={{ background: 'rgba(248,106,11,0.08)', border: '1px solid rgba(248,106,11,0.28)' }}>
              <Text size="sm">{revisionItem.description}</Text>
            </Paper>
          )}

          <Textarea
            label="Feedback for the applicant"
            description="Explain what needs to be corrected, updated, or re-submitted"
            placeholder="e.g., The damage description is missing dimensions. Please add square footage measurements for each affected area."
            value={revisionNote}
            onChange={e => setRevisionNote(e.target.value)}
            minRows={3}
            required
          />

          <Text size="xs" c="dimmed">
            This will mark the item as needing revision and notify the applicant. They will need to re-submit to clear it.
          </Text>

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setRevisionItem(null)}>Cancel</Button>
            <Button color="#F86A0B" loading={revisionSaving} onClick={handleRequestRevision}
              leftSection={<IconArrowLeft size={14} />}
              disabled={!revisionNote.trim()}>
              Request Revision
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── AI Cost Extraction Review Modal ────────────────────── */}
      <Modal
        opened={extractModalOpen}
        onClose={() => { setExtractModalOpen(false); setExtractedCosts([]); }}
        title={`Review Extracted Costs — ${extractSource}`}
        size="xl"
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Stack gap="sm">
          <Paper p="sm" withBorder style={{ background: 'rgba(166,39,255,0.08)', border: '1px solid rgba(166,39,255,0.14)' }}>
            <Group gap="xs">
              <IconRobot size={16} color="var(--data-viz-650)" />
              <Text size="sm" c="violet.7" fw={500}>
                AI extracted {extractedCosts.length} cost line{extractedCosts.length !== 1 ? 's' : ''} from &quot;{extractSource}&quot;
              </Text>
            </Group>
            <Text size="xs" c="dimmed" mt={4}>Review and edit before importing. Remove any incorrect items.</Text>
          </Paper>

          {extractedCosts.map((cost, idx) => (
            <Paper key={idx} p="sm" withBorder>
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <Badge size="sm" color={COST_TYPE_COLORS[cost.costType] || '#939598'}>
                    {COST_TYPE_LABELS[cost.costType] || cost.costType}
                  </Badge>
                  {cost.cefPart && <Badge size="xs" variant="outline">CEF Part {cost.cefPart}</Badge>}
                </Group>
                <ActionIcon size="sm" variant="subtle" color="var(--color-error)"
                  onClick={() => setExtractedCosts(prev => prev.filter((_, i) => i !== idx))}>
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                <TextInput size="xs" label="Description" value={cost.description}
                  onChange={e => {
                    const next = [...extractedCosts];
                    next[idx] = { ...next[idx], description: e.target.value };
                    setExtractedCosts(next);
                  }} />
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xs">
                  <TextInput size="xs" label="Qty" value={cost.quantity}
                    onChange={e => {
                      const next = [...extractedCosts];
                      next[idx] = { ...next[idx], quantity: e.target.value };
                      setExtractedCosts(next);
                    }} />
                  <TextInput size="xs" label="Unit" value={cost.unit}
                    onChange={e => {
                      const next = [...extractedCosts];
                      next[idx] = { ...next[idx], unit: e.target.value };
                      setExtractedCosts(next);
                    }} />
                  <TextInput size="xs" label="Total"
                    value={formatCurrency(cost.totalCost)}
                    onChange={e => {
                      const next = [...extractedCosts];
                      next[idx] = { ...next[idx], totalCost: stripCurrency(e.target.value) };
                      setExtractedCosts(next);
                    }}
                    styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
                </SimpleGrid>
              </SimpleGrid>
            </Paper>
          ))}

          {extractedCosts.length === 0 && (
            <Text c="dimmed" ta="center" py="md">All items removed. Nothing to import.</Text>
          )}

          <Divider />
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              Total: {formatCurrency(String(extractedCosts.reduce((sum, c) => sum + (parseFloat(c.totalCost) || 0), 0)))}
            </Text>
            <Group gap="sm">
              <Button variant="default" onClick={() => { setExtractModalOpen(false); setExtractedCosts([]); }}>
                Cancel
              </Button>
              <Button color="var(--data-viz-650)" loading={extracting}
                disabled={extractedCosts.length === 0}
                leftSection={<IconRobot size={14} />}
                onClick={handleImportExtractedCosts}>
                Import {extractedCosts.length} Cost{extractedCosts.length !== 1 ? 's' : ''}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      {/* ── Scan Receipt Review Modal ─────────────────────── */}
      <Modal
        opened={scanModalOpen}
        onClose={() => { setScanModalOpen(false); setScannedCosts([]); }}
        title={<Group gap="xs"><IconScan size={20} color="var(--data-viz-650)" /> Review Scanned Costs — {scanSource}</Group>}
        size="xl"
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Stack gap="sm">
          <Paper p="sm" withBorder style={{ background: 'rgba(166,39,255,0.08)', border: '1px solid rgba(166,39,255,0.14)' }}>
            <Group gap="xs">
              <IconRobot size={16} color="var(--data-viz-650)" />
              <Text size="sm" c="violet.7" fw={500}>
                AI found {scannedCosts.length} cost line{scannedCosts.length !== 1 ? 's' : ''} in &quot;{scanSource}&quot;
              </Text>
            </Group>
            <Text size="xs" c="dimmed" mt={4}>Review each item — edit the category, amounts, or description. Remove anything incorrect before confirming.</Text>
          </Paper>

          {scannedCosts.map((cost, idx) => (
            <Paper key={idx} p="sm" withBorder>
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <Badge size="sm" color={COST_TYPE_COLORS[cost.costType] || '#939598'}>
                    {COST_TYPE_LABELS[cost.costType] || cost.costType}
                  </Badge>
                  {cost.cefPart && <Badge size="xs" variant="outline">CEF Part {cost.cefPart}</Badge>}
                </Group>
                <ActionIcon size="sm" variant="subtle" color="var(--color-error)"
                  onClick={() => setScannedCosts(prev => prev.filter((_, i) => i !== idx))}>
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                <TextInput size="xs" label="Description" value={cost.description}
                  onChange={e => {
                    const next = [...scannedCosts];
                    next[idx] = { ...next[idx], description: e.target.value };
                    setScannedCosts(next);
                  }} />
                <Select size="xs" label="Category" data={[
                  { value: 'labor', label: 'Labor (CEF A)' },
                  { value: 'equipment', label: 'Equipment (CEF B)' },
                  { value: 'materials', label: 'Materials (CEF C)' },
                  { value: 'rented_equip', label: 'Rented Equipment (CEF D)' },
                  { value: 'contracts', label: 'Contracts (CEF E)' },
                  { value: 'other', label: 'Other / Admin (CEF F)' },
                  { value: 'donated', label: 'Donated Resources' },
                ]}
                  value={cost.costType}
                  onChange={v => {
                    const cefMap: Record<string, string> = { labor: 'A', equipment: 'B', materials: 'C', rented_equip: 'D', contracts: 'E', other: 'F', donated: '' };
                    const next = [...scannedCosts];
                    next[idx] = { ...next[idx], costType: v ?? 'other', cefPart: cefMap[v ?? 'other'] ?? '' };
                    setScannedCosts(next);
                  }} />
              </SimpleGrid>
              <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs" mt="xs">
                <TextInput size="xs" label="Qty" value={cost.quantity}
                  onChange={e => {
                    const next = [...scannedCosts];
                    next[idx] = { ...next[idx], quantity: e.target.value };
                    setScannedCosts(next);
                  }} />
                <TextInput size="xs" label="Unit" value={cost.unit}
                  onChange={e => {
                    const next = [...scannedCosts];
                    next[idx] = { ...next[idx], unit: e.target.value };
                    setScannedCosts(next);
                  }} />
                <TextInput size="xs" label="Unit Cost"
                  value={formatCurrency(cost.unitCost)}
                  onChange={e => {
                    const next = [...scannedCosts];
                    next[idx] = { ...next[idx], unitCost: stripCurrency(e.target.value) };
                    setScannedCosts(next);
                  }}
                  styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
                <TextInput size="xs" label="Total"
                  value={formatCurrency(cost.totalCost)}
                  onChange={e => {
                    const next = [...scannedCosts];
                    next[idx] = { ...next[idx], totalCost: stripCurrency(e.target.value) };
                    setScannedCosts(next);
                  }}
                  styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
              </SimpleGrid>
            </Paper>
          ))}

          {scannedCosts.length === 0 && (
            <Text c="dimmed" ta="center" py="md">All items removed. Nothing to import.</Text>
          )}

          <Divider />
          <Group justify="space-between">
            <Text size="sm" fw={500} style={{ fontFamily: 'DM Mono, monospace' }}>
              Total: {formatCurrency(String(scannedCosts.reduce((sum, c) => sum + (parseFloat(c.totalCost) || 0), 0)))}
            </Text>
            <Group gap="sm">
              <Button variant="default" onClick={() => { setScanModalOpen(false); setScannedCosts([]); }}>
                Cancel
              </Button>
              <Button color="var(--color-success)" loading={scanning}
                disabled={scannedCosts.length === 0}
                leftSection={<IconCheck size={14} />}
                onClick={handleImportScannedCosts}>
                Confirm &amp; Import {scannedCosts.length} Cost{scannedCosts.length !== 1 ? 's' : ''}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      {/* ── AI Assist Modal ────────────────────────────────── */}
      <Modal
        opened={aiAssistOpen}
        onClose={() => setAiAssistOpen(false)}
        title={<Group gap="xs"><IconRobot size={20} color="var(--data-viz-650)" /> AI Project Review</Group>}
        size="lg"
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      >
        {aiAssistLoading ? (
          <Stack align="center" py="xl" gap="sm">
            <Loader size={32} color="var(--data-viz-650)" />
            <Text size="sm" c="dimmed">Analyzing project data...</Text>
          </Stack>
        ) : (
          <Stack gap="sm">
            <Paper p="sm" withBorder style={{ background: 'rgba(166,39,255,0.08)', border: '1px solid rgba(166,39,255,0.14)' }}>
              <Text size="xs" c="violet.7">AI review of {project?.projectNumber} — {project?.projectTitle}</Text>
            </Paper>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: aiAssistResult
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/^### (.*$)/gm, '<h4 style="margin:12px 0 4px;color:var(--data-viz-650)">$1</h4>')
                .replace(/^## (.*$)/gm, '<h3 style="margin:16px 0 6px;color:#6C1B9A">$1</h3>')
                .replace(/^# (.*$)/gm, '<h3 style="margin:16px 0 6px;color:#6C1B9A">$1</h3>')
                .replace(/^- (.*$)/gm, '<div style="padding-left:16px">• $1</div>')
                .replace(/^\d+\. (.*$)/gm, '<div style="padding-left:16px">$&</div>')
              }} />
            <Group justify="flex-end" mt="sm">
              <Button variant="light" color="var(--data-viz-650)" leftSection={<IconRobot size={14} />}
                loading={aiAssistLoading} onClick={handleAiAssist}>
                Re-analyze
              </Button>
              <Button variant="default" onClick={() => setAiAssistOpen(false)}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* ── Timesheet Import Modal ───────────────────────────── */}
      <Modal
        opened={timesheetImportOpen}
        onClose={() => { setTimesheetImportOpen(false); setTimesheetSelected(new Set()); setTsSearch(''); setTsCatFilter(null); }}
        title="Import Timesheets into Project"
        size="xl"
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Stack gap="md">
          <Text size="sm" fw={600}>
            Select time entries to assign to {project?.projectNumber} &mdash; {project?.projectTitle}
          </Text>
          <Text size="xs" c="dimmed">
            Only supervisor-approved timesheets are shown. Pending entries must be approved in Time &amp; Labor before import.
          </Text>

          {/* Filter bar */}
          <Group gap="sm">
            <TextInput
              size="xs" placeholder="Search employee or activity..."
              value={tsSearch} onChange={e => setTsSearch(e.target.value)}
              style={{ flex: 1 }}
            />
            <Select
              size="xs" placeholder="Category" clearable
              value={tsCatFilter} onChange={v => setTsCatFilter(v)}
              data={[
                { value: 'A', label: 'Cat A — Debris' },
                { value: 'B', label: 'Cat B — Emergency' },
                { value: 'C', label: 'Cat C — Roads' },
                { value: 'D', label: 'Cat D — Water' },
                { value: 'E', label: 'Cat E — Buildings' },
                { value: 'F', label: 'Cat F — Utilities' },
                { value: 'G', label: 'Cat G — Parks' },
              ]}
              w={180}
            />
          </Group>

          {/* Unassigned labor table */}
          {unassignedLabor.length === 0 ? (
            <Paper p="lg" withBorder>
              <Text size="sm" c="dimmed" ta="center">
                No unassigned time entries found{tsCatFilter ? ` for Category ${tsCatFilter}` : ''}{tsSearch ? ` matching "${tsSearch}"` : ''}.
              </Text>
            </Paper>
          ) : (
            <Paper p="sm" withBorder>
              <Table striped highlightOnHover fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={40}>
                      <Checkbox
                        size="xs"
                        checked={unassignedLabor.length > 0 && unassignedLabor.every(r => timesheetSelected.has(r.Rid))}
                        indeterminate={unassignedLabor.some(r => timesheetSelected.has(r.Rid)) && !unassignedLabor.every(r => timesheetSelected.has(r.Rid))}
                        onChange={() => {
                          const allSelected = unassignedLabor.every(r => timesheetSelected.has(r.Rid));
                          if (allSelected) {
                            setTimesheetSelected(new Set());
                          } else {
                            setTimesheetSelected(new Set(unassignedLabor.map(r => r.Rid)));
                          }
                        }}
                      />
                    </Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Employee</Table.Th>
                    <Table.Th>Title</Table.Th>
                    <Table.Th>Activity</Table.Th>
                    <Table.Th>Cat</Table.Th>
                    <Table.Th>Logged Hrs</Table.Th>
                    <Table.Th>Appr Hrs</Table.Th>
                    <Table.Th>Rate</Table.Th>
                    <Table.Th>Total</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {unassignedLabor.map(lr => {
                    const matchesCat = project?.category && lr.category === project.category;
                    return (
                      <Table.Tr
                        key={lr.Rid}
                        style={matchesCat ? { background: 'rgba(3,119,48,0.08)' } : undefined}
                      >
                        <Table.Td>
                          <Checkbox
                            size="xs"
                            checked={timesheetSelected.has(lr.Rid)}
                            onChange={() => {
                              setTimesheetSelected(prev => {
                                const next = new Set(prev);
                                if (next.has(lr.Rid)) next.delete(lr.Rid);
                                else next.add(lr.Rid);
                                return next;
                              });
                            }}
                          />
                        </Table.Td>
                        <Table.Td>{fmtDate(lr.workDate)}</Table.Td>
                        <Table.Td>{lr.employeeName || '-'}</Table.Td>
                        <Table.Td>{lr.employeeTitle || '-'}</Table.Td>
                        <Table.Td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {lr.activityDescription || '-'}
                        </Table.Td>
                        <Table.Td>
                          {lr.category ? (
                            <Badge size="xs" variant="light" color={DAMAGE_CATEGORY_COLORS[lr.category as DamageCategory] || '#939598'}>
                              {lr.category}
                            </Badge>
                          ) : '-'}
                        </Table.Td>
                        <Table.Td>{num(lr.hoursRegular) + num(lr.hoursOvertime)}</Table.Td>
                        <Table.Td>
                          {(() => {
                            const aR = lr.approvedRegHours ? parseFloat(lr.approvedRegHours) : num(lr.hoursRegular);
                            const aO = lr.approvedOTHours ? parseFloat(lr.approvedOTHours) : num(lr.hoursOvertime);
                            const approvedTotal = aR + aO;
                            const loggedTotal = num(lr.hoursRegular) + num(lr.hoursOvertime);
                            const isReduced = approvedTotal < loggedTotal;
                            return (
                              <Text size="sm" fw={isReduced ? 600 : undefined} c={isReduced ? '#F86A0B' : undefined}>
                                {approvedTotal}
                              </Text>
                            );
                          })()}
                        </Table.Td>
                        <Table.Td>{fmt(num(lr.rateRegular))}</Table.Td>
                        <Table.Td fw={600}>{fmt(num(lr.approvedCost || lr.totalCost))}</Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Paper>
          )}

          {/* Already-assigned entries from other projects */}
          {otherProjectLabor.length > 0 && (
            <Paper p="sm" withBorder style={{ opacity: 0.6 }}>
              <Text size="xs" c="dimmed" mb="xs">
                {otherProjectLabor.length} time {otherProjectLabor.length === 1 ? 'entry' : 'entries'} already assigned to other projects
              </Text>
              {otherProjectLabor.slice(0, 5).map(lr => {
                const assignedProj = projects.find(p => String(p.Rid) === lr.projectId);
                return (
                  <Text key={lr.Rid} size="xs" c="dimmed">
                    {lr.employeeName} — {fmtDate(lr.workDate)} &rarr; Assigned to {assignedProj?.projectNumber || `Project #${lr.projectId}`}
                  </Text>
                );
              })}
              {otherProjectLabor.length > 5 && (
                <Text size="xs" c="dimmed">...and {otherProjectLabor.length - 5} more</Text>
              )}
            </Paper>
          )}

          {/* Footer with selection summary */}
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              {timesheetSelected.size > 0 ? (
                <>
                  {timesheetSelected.size} {timesheetSelected.size === 1 ? 'entry' : 'entries'} selected
                  {' '}&mdash;{' '}
                  {laborRecords.filter(r => timesheetSelected.has(r.Rid)).reduce((s, r) => {
                    const aR = r.approvedRegHours ? parseFloat(r.approvedRegHours) : num(r.hoursRegular);
                    const aO = r.approvedOTHours ? parseFloat(r.approvedOTHours) : num(r.hoursOvertime);
                    return s + aR + aO;
                  }, 0)} hrs
                  {' '}&mdash;{' '}
                  {fmt(laborRecords.filter(r => timesheetSelected.has(r.Rid)).reduce((s, r) => s + num(r.approvedCost || r.totalCost), 0))}
                </>
              ) : (
                <Text size="sm" c="dimmed">Select time entries to import</Text>
              )}
            </Text>
            <Group gap="sm">
              <Button variant="default" onClick={() => { setTimesheetImportOpen(false); setTimesheetSelected(new Set()); }}>
                Cancel
              </Button>
              <Button
                color="var(--data-viz-650)"
                loading={timesheetImporting}
                disabled={timesheetSelected.size === 0}
                leftSection={<IconCheck size={14} />}
                onClick={handleTimesheetImport}
              >
                Import Selected ({timesheetSelected.size})
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
      {/* ── DOB Add / Edit Modal ─────────────────────────────── */}
      <Modal
        opened={dobModalOpen}
        onClose={() => setDobModalOpen(false)}
        title={dobEditIdx === null ? 'Add Funding Source' : 'Edit Funding Source'}
        size="lg"
      >
        <Stack gap="md">
          <Select
            label="Source Type"
            data={[
              { value: 'insurance', label: 'Insurance Proceeds' },
              { value: 'sba',       label: 'SBA Loan' },
              { value: 'cdbg_dr',   label: 'CDBG-DR' },
              { value: 'other',     label: 'Other Federal / State' },
            ]}
            value={dobDraft.type}
            onChange={v => setDobDraft(d => ({ ...d, type: (v || 'insurance') as DobSource['type'] }))}
          />
          <TextInput
            label="Program / Policy #"
            placeholder="e.g. Policy #1234567, SBA Loan #…"
            value={dobDraft.program}
            onChange={e => setDobDraft(d => ({ ...d, program: e.target.value }))}
          />
          <div>
            <label className="form-label">Amount Received ($)</label>
            <input
              className="form-input"
              type="text"
              inputMode="decimal"
              placeholder="$0"
              value={dobAmountStr}
              onChange={e => {
                const raw = e.target.value.replace(/[^0-9.]/g, '');
                setDobAmountStr(e.target.value);
                setDobDraft(d => ({ ...d, amount: parseFloat(raw) || 0 }));
              }}
              onBlur={() => {
                if (dobDraft.amount > 0) setDobAmountStr(fmt(dobDraft.amount));
                else setDobAmountStr('');
              }}
              onFocus={() => {
                setDobAmountStr(dobDraft.amount === 0 ? '' : String(dobDraft.amount));
              }}
              style={{ width: '100%' }}
            />
          </div>
          <Textarea
            label="Scope Overlap Notes"
            placeholder="Which eligible costs does this source cover?"
            value={dobDraft.notes}
            onChange={e => setDobDraft(d => ({ ...d, notes: e.target.value }))}
            rows={3}
          />

          {/* Cost item linking */}
          {dobCostItems.length > 0 && (
            <div>
              <Text size="sm" fw={600} mb={6}>Linked Cost Items</Text>
              <Text size="xs" c="dimmed" mb={8}>
                Select the eligible cost items this source covers (used in the Coverage Map).
              </Text>
              <Paper p="sm" withBorder style={{ maxHeight: 220, overflowY: 'auto' }}>
                <Stack gap={6}>
                  {dobCostItems.map(item => {
                    const checked = (dobDraft.linkedCostKeys || []).includes(item.key);
                    return (
                      <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '4px 2px' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setDobDraft(d => {
                              const keys = d.linkedCostKeys || [];
                              return {
                                ...d,
                                linkedCostKeys: checked
                                  ? keys.filter(k => k !== item.key)
                                  : [...keys, item.key],
                              };
                            });
                          }}
                        />
                        <Badge size="xs" variant="light" style={{ background: item.badgeColor + '22', color: item.badgeColor, border: `1px solid ${item.badgeColor}44`, flexShrink: 0 }}>
                          {item.badge}
                        </Badge>
                        <Text size="xs" style={{ flex: 1 }}>{item.label}</Text>
                        <Text size="xs" fw={600} c="dimmed" style={{ flexShrink: 0 }}>{fmt(item.amount)}</Text>
                      </label>
                    );
                  })}
                </Stack>
              </Paper>
              {(dobDraft.linkedCostKeys || []).length > 0 && (
                <Text size="xs" c="violet" mt={4}>
                  {(dobDraft.linkedCostKeys || []).length} item{(dobDraft.linkedCostKeys || []).length !== 1 ? 's' : ''} selected
                  {' · '}
                  {fmt(dobCostItems.filter(c => (dobDraft.linkedCostKeys || []).includes(c.key)).reduce((s, c) => s + c.amount, 0))} covered
                </Text>
              )}
            </div>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setDobModalOpen(false)}>Cancel</Button>
            <Button leftSection={<IconCheck size={14} />} onClick={saveDobDraft}>
              {dobEditIdx === null ? 'Add Source' : 'Save Changes'}
            </Button>
          </Group>
        </Stack>
      </Modal>

    </Stack>
  );
}
